import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { collection, deleteDoc, doc, getDocs, limit, onSnapshot, orderBy, query, setDoc, serverTimestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { appId, db, functions } from './firebase-config';
import { buildCompanyAccessPath } from './core/company/companyAccess';
import {
  resolveCompaniesCollectionPath,
  resolveCompanyDocPath,
  resolveCompanyGeneralSettingsPath,
  resolveCompanyModulesSettingsPath,
  resolveCompanyPlanSettingsPath,
  resolveRoutesCollectionPath,
} from './core/company/companyPaths';
import {
  BILLING_OPTIONS,
  COMMERCIAL_PLANS_DOC_PATH,
  DEFAULT_COMMERCIAL_CATALOG,
  formatCommercialPrice,
  normalizeCommercialPlanKey,
  resolveCommercialCatalog,
} from './CompanyPlanPanel';
import { ensureCompanyBootstrap } from './core/company/companyBootstrap';
import commercialArgentinaMap from './assets/commercial-argentina-map.png';

const MODULE_DEFINITIONS = [
  {
    key: 'stock',
    label: 'Stock y movimientos',
    shortDescription: 'Inventario y movimientos.',
    icon: 'M3.75 21h16.5M4.5 3h15M5.25 3v18m13.5-18v18M9 6.75h6.375a.375.375 0 01.375.375v1.5a.375.375 0 01-.375.375H9a.375.375 0 01-.375-.375v-1.5A.375.375 0 019 6.75z',
  },
  {
    key: 'logistics',
    label: 'Logística',
    shortDescription: 'Rutas, unidades y operación.',
    icon: 'M8.25 18.75a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 01-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h1.125c.621 0 1.129-.504 1.09-1.124a17.902 17.902 0 00-3.213-9.193 2.056 2.056 0 00-1.58-.86H14.25',
  },
  {
    key: 'routeMonitor',
    label: 'Monitor en tiempo real',
    shortDescription: 'Seguimiento en vivo de rutas activas.',
    icon: 'M9 17.25v.001M12 17.25v.001M15 17.25v.001M4.5 5.25v13.5a2.25 2.25 0 002.25 2.25h10.5a2.25 2.25 0 002.25-2.25V5.25a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25z',
  },
  {
    key: 'reports',
    label: 'Reportes',
    shortDescription: 'Historial, liquidaciones y balance.',
    icon: 'M3 3.75A.75.75 0 013.75 3h10.5a.75.75 0 01.53.22l3 3a.75.75 0 01.22.53v13.5a.75.75 0 01-.75.75H3.75a.75.75 0 01-.75-.75V3.75zm3.75 4.5h6m-6 3h10.5m-10.5 3h10.5m-10.5 3h6',
  },
  {
    key: 'fleetExpenses',
    label: 'Gastos de Flota',
    shortDescription: 'Costos y gastos móviles.',
    icon: 'M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h6m3-3.75l-3.75-3.75A1.125 1.125 0 015.25 6H3.75a2.25 2.25 0 00-2.25 2.25v1.5a2.25 2.25 0 002.25 2.25h1.5a1.125 1.125 0 01.83.375l3.75 3.75a1.125 1.125 0 010 1.59l-3.75 3.75a1.125 1.125 0 01-.83.375H3.75a2.25 2.25 0 01-2.25-2.25v-1.5a2.25 2.25 0 012.25-2.25h1.5A1.125 1.125 0 005.25 12z',
  },
  {
    key: 'systemGuide',
    label: 'Guía y soporte',
    shortDescription: 'Referencia y ayuda interna.',
    icon: 'M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z',
  },
];

const MODULE_LABELS = MODULE_DEFINITIONS.reduce((acc, module) => {
  acc[module.key] = module.label;
  return acc;
}, {});

const DEFAULT_TRIAL_DAYS = 30;
const COMMERCIAL_WHATSAPP = '+54 9 11 4209-0884';
const COMMERCIAL_EMAIL = 'g.centra.app@gmail.com';
const COMMERCIAL_PUBLIC_URL = 'https://g-centra.com.ar/';
const COMMERCIAL_PRESENTATION_MESSAGE =
  `Después de dos años de desarrollo, hoy presentamos G-CENTRA: una plataforma pensada para ordenar rutas, mercadería, trazabilidad y control operativo en un solo lugar. Podés conocerla en ${COMMERCIAL_PUBLIC_URL}. En esta etapa de presentación, ofrecemos un mes de uso sin costo y, si durante ese período necesitás asesoramiento, acompañamiento o capacitación, te lo brindamos sin cargo.`;
const COMMERCIAL_WHATSAPP_TEMPLATE =
  `Hola, ¿cómo estás? Después de dos años de desarrollo, hoy presentamos G-CENTRA: una plataforma pensada para ordenar rutas, mercadería, trazabilidad y control operativo en un solo lugar. Podés ver la plataforma acá: ${COMMERCIAL_PUBLIC_URL}. En esta etapa de presentación, estamos ofreciendo un mes de uso sin costo y, si durante ese período necesitás asesoramiento, acompañamiento o capacitación, te lo brindamos sin cargo. Si querés, coordinamos una muestra breve y te contamos cómo podría aplicarse a tu operación.`;
const COMMERCIAL_EMAIL_TEMPLATE = `Asunto: Presentamos G-CENTRA

Hola, ¿cómo estás?

Después de dos años de desarrollo, hoy presentamos G-CENTRA: una plataforma pensada para empresas que necesitan ordenar rutas, mercadería, trazabilidad y control operativo en un solo lugar.

G-CENTRA permite seguir qué transporte está trabajando, qué ruta está realizando y en qué destino se encuentra, además de integrar evidencias, movimientos y lectura operativa en una misma base.

Podés ver la plataforma acá: ${COMMERCIAL_PUBLIC_URL}

En esta etapa de presentación, estamos ofreciendo un mes de uso sin costo. Y si durante ese período necesitás asesoramiento, acompañamiento o capacitación, te lo brindamos sin cargo.

Si te interesa, podemos coordinar una muestra breve para contarte cómo podría aplicarse a tu empresa.

Saludos,
G-CENTRA
WhatsApp: +54 9 11 4209-0884
Correo: g.centra.app@gmail.com
Web: ${COMMERCIAL_PUBLIC_URL}`;

const PROSPECT_SEGMENTS = [
  'Agencias de motos',
  'Mensajería y logística urbana',
  'Distribuidoras con reparto propio',
  'Empresas con logística propia',
  'Logísticas medianas',
  'Otro perfil',
];

const PROSPECT_STATUSES = [
  { key: 'pending_review', label: 'Pendiente de revisión', tone: 'border-slate-200 bg-slate-100 text-slate-700' },
  { key: 'queued_to_send', label: 'En cola de contacto', tone: 'border-cyan-500/30 bg-cyan-500/10 text-cyan-200' },
  { key: 'sent', label: 'Enviado', tone: 'border-indigo-500/30 bg-indigo-500/10 text-indigo-200' },
  { key: 'manual_sent', label: 'Manual enviado', tone: 'border-sky-500/30 bg-sky-500/10 text-sky-200' },
  { key: 'responded', label: 'Respondió', tone: 'border-fuchsia-500/30 bg-fuchsia-500/10 text-fuchsia-200' },
  { key: 'demo', label: 'Demo agendada', tone: 'border-violet-500/30 bg-violet-500/10 text-violet-200' },
  { key: 'trial', label: 'Prueba activa', tone: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-200' },
  { key: 'converted', label: 'Cliente activo', tone: 'border-amber-500/30 bg-amber-500/10 text-amber-200' },
  { key: 'archived', label: 'Archivado', tone: 'border-slate-200 bg-slate-100 text-slate-600' },
];

const PROSPECT_STATUS_ALIASES = {
  new: 'pending_review',
  qualified: 'queued_to_send',
  ready_to_send: 'queued_to_send',
  contacted: 'sent',
  manual_contacted: 'manual_sent',
  contacted_manual: 'manual_sent',
  archive: 'archived',
};
const PROSPECT_FINAL_STATUSES = new Set(['sent', 'manual_sent', 'responded', 'demo', 'trial', 'converted', 'archived']);
const PROSPECT_INITIAL_STATUSES = new Set(['pending_review', 'queued_to_send']);

const RADAR_STATUSES = [
  { key: 'detected', label: 'Radar', tone: 'border-slate-200 bg-slate-100 text-slate-700' },
  { key: 'validated', label: 'Validación', tone: 'border-cyan-200 bg-cyan-50 text-cyan-700' },
  { key: 'loaded', label: 'Cargado', tone: 'border-indigo-200 bg-indigo-50 text-indigo-700' },
  { key: 'discarded', label: 'Descartado', tone: 'border-rose-200 bg-rose-50 text-rose-700' },
];

const RADAR_STATUS_ALIASES = {
  prospect: 'loaded',
};

const COMMERCIAL_AGENT_DEFINITIONS = [
  {
    key: 'radar',
    label: 'Agente Radar',
    helper: 'Detecta empresas y genera hallazgos nuevos.',
  },
  {
    key: 'validation',
    label: 'Agente Validación',
    helper: 'Confirma existencia, encaje y calidad del caso.',
  },
  {
    key: 'loading',
    label: 'Agente de Carga',
    helper: 'Empuja a Prospectos los casos validados y evita duplicados.',
  },
  {
    key: 'contact',
    label: 'Agente Contacto',
    helper: 'Envía mails automáticamente cuando el caso está listo.',
  },
  {
    key: 'followup',
    label: 'Agente Seguimiento',
    helper: 'Sigue respuestas, demos y pruebas activas.',
  },
];

const COMMERCIAL_TARGET_PRESETS = {
  argentina_logistica: {
    key: 'argentina_logistica',
    label: 'Argentina · foco operativo',
    slogan: 'Empresas con logística propia, reparto y distribución.',
    summary: 'Buscar primero donde G-CENTRA resuelve coordinación, trazabilidad y seguimiento real de unidades.',
    segments: [
      'Empresas con logística propia',
      'Distribuidoras con reparto propio',
      'Logísticas medianas',
      'Mensajería y logística urbana',
    ],
    cities: [
      'AMBA',
      'La Plata',
      'Mar del Plata',
      'Bahía Blanca',
      'Tandil',
      'Olavarría',
      'Azul',
      'Necochea',
      'Tres Arroyos',
      'Junín',
      'Pergamino',
      'San Nicolás',
      'Zárate',
      'Campana',
      'Luján',
      'Mercedes',
      'Chivilcoy',
      'Trenque Lauquen',
      'Córdoba',
      'Río Cuarto',
      'Villa María',
      'San Francisco',
      'Villa Carlos Paz',
      'Río Tercero',
      'Alta Gracia',
      'Jesús María',
      'Bell Ville',
      'Marcos Juárez',
      'Rosario',
      'Santa Fe',
      'Rafaela',
      'Venado Tuerto',
      'Reconquista',
      'Villa Constitución',
      'San Lorenzo',
      'Santo Tomé',
      'Mendoza',
      'San Rafael',
      'Godoy Cruz',
      'Guaymallén',
      'Maipú Mendoza',
      'Luján de Cuyo',
      'Tunuyán',
      'Malargüe',
      'San Miguel de Tucumán',
      'Yerba Buena',
      'Concepción Tucumán',
      'Salta',
      'Orán',
      'Tartagal',
      'San Salvador de Jujuy',
      'Palpalá',
      'Perico',
      'Posadas',
      'Oberá',
      'Eldorado',
      'Puerto Iguazú',
      'Corrientes',
      'Goya',
      'Paso de los Libres',
      'Resistencia',
      'Presidencia Roque Sáenz Peña',
      'Villa Ángela',
      'Paraná',
      'Concordia',
      'Gualeguaychú',
      'Concepción del Uruguay',
      'Villaguay',
      'Neuquén',
      'Cipolletti',
      'General Roca',
      'Villa Regina',
      'Viedma',
      'Bariloche',
      'San Martín de los Andes',
      'Cutral Có',
      'San Juan',
      'Rawson San Juan',
      'Chimbas',
      'San Luis',
      'Villa Mercedes',
      'Merlo San Luis',
      'Santiago del Estero',
      'La Banda',
      'Formosa',
      'Clorinda',
      'La Rioja',
      'Chilecito',
      'Catamarca',
      'Valle Viejo',
      'Trelew',
      'Puerto Madryn',
      'Rawson Chubut',
      'Esquel',
      'Comodoro Rivadavia',
      'Río Gallegos',
      'Caleta Olivia',
      'El Calafate',
      'Ushuaia',
      'Río Grande',
    ],
    cadence: 'Recorrer Argentina por tandas amplias de ciudades, alternando grandes centros y plazas medianas para evitar repetir siempre los mismos resultados.',
  },
};

const DEFAULT_COMMERCIAL_TARGET_PRESET_KEY = 'argentina_logistica';

const resolveCommercialTargetPreset = (presetKey = DEFAULT_COMMERCIAL_TARGET_PRESET_KEY) => {
  return COMMERCIAL_TARGET_PRESETS[presetKey] || COMMERCIAL_TARGET_PRESETS[DEFAULT_COMMERCIAL_TARGET_PRESET_KEY];
};

const mergeCommercialUniqueValues = (baseValues = [], nextValues = []) => {
  const seen = new Set();
  return [...baseValues, ...nextValues].filter((value) => {
    const normalized = String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, ' ');
    if (!normalized || seen.has(normalized)) return false;
    seen.add(normalized);
    return true;
  });
};

const resolveCommercialTargetProfile = (raw = {}) => {
  const preset = resolveCommercialTargetPreset(raw?.presetKey);
  return {
    presetKey: preset.key,
    label: String(raw?.label || preset.label || '').trim(),
    slogan: String(raw?.slogan || preset.slogan || '').trim(),
    summary: String(raw?.summary || preset.summary || '').trim(),
    segments: mergeCommercialUniqueValues(preset.segments, Array.isArray(raw?.segments) ? raw.segments : []),
    cities: mergeCommercialUniqueValues(preset.cities, Array.isArray(raw?.cities) ? raw.cities : []),
    cadence: String(raw?.cadence || preset.cadence || '').trim(),
    updatedAt: raw?.updatedAt || null,
  };
};

const DEFAULT_COMMERCIAL_AGENT_CONTROL = {
  ...COMMERCIAL_AGENT_DEFINITIONS.reduce((acc, agent) => {
    acc[agent.key] = {
      active: true,
      updatedAt: null,
    };
    return acc;
  }, {}),
  targetProfile: resolveCommercialTargetProfile(),
  followup: {
    active: true,
    responseNotifyTo: '',
  },
  lastBackendRadarRunAt: null,
  lastBackendRadarRunSummary: null,
  updatedAt: null,
};

const COMMERCIAL_AGENT_PILOT_BATCH_AR = [
  {
    companyName: 'Mensajería Urbana Sur',
    segment: 'Mensajería y logística urbana',
    city: 'Avellaneda, Buenos Aires',
    source: 'Radar piloto Argentina',
    sourceUrl: '',
    website: '',
    contactEmail: 'contacto@mensajeriaurbana.example',
    contactWhatsApp: '',
    contactValue: 'contacto@mensajeriaurbana.example',
    priority: 'Alta',
    recommendedAction: 'Revisar encaje para operación urbana y seguimiento en tiempo real.',
    notes: 'Caso piloto para validar flujo comercial orientado a entregas urbanas y trazabilidad operativa.',
  },
  {
    companyName: 'Distribución Oeste Operativa',
    segment: 'Distribuidoras con reparto propio',
    city: 'Morón, Buenos Aires',
    source: 'Radar piloto Argentina',
    sourceUrl: '',
    website: '',
    contactEmail: 'comercial@distribucionoeste.example',
    contactWhatsApp: '',
    contactValue: 'comercial@distribucionoeste.example',
    priority: 'Media',
    recommendedAction: 'Confirmar necesidad de rutas, control de mercadería y estado de unidades.',
    notes: 'Caso piloto enfocado en reparto propio y orden operativo diario.',
  },
  {
    companyName: 'Flota Técnica Centro',
    segment: 'Empresas con logística propia',
    city: 'CABA',
    source: 'Radar piloto Argentina',
    sourceUrl: '',
    website: '',
    contactEmail: 'info@flotatecnica.example',
    contactWhatsApp: '',
    contactValue: 'info@flotatecnica.example',
    priority: 'Alta',
    recommendedAction: 'Validar interés en seguimiento por transporte y coordinación de servicios.',
    notes: 'Caso piloto orientado a empresas con flota interna y necesidad de control de ruta.',
  },
];

const ARCHITECT_SECTIONS = [
  { key: 'companies', label: 'Empresas' },
  { key: 'requests', label: 'Solicitudes' },
  { key: 'traffic', label: 'Visitas' },
  { key: 'catalog', label: 'Catálogo' },
  { key: 'prospects', label: 'Prospectos' },
  { key: 'radar', label: 'Radar comercial' },
];

const ACTIVE_ROUTE_STATUSES = new Set([
  'active',
  'activa',
  'activo',
  'in_progress',
  'in-progress',
  'en_curso',
  'en-curso',
  'started',
  'pending',
  'pendiente',
  'assigned',
  'asignada',
  'asignado',
]);

const PLAN_PRESETS = [
  {
    key: 'base',
    label: 'Base',
    description: 'Operación diaria con base centralizada, acceso individual por transporte y seguimiento unidad por unidad.',
    modules: ['logistics', 'systemGuide'],
  },
  {
    key: 'intermedio',
    label: 'Intermedio',
    description: 'Suma monitor multiunidad y stock operativo para leer rutas, compras, salidas y movimientos.',
    modules: ['stock', 'logistics', 'routeMonitor', 'systemGuide'],
  },
  {
    key: 'avanzado',
    label: 'Avanzado',
    description: 'Integra trazabilidad profunda, reportes, balance, flota, garantías e historia económica.',
    modules: ['stock', 'logistics', 'routeMonitor', 'reports', 'fleetExpenses', 'systemGuide'],
  },
];

const TRIAL_PLAN = {
  key: 'trial',
  label: 'Trial',
  description: 'Acceso temporal al núcleo operativo base con seguimiento individual por transporte.',
};

const PERSONALIZED_PLAN = {
  key: 'personalizado',
  label: 'Personalizado',
  description: 'Combinación manual de módulos fuera de un preset.',
};

const PLAN_PRESET_LOOKUP = PLAN_PRESETS.reduce((acc, preset) => {
  acc[preset.key] = preset;
  return acc;
}, {});

const ALWAYS_ON_MODULE_KEYS = new Set(['systemGuide']);
const MS_PER_DAY = 24 * 60 * 60 * 1000;
const TRIAL_EXPIRING_SOON_DAYS = 3;
const RENEWAL_EXPIRING_SOON_DAYS = 7;
const DEFAULT_COMPANY_ID = 'default';
const DEFAULT_COMPANY_NAME = 'Empresa Base';
const INTERNAL_COMPANY_PROFILES = {
  commercial_sandbox: {
    label: 'Comercial interna',
    badgeTone: 'border-cyan-300 bg-cyan-100 text-cyan-900',
    helper: 'Empresa interna realista para validar la experiencia comercial completa.',
    protected: true,
  },
  laboratory: {
    label: 'Laboratorio',
    badgeTone: 'border-violet-300 bg-violet-100 text-violet-900',
    helper: 'Entorno de pruebas y validación técnica antes de mover cambios al resto.',
    protected: true,
  },
};

const Icon = ({ path, className = 'w-5 h-5' }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
    className={className}
  >
    <path strokeLinecap="round" strokeLinejoin="round" d={path} />
  </svg>
);

const getDateFromUnknown = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value?.toDate === 'function') return value.toDate();
  if (typeof value === 'object' && Number.isFinite(Number(value.seconds))) {
    const seconds = Number(value.seconds || 0);
    const nanoseconds = Number(value.nanoseconds || 0);
    const millis = (seconds * 1000) + Math.floor(nanoseconds / 1000000);
    const parsedFromTimestamp = new Date(millis);
    return Number.isNaN(parsedFromTimestamp.getTime()) ? null : parsedFromTimestamp;
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const addDays = (value, days) => {
  const date = getDateFromUnknown(value) || new Date();
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
};

const getLatestDate = (...values) => {
  const dates = values
    .flat()
    .map(getDateFromUnknown)
    .filter(Boolean)
    .sort((a, b) => b.getTime() - a.getTime());

  return dates[0] || null;
};

const isToday = (date) => {
  if (!date) return false;
  const now = new Date();
  return (
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate()
  );
};

const formatLastActivity = (date) => {
  if (!date) return 'Sin actividad registrada';
  return date.toLocaleString('es-AR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatCompactDate = (value) => {
  const date = getDateFromUnknown(value);
  if (!date) return 'Sin fecha';
  return date.toLocaleDateString('es-AR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

const getStartOfDay = (value = new Date()) => {
  const date = getDateFromUnknown(value) || new Date();
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
};

const normalizeComparableText = (value = '') => {
  return String(value || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .trim()
    .toLowerCase();
};

const COMMERCIAL_ARGENTINA_PROVINCE_BLOCKS = [
  { key: 'jujuy', label: 'Jujuy', shortLabel: 'JY', x: 43, y: 8, width: 11, height: 8 },
  { key: 'salta', label: 'Salta', shortLabel: 'SA', x: 30, y: 18, width: 18, height: 13 },
  { key: 'formosa', label: 'Formosa', shortLabel: 'FO', x: 70, y: 18, width: 14, height: 11 },
  { key: 'misiones', label: 'Misiones', shortLabel: 'MI', x: 91, y: 38, width: 9, height: 15 },
  { key: 'chaco', label: 'Chaco', shortLabel: 'CH', x: 61, y: 34, width: 15, height: 12 },
  { key: 'corrientes', label: 'Corrientes', shortLabel: 'CR', x: 78, y: 51, width: 15, height: 13 },
  { key: 'santiago_del_estero', label: 'Santiago del Estero', shortLabel: 'SE', x: 45, y: 47, width: 16, height: 14 },
  { key: 'tucuman', label: 'Tucumán', shortLabel: 'TU', x: 40, y: 63, width: 9, height: 8 },
  { key: 'catamarca', label: 'Catamarca', shortLabel: 'CA', x: 27, y: 61, width: 12, height: 14 },
  { key: 'la_rioja', label: 'La Rioja', shortLabel: 'LR', x: 28, y: 78, width: 13, height: 12 },
  { key: 'santa_fe', label: 'Santa Fe', shortLabel: 'SF', x: 59, y: 71, width: 16, height: 18 },
  { key: 'entre_rios', label: 'Entre Ríos', shortLabel: 'ER', x: 77, y: 89, width: 12, height: 17 },
  { key: 'cordoba', label: 'Córdoba', shortLabel: 'CB', x: 43, y: 88, width: 18, height: 17 },
  { key: 'san_juan', label: 'San Juan', shortLabel: 'SJ', x: 21, y: 100, width: 12, height: 12 },
  { key: 'san_luis', label: 'San Luis', shortLabel: 'SL', x: 33, y: 112, width: 14, height: 11 },
  { key: 'mendoza', label: 'Mendoza', shortLabel: 'MZ', x: 17, y: 128, width: 14, height: 15 },
  { key: 'la_pampa', label: 'La Pampa', shortLabel: 'LP', x: 42, y: 138, width: 20, height: 15 },
  { key: 'buenos_aires', label: 'Buenos Aires', shortLabel: 'BA', x: 67, y: 140, width: 28, height: 28 },
  { key: 'caba', label: 'CABA', shortLabel: 'C', x: 89, y: 153, width: 4, height: 4 },
  { key: 'neuquen', label: 'Neuquén', shortLabel: 'NQ', x: 19, y: 177, width: 18, height: 14 },
  { key: 'rio_negro', label: 'Río Negro', shortLabel: 'RN', x: 39, y: 189, width: 40, height: 12 },
  { key: 'chubut', label: 'Chubut', shortLabel: 'CT', x: 31, y: 219, width: 45, height: 15 },
  { key: 'santa_cruz', label: 'Santa Cruz', shortLabel: 'SC', x: 27, y: 248, width: 41, height: 23 },
  { key: 'tierra_del_fuego', label: 'Tierra del Fuego', shortLabel: 'TF', x: 43, y: 286, width: 18, height: 9 },
];

const COMMERCIAL_CITY_TO_PROVINCE = {
  amba: ['buenos_aires', 'caba'],
  caba: ['caba'],
  'buenos aires': ['buenos_aires'],
  'la plata': ['buenos_aires'],
  'mar del plata': ['buenos_aires'],
  'bahia blanca': ['buenos_aires'],
  cordoba: ['cordoba'],
  'rio cuarto': ['cordoba'],
  'villa maria': ['cordoba'],
  rosario: ['santa_fe'],
  'santa fe': ['santa_fe'],
  rafaela: ['santa_fe'],
  mendoza: ['mendoza'],
  'san rafael': ['mendoza'],
  'san miguel de tucuman': ['tucuman'],
  tucuman: ['tucuman'],
  salta: ['salta'],
  'san salvador de jujuy': ['jujuy'],
  jujuy: ['jujuy'],
  posadas: ['misiones'],
  corrientes: ['corrientes'],
  resistencia: ['chaco'],
  parana: ['entre_rios'],
  neuquen: ['neuquen'],
  cipolletti: ['rio_negro'],
  'rio negro': ['rio_negro'],
  'san juan': ['san_juan'],
  'san luis': ['san_luis'],
  'santiago del estero': ['santiago_del_estero'],
  formosa: ['formosa'],
  'la rioja': ['la_rioja'],
  catamarca: ['catamarca'],
  trelew: ['chubut'],
  'comodoro rivadavia': ['chubut'],
  'rio gallegos': ['santa_cruz'],
  ushuaia: ['tierra_del_fuego'],
};

const resolveCommercialProvinceKeys = (value = '') => {
  const normalized = normalizeComparableText(value);
  if (!normalized) return [];

  if (COMMERCIAL_CITY_TO_PROVINCE[normalized]) {
    return COMMERCIAL_CITY_TO_PROVINCE[normalized];
  }

  const directProvince = COMMERCIAL_ARGENTINA_PROVINCE_BLOCKS.find((province) => normalizeComparableText(province.label) === normalized);
  if (directProvince) return [directProvince.key];

  const matchedEntry = Object.entries(COMMERCIAL_CITY_TO_PROVINCE).find(([cityKey]) => normalized.includes(cityKey));
  if (matchedEntry) return matchedEntry[1];

  const matchedProvince = COMMERCIAL_ARGENTINA_PROVINCE_BLOCKS.find((province) => normalized.includes(normalizeComparableText(province.label)));
  return matchedProvince ? [matchedProvince.key] : [];
};

const buildCommercialProvinceSet = (values = []) => {
  const next = new Set();
  values.forEach((value) => {
    resolveCommercialProvinceKeys(value).forEach((provinceKey) => next.add(provinceKey));
  });
  return next;
};

const normalizeBillingRequestCompanyKey = (value = '') => normalizeComparableText(value);
const resolveCommercialProspectsCollectionPath = () => `artifacts/${appId}/commercialProspects`;
const resolveCommercialRadarCollectionPath = () => `artifacts/${appId}/commercialRadar`;
const resolveCommercialAgentsDocPath = () => `artifacts/${appId}/commercialControl/agents`;
const resolveCommercialRadarExcludedCollectionPath = () => `artifacts/${appId}/commercialRadarExcluded`;
const resolvePublicTrafficDailyCollectionPath = () => `artifacts/${appId}/publicTrafficDaily`;
const resolvePublicTrafficSummaryDocPath = () => `artifacts/${appId}/publicTrafficSummary/overview`;

const normalizeProspectStatusKey = (statusKey = 'pending_review') => {
  const normalized = normalizeComparableText(statusKey).replace(/\s+/g, '_');
  return PROSPECT_STATUS_ALIASES[normalized] || normalized || 'pending_review';
};

const resolveProspectStatusAfterLoad = (existingStatus = '', nextStatus = 'pending_review') => {
  const normalizedExisting = normalizeProspectStatusKey(existingStatus);
  const normalizedNext = normalizeProspectStatusKey(nextStatus);

  if (!normalizedExisting) return normalizedNext;
  if (PROSPECT_FINAL_STATUSES.has(normalizedExisting)) return normalizedExisting;
  if (normalizedExisting === 'queued_to_send') return 'queued_to_send';
  if (PROSPECT_INITIAL_STATUSES.has(normalizedExisting)) {
    return normalizedNext === 'queued_to_send' ? 'queued_to_send' : normalizedExisting;
  }

  return normalizedExisting;
};

const getProspectStatusMeta = (statusKey = 'pending_review') => {
  const resolved = normalizeProspectStatusKey(statusKey);
  return PROSPECT_STATUSES.find((item) => item.key === resolved) || PROSPECT_STATUSES[0];
};

const getRadarStatusMeta = (statusKey = 'detected') => {
  const normalized = normalizeComparableText(statusKey).replace(/\s+/g, '_');
  const resolved = RADAR_STATUS_ALIASES[normalized] || normalized || 'detected';
  return RADAR_STATUSES.find((item) => item.key === resolved) || RADAR_STATUSES[0];
};

const isRadarItemDerivedToProspects = (item = {}, prospects = []) => {
  const radarStatusKey = getRadarStatusMeta(item?.status).key;
  if (radarStatusKey === 'loaded') return true;
  if (item?.linkedProspectId && prospects.some((prospect) => prospect?.id === item.linkedProspectId)) return true;

  const companyKey = normalizeComparableText(item?.companyName || '');
  if (!companyKey) return false;
  return prospects.some((prospect) => normalizeComparableText(prospect?.companyName || '') === companyKey);
};

const getProspectStageTimestamp = (prospect = {}, statusKey = '') => {
  const normalized = normalizeProspectStatusKey(statusKey);
  return (
    prospect?.stageTimestamps?.[normalized] ||
    prospect?.statusUpdatedAt ||
    prospect?.updatedAt ||
    prospect?.createdAt ||
    null
  );
};

const getProspectActivityDate = (prospect = {}) => {
  return getDateFromUnknown(
    prospect?.statusUpdatedAt ||
    prospect?.updatedAt ||
    prospect?.createdAt ||
    null
  );
};

const looksLikeEmail = (value = '') => /\S+@\S+\.\S+/.test(String(value || '').trim());

const normalizePhoneDigits = (value = '') => String(value || '').replace(/\D/g, '');

const looksLikePhone = (value = '') => normalizePhoneDigits(value).length >= 8;

const normalizeWebUrl = (value = '') => {
  const trimmed = String(value || '').trim();
  if (!trimmed) return '';
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
};

const buildWhatsAppUrl = (value = '') => {
  const digits = normalizePhoneDigits(value);
  if (!digits) return '';
  return `https://wa.me/${digits}`;
};

const resolveCommercialChannels = (entry = {}) => {
  const website = normalizeWebUrl(entry.website || entry.sourceUrl || '');
  const email = String(entry.contactEmail || entry.email || '').trim();
  const whatsapp = String(entry.contactWhatsApp || entry.whatsapp || '').trim();
  const contactValue = String(entry.contactValue || '').trim();

  return {
    website,
    email: email || (looksLikeEmail(contactValue) ? contactValue : ''),
    whatsapp: whatsapp || (looksLikePhone(contactValue) && !looksLikeEmail(contactValue) ? contactValue : ''),
  };
};

const buildCommercialProspectSubject = (prospect = {}) => {
  const companyName = String(prospect?.companyName || '').trim();
  return companyName ? `Presentamos G-CENTRA para ${companyName}` : 'Presentamos G-CENTRA';
};

const buildCommercialProspectEmailBody = (prospect = {}) => {
  const companyName = String(prospect?.companyName || '').trim();
  const greeting = companyName ? `Hola equipo de ${companyName},` : 'Hola, ¿cómo están?';
  const segment = normalizeComparableText(prospect?.segment || '');
  const narrativeBySegment = {
    'agencias de motos': 'Sabemos que en una operación liviana y dinámica, tener claro qué viaje está activo, qué chofer lo toma y qué evidencias quedaron puede marcar la diferencia.',
    'mensajeria y logistica urbana': 'En operaciones urbanas con ritmo alto, ordenar rutas, estados, evidencias y seguimiento en tiempo real ayuda a evitar desorden y pérdida de contexto.',
    'distribuidoras con reparto propio': 'Cuando el reparto propio crece, se vuelve clave coordinar unidades, destinos, mercadería y trazabilidad desde una sola base.',
    'empresas con logistica propia': 'Para una empresa con logística interna, tener control de rutas, destinos, unidades y lectura operativa en tiempo real ayuda a ordenar toda la ejecución.',
    'logisticas medianas': 'En estructuras con varios viajes y unidades, centralizar operación, trazabilidad y control económico simplifica mucho la coordinación diaria.',
  };
  const tailoredNarrative = narrativeBySegment[segment] || 'G-CENTRA está pensada para ordenar rutas, viajes, mercadería, trazabilidad y control operativo en una sola plataforma.';

  return [
    greeting,
    '',
    'Después de dos años de desarrollo, hoy presentamos G-CENTRA: una plataforma pensada para ordenar rutas, mercadería, trazabilidad y control operativo en un solo lugar.',
    tailoredNarrative,
    '',
    `Podés ver la plataforma acá: ${COMMERCIAL_PUBLIC_URL}`,
    '',
    'En esta etapa de presentación, estamos ofreciendo un mes de uso sin costo. Y si durante ese período necesitan asesoramiento, acompañamiento o capacitación, se los brindamos sin cargo.',
    '',
    'Si les interesa, podemos coordinar una muestra breve para contarles cómo podría aplicarse a su operación.',
    '',
    'Saludos,',
    'G-CENTRA',
    `WhatsApp: ${COMMERCIAL_WHATSAPP}`,
    `Correo: ${COMMERCIAL_EMAIL}`,
    `Web: ${COMMERCIAL_PUBLIC_URL}`,
  ].join('\n');
};

const buildCommercialProspectWhatsAppMessage = (prospect = {}) => {
  const companyName = String(prospect?.companyName || '').trim();
  const greeting = companyName ? `Hola equipo de ${companyName}, ¿cómo están?` : 'Hola, ¿cómo están?';
  const segment = normalizeComparableText(prospect?.segment || '');
  const narrativeBySegment = {
    'agencias de motos': 'Pensamos G-CENTRA para dar más claridad sobre viajes, choferes y evidencias en operaciones rápidas.',
    'mensajeria y logistica urbana': 'Pensamos G-CENTRA para ordenar rutas, estados y seguimiento en tiempo real en operaciones urbanas.',
    'distribuidoras con reparto propio': 'Pensamos G-CENTRA para coordinar mejor unidades, destinos y mercadería en reparto propio.',
    'empresas con logistica propia': 'Pensamos G-CENTRA para dar visibilidad real sobre rutas, destinos, unidades y control operativo.',
    'logisticas medianas': 'Pensamos G-CENTRA para centralizar operación, trazabilidad y lectura económica cuando ya hay varios viajes activos.',
  };
  const tailoredNarrative = narrativeBySegment[segment] || 'Pensamos G-CENTRA para ordenar rutas, mercadería, trazabilidad y control operativo.';

  return [
    greeting,
    'Después de dos años de desarrollo, hoy presentamos G-CENTRA: una plataforma pensada para ordenar rutas, mercadería, trazabilidad y control operativo en un solo lugar.',
    tailoredNarrative,
    `Podés ver la plataforma acá: ${COMMERCIAL_PUBLIC_URL}.`,
    'En esta etapa de presentación, ofrecemos un mes de uso sin costo y, si durante ese período necesitan asesoramiento, acompañamiento o capacitación, se los brindamos sin cargo.',
    'Si les interesa, coordinamos una muestra breve y les contamos cómo podría aplicarse a su operación.',
  ].join(' ');
};

const buildCommercialProspectManualMessage = (prospect = {}) => {
  const companyName = String(prospect?.companyName || '').trim();
  const greeting = companyName ? `Hola equipo de ${companyName},` : 'Hola, ¿cómo están?';
  const segment = normalizeComparableText(prospect?.segment || '');
  const narrativeBySegment = {
    'agencias de motos': 'Vimos que trabajan con una operación dinámica donde tener claro qué viaje está activo, qué chofer lo toma y qué evidencias quedaron puede hacer una diferencia real.',
    'mensajeria y logistica urbana': 'Vimos que operan con ritmo urbano y alto movimiento, donde ordenar rutas, estados y seguimiento en tiempo real ayuda mucho a la coordinación diaria.',
    'distribuidoras con reparto propio': 'Vimos que el reparto propio necesita coordinar mejor unidades, destinos, mercadería y trazabilidad desde un solo lugar.',
    'empresas con logistica propia': 'Vimos que una operación con logística interna gana mucha claridad cuando rutas, destinos, unidades y control operativo viven sobre la misma base.',
    'logisticas medianas': 'Vimos que cuando ya hay varios viajes y unidades activas, centralizar operación, trazabilidad y lectura económica simplifica mucho la gestión diaria.',
  };
  const tailoredNarrative = narrativeBySegment[segment] || 'Vimos una operación donde ordenar rutas, mercadería, trazabilidad y control operativo en un solo lugar podría aportar claridad y seguimiento.';

  return [
    greeting,
    '',
    'Les escribimos para presentar G-CENTRA, una plataforma pensada para empresas que brindan servicios de logística o que necesitan ordenar su propia operación logística.',
    tailoredNarrative,
    '',
    `Pueden ver la plataforma acá: ${COMMERCIAL_PUBLIC_URL}`,
    '',
    'En esta etapa estamos ofreciendo un mes de uso sin costo, con acompañamiento, asesoramiento y capacitación sin cargo durante ese período.',
    '',
    'Si les interesa, podemos coordinar una muestra breve para contarles cómo podría aplicarse a su operación.',
    '',
    'Saludos,',
    'G-CENTRA',
    `WhatsApp: ${COMMERCIAL_WHATSAPP}`,
  ].join('\n');
};

const resolveCommercialProspectManualChannelLabel = (channels = {}) => {
  if (channels.website && channels.whatsapp) return 'Mensaje sugerido para formulario web o WhatsApp';
  if (channels.website) return 'Mensaje sugerido para formulario web';
  if (channels.whatsapp) return 'Mensaje sugerido para WhatsApp';
  return 'Mensaje sugerido';
};

const COMMERCIAL_LEGACY_PUBLIC_URL_PATTERN = /https?:\/\/g-centra-app\.web\.app\/?/gi;
const hasLegacyCommercialUrl = (value = '') => /https?:\/\/g-centra-app\.web\.app\/?/i.test(String(value || ''));

const countCommercialUrlOccurrences = (value = '') => {
  const matches = String(value || '').match(/https?:\/\/g-centra\.com\.ar\/?/gi);
  return matches ? matches.length : 0;
};

const finalizeCommercialProspectEmailBody = (prospect = {}) => {
  const draftedBody = String(prospect?.outreachDraft?.emailBody || '').trim();
  if (!draftedBody) {
    return buildCommercialProspectEmailBody(prospect);
  }

  const normalizedDraft = draftedBody.replace(COMMERCIAL_LEGACY_PUBLIC_URL_PATTERN, COMMERCIAL_PUBLIC_URL);
  if (hasLegacyCommercialUrl(draftedBody) || countCommercialUrlOccurrences(normalizedDraft) > 1) {
    return buildCommercialProspectEmailBody(prospect);
  }

  if (normalizedDraft.includes(COMMERCIAL_PUBLIC_URL)) {
    return normalizedDraft;
  }

  return [
    normalizedDraft,
    '',
    `Podés ver la plataforma acá: ${COMMERCIAL_PUBLIC_URL}`,
    '',
    'Además, hoy estamos ofreciendo un mes de uso sin costo. Si durante ese período necesitan asesoramiento, acompañamiento o capacitación, se los brindamos sin cargo.',
  ].join('\n');
};

const finalizeCommercialProspectWhatsAppMessage = (prospect = {}) => {
  const draftedMessage = String(prospect?.outreachDraft?.whatsappBody || '').trim();
  if (!draftedMessage) {
    return buildCommercialProspectWhatsAppMessage(prospect);
  }

  const normalizedDraft = draftedMessage.replace(COMMERCIAL_LEGACY_PUBLIC_URL_PATTERN, COMMERCIAL_PUBLIC_URL);
  if (hasLegacyCommercialUrl(draftedMessage) || countCommercialUrlOccurrences(normalizedDraft) > 1) {
    return buildCommercialProspectWhatsAppMessage(prospect);
  }

  if (normalizedDraft.includes(COMMERCIAL_PUBLIC_URL)) {
    return normalizedDraft;
  }

  return `${normalizedDraft} Podés ver la plataforma acá: ${COMMERCIAL_PUBLIC_URL}. Además, hoy estamos ofreciendo un mes de uso sin costo con acompañamiento sin cargo durante ese período.`;
};

const buildMailtoLink = (prospect = {}) => {
  const email = String(prospect?.contactEmail || prospect?.email || '').trim();
  if (!email) return '';

  const subject = String(prospect?.outreachDraft?.subject || buildCommercialProspectSubject(prospect)).trim();
  const body = finalizeCommercialProspectEmailBody(prospect);
  return `mailto:${encodeURIComponent(email)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
};

const buildProspectWhatsAppLink = (prospect = {}) => {
  const baseUrl = buildWhatsAppUrl(prospect?.contactWhatsApp || prospect?.whatsapp || prospect?.contactValue || '');
  if (!baseUrl) return '';
  const message = finalizeCommercialProspectWhatsAppMessage(prospect);
  return `${baseUrl}?text=${encodeURIComponent(message)}`;
};

const resolveCommercialAgentControl = (raw = {}) => {
  const resolvedAgents = COMMERCIAL_AGENT_DEFINITIONS.reduce((acc, agent) => {
    const current = raw?.[agent.key] || {};
    acc[agent.key] = {
      active: current.active !== false,
      updatedAt: current.updatedAt || null,
    };
    return acc;
  }, {});

  return {
    ...resolvedAgents,
    targetProfile: resolveCommercialTargetProfile(raw?.targetProfile || {}),
    lastBackendRadarRunAt: raw?.lastBackendRadarRunAt || null,
    lastBackendRadarRunSummary: raw?.lastBackendRadarRunSummary || null,
    radar: raw?.radar || {},
    followup: {
      active: raw?.followup?.active !== false,
      responseNotifyTo: String(raw?.followup?.responseNotifyTo || '').trim(),
      processedMessageIds: Array.isArray(raw?.followup?.processedMessageIds) ? raw.followup.processedMessageIds : [],
      lastRunAt: raw?.followup?.lastRunAt || null,
      lastRunSummary: raw?.followup?.lastRunSummary || null,
    },
    updatedAt: raw?.updatedAt || null,
  };
};

const normalizeInternalProfileKey = (value = '') => {
  const normalized = normalizeComparableText(value);
  if (['commercial_sandbox', 'commercial sandbox', 'comercial interna', 'comercial-interna', 'cuenca'].includes(normalized)) {
    return 'commercial_sandbox';
  }
  if (['laboratory', 'laboratorio', 'lab'].includes(normalized)) {
    return 'laboratory';
  }
  return '';
};

const resolveInternalCompanyProfile = (company = {}, generalSettings = {}) => {
  const explicitProfile = normalizeInternalProfileKey(
    generalSettings.internalProfile ||
    generalSettings.environmentTag ||
    company.internalProfile ||
    company.environmentTag
  );

  if (explicitProfile && INTERNAL_COMPANY_PROFILES[explicitProfile]) {
    return {
      key: explicitProfile,
      ...INTERNAL_COMPANY_PROFILES[explicitProfile],
    };
  }

  const normalizedCompanyId = normalizeComparableText(company.id || company.companyId || '');
  const normalizedCompanyName = normalizeComparableText(generalSettings.companyName || company.name || '');

  if (normalizedCompanyId === 'cuenca' || normalizedCompanyName === 'cuenca') {
    return {
      key: 'commercial_sandbox',
      ...INTERNAL_COMPANY_PROFILES.commercial_sandbox,
    };
  }

  if (normalizedCompanyId === 'laboratorio' || normalizedCompanyName === 'laboratorio') {
    return {
      key: 'laboratory',
      ...INTERNAL_COMPANY_PROFILES.laboratory,
    };
  }

  return null;
};

const resolveBillingCycle = (source = {}) => {
  const candidate = normalizeComparableText(
    source?.billingCycle || source?.planBillingCycle || source?.subscriptionCycle || source?.cycle
  );
  return candidate === 'annual' || candidate === 'anual' ? 'annual' : 'monthly';
};

const buildModulesSettingsFromKeys = (moduleKeys = []) => {
  const enabledKeys = new Set([...moduleKeys, ...ALWAYS_ON_MODULE_KEYS]);
  return MODULE_DEFINITIONS.reduce((acc, module) => {
    acc[module.key] = {
      enabled: enabledKeys.has(module.key),
    };
    return acc;
  }, {});
};

const normalizeModulesSettings = (modulesSettings = {}) => {
  return MODULE_DEFINITIONS.reduce((acc, module) => {
    const forcedEnabled = ALWAYS_ON_MODULE_KEYS.has(module.key);
    acc[module.key] = {
      enabled: forcedEnabled ? true : modulesSettings?.[module.key]?.enabled !== false,
    };
    return acc;
  }, {});
};

const getEnabledModules = (modulesSettings = {}) => {
  const normalized = normalizeModulesSettings(modulesSettings);

  return MODULE_DEFINITIONS.filter((module) => normalized[module.key]?.enabled !== false).map((module) => ({
    key: module.key,
    label: module.label,
  }));
};

const detectPresetFromModules = (modulesSettings = {}) => {
  const normalized = normalizeModulesSettings(modulesSettings);

  const matchedPreset = PLAN_PRESETS.find((preset) => {
    const enabledKeys = new Set(preset.modules);
    return MODULE_DEFINITIONS.every((module) => {
      const isEnabled = normalized[module.key]?.enabled !== false;
      return isEnabled === enabledKeys.has(module.key);
    });
  });

  return matchedPreset?.key || null;
};

const buildPlanFields = (planState, extra = {}) => {
  const normalizedPlanKey = normalizeCommercialPlanKey(
    planState?.key || planState?.label || planState?.plan || planState?.planName
  );
  const planLabel = planState?.label || 'Sin plan';
  const isTrial = normalizedPlanKey === 'trial';
  const billingCycle = isTrial ? null : resolveBillingCycle(extra);

  return {
    // Campos canónicos actuales
    plan: planLabel,
    planName: planLabel,
    label: planLabel,
    planId: normalizedPlanKey || null,
    planKey: normalizedPlanKey || null,
    subscriptionPlan: normalizedPlanKey || null,
    subscriptionPlanName: planLabel,

    // Campos legacy que todavía pueden existir en documentos viejos.
    // Se pisan a propósito para que no quede un "Plan Base" rancio clavado arriba.
    currentPlanId: normalizedPlanKey || null,
    currentPlanName: planLabel,

    billingCycle,
    planBillingCycle: billingCycle,
    subscriptionCycle: billingCycle,
    status: isTrial ? 'trial' : normalizedPlanKey ? 'active' : 'inactive',
    trialStatus: isTrial ? extra?.trialStatus || 'active' : extra?.trialStatus || null,
  };
};

const buildPlanSettingsPayload = (planState, extra = {}) => {
  const payload = {
    ...buildPlanFields(planState, extra),
    updatedAt: serverTimestamp(),
  };

  ['trialStartedAt', 'trialEndsAt', 'subscribedAt', 'nextBillingAt'].forEach((field) => {
    if (field in extra) {
      payload[field] = extra[field] || null;
    }
  });

  return payload;
};

const buildModulesSettingsPayload = (moduleKeys = []) => ({
  ...buildModulesSettingsFromKeys(moduleKeys),
  updatedAt: serverTimestamp(),
});

const isRouteActive = (route) => {
  if (!route || typeof route !== 'object') return false;
  if (route.active === true || route.isActive === true) return true;

  const normalizedStatus = String(route.status || route.state || '')
    .trim()
    .toLowerCase();

  return ACTIVE_ROUTE_STATUSES.has(normalizedStatus);
};

const getCompanyStatus = ({ generalSettings, activeRoutesCount, lastActivityAt, hasError }) => {
  if (hasError) {
    return {
      label: 'Revisar',
      tone: 'danger',
      hint: 'Hubo un problema leyendo parte del resumen.',
    };
  }

  if (generalSettings?.isActive === false || generalSettings?.status === 'inactive') {
    return {
      label: 'Inactiva',
      tone: 'muted',
      hint: 'La empresa figura como inactiva.',
    };
  }

  if (activeRoutesCount > 0) {
    return {
      label: 'Con rutas activas',
      tone: 'success',
      hint: `${activeRoutesCount} ruta${activeRoutesCount === 1 ? '' : 's'} activa${activeRoutesCount === 1 ? '' : 's'}.`,
    };
  }

  if (isToday(lastActivityAt)) {
    return {
      label: 'Con actividad hoy',
      tone: 'info',
      hint: 'Se detectó movimiento durante el día.',
    };
  }

  return {
    label: 'Activa',
    tone: 'neutral',
    hint: 'Empresa accesible y sin alertas visibles.',
  };
};

const getTrialState = (generalSettings = {}) => {
  const statusCandidates = [
    generalSettings.status,
    generalSettings.trialStatus,
    generalSettings.planId,
    generalSettings.planKey,
    generalSettings.plan,
    generalSettings.planName,
  ]
    .map(normalizeComparableText)
    .filter(Boolean);

  const currentPlanKey = normalizeCommercialPlanKey(
    generalSettings.planId ||
      generalSettings.planKey ||
      generalSettings.subscriptionPlan ||
      generalSettings.plan ||
      generalSettings.planName
  );
  const endsAt = getDateFromUnknown(generalSettings.trialEndsAt);
  const startedAt = getDateFromUnknown(generalSettings.trialStartedAt || generalSettings.createdAt);
  const trialStatus = normalizeComparableText(generalSettings.trialStatus);
  const hasConvertedOutOfTrial =
    (currentPlanKey && currentPlanKey !== 'trial') ||
    ['converted', 'upgraded', 'upgrade', 'paid', 'active', 'finished', 'finalized'].includes(trialStatus);
  const isTrial = hasConvertedOutOfTrial
    ? false
    : statusCandidates.includes('trial') ||
      statusCandidates.includes('plan trial') ||
      Boolean(endsAt);

  if (!isTrial) {
    return {
      isTrial: false,
      isExpired: false,
      expiresSoon: false,
      remainingDays: null,
      startedAt: null,
      endsAt: null,
      label: '',
      helperText: '',
    };
  }

  const todayStart = getStartOfDay(new Date());
  const endsAtStart = endsAt ? getStartOfDay(endsAt) : null;
  const remainingDays = endsAtStart
    ? Math.max(0, Math.round((endsAtStart.getTime() - todayStart.getTime()) / MS_PER_DAY))
    : null;
  const isExpired = endsAtStart ? endsAtStart.getTime() < todayStart.getTime() : false;
  const expiresSoon = !isExpired && remainingDays !== null && remainingDays <= TRIAL_EXPIRING_SOON_DAYS;

  let label = 'Trial activo';
  let helperText = 'Sin vencimiento cargado todavía.';

  if (isExpired) {
    label = 'Trial vencido';
    helperText = endsAt ? `Venció el ${formatCompactDate(endsAt)}.` : 'El período trial ya venció.';
  } else if (remainingDays === 0) {
    label = 'Vence hoy';
    helperText = endsAt ? `Último día: ${formatCompactDate(endsAt)}.` : 'Último día de trial.';
  } else if (typeof remainingDays === 'number') {
    label = `${remainingDays} día${remainingDays === 1 ? '' : 's'} restantes`;
    helperText = endsAt ? `Vence el ${formatCompactDate(endsAt)}.` : 'Período trial activo.';
  } else if (endsAt) {
    helperText = `Vence el ${formatCompactDate(endsAt)}.`;
  }

  return {
    isTrial: true,
    isExpired,
    expiresSoon,
    remainingDays,
    startedAt,
    endsAt,
    label,
    helperText,
  };
};

const getSubscriptionState = (generalSettings = {}) => {
  const nextBillingAt = getDateFromUnknown(generalSettings.nextBillingAt);
  const billingCycle = resolveBillingCycle(generalSettings);

  if (!nextBillingAt) {
    return {
      hasRenewalDate: false,
      isExpired: false,
      renewsSoon: false,
      remainingDays: null,
      nextBillingAt: null,
      billingCycle,
      label: billingCycle === 'annual' ? 'Plan anual' : 'Plan mensual',
      helperText: 'Sin fecha comercial cargada todavía.',
    };
  }

  const todayStart = getStartOfDay(new Date());
  const renewalStart = getStartOfDay(nextBillingAt);
  const remainingDays = Math.round((renewalStart.getTime() - todayStart.getTime()) / MS_PER_DAY);
  const isExpired = renewalStart.getTime() < todayStart.getTime();
  const renewsSoon = !isExpired && remainingDays !== null && remainingDays <= RENEWAL_EXPIRING_SOON_DAYS;

  let label = billingCycle === 'annual' ? 'Plan anual' : 'Plan mensual';
  let helperText = `Renueva el ${formatCompactDate(nextBillingAt)}.`;

  if (isExpired) {
    label = 'Renovación pendiente';
    helperText = `La fecha comercial fue el ${formatCompactDate(nextBillingAt)}.`;
  } else if (remainingDays === 0) {
    label = 'Renueva hoy';
    helperText = `Fecha comercial: ${formatCompactDate(nextBillingAt)}.`;
  } else if (renewsSoon) {
    label = 'Renueva pronto';
    helperText = `Fecha comercial: ${formatCompactDate(nextBillingAt)}.`;
  }

  return {
    hasRenewalDate: true,
    isExpired,
    renewsSoon,
    remainingDays,
    nextBillingAt,
    billingCycle,
    label,
    helperText,
  };
};

const resolvePlanState = (generalSettings = {}, modulesSettings = {}) => {
  const trialState = getTrialState(generalSettings);
  if (trialState.isTrial) {
    return TRIAL_PLAN;
  }

  const explicitCandidates = [
    generalSettings.planKey,
    generalSettings.planId,
    generalSettings.subscriptionPlan,
    generalSettings.plan,
    generalSettings.planName,
  ]
    .map(normalizeComparableText)
    .filter(Boolean);

  const explicitPreset = explicitCandidates
    .map((candidate) => normalizeCommercialPlanKey(candidate))
    .find((candidate) => PLAN_PRESET_LOOKUP[candidate]);
  if (explicitPreset) {
    return PLAN_PRESET_LOOKUP[explicitPreset];
  }

  if (explicitCandidates.some((candidate) => candidate in { personalizado: true, custom: true, manual: true })) {
    return PERSONALIZED_PLAN;
  }

  const detectedPresetKey = detectPresetFromModules(modulesSettings);
  if (detectedPresetKey) {
    return PLAN_PRESET_LOOKUP[detectedPresetKey];
  }

  const hasPlanSignal = explicitCandidates.length > 0;
  const hasEnabledModules = MODULE_DEFINITIONS.some((module) => modulesSettings?.[module.key]?.enabled !== false);

  if (hasPlanSignal || hasEnabledModules) {
    return PERSONALIZED_PLAN;
  }

  return null;
};

const normalizePlan = (generalSettings = {}, modulesSettings = {}) => {
  const planState = resolvePlanState(generalSettings, modulesSettings);

  return {
    key: planState?.key || null,
    label: planState?.label || 'Sin plan',
    description: planState?.description || 'Todavía sin asignación comercial.',
  };
};

const shouldHidePlaceholderDefault = (summary, totalCount) => {
  if (summary.id !== 'default') return false;
  if (totalCount <= 1) return false;

  const hasSignals =
    summary.plan !== 'Sin plan' ||
    summary.activeRoutesCount > 0 ||
    Boolean(summary.lastActivityAt) ||
    Boolean(summary.generalSettings?.companyName) ||
    Boolean(summary.hasModulesDocument) ||
    Boolean(summary.hasGeneralDocument);

  return !hasSignals;
};

const sortAndFilterSummaries = (summaries = [], activeCompanyId) => {
  return summaries
    .filter((summary) => !shouldHidePlaceholderDefault(summary, summaries.length))
    .sort((a, b) => {
      if (a.id === activeCompanyId) return -1;
      if (b.id === activeCompanyId) return 1;
      return String(a.name || a.id).localeCompare(String(b.name || b.id));
    });
};

const buildCompanySummaryFromState = (company, state = {}) => {
  const generalSettings = state.generalSettings || {};
  const modulesSettings = normalizeModulesSettings(state.modulesSettings || {});
  const routes = Array.isArray(state.routes) ? state.routes : [];
  const planState = normalizePlan(generalSettings, modulesSettings);
  const modules = getEnabledModules(modulesSettings);
  const activeRoutesCount = routes.filter(isRouteActive).length;
  const trialState = getTrialState(generalSettings);
  const billingCycle = resolveBillingCycle(generalSettings);
  const internalProfile = resolveInternalCompanyProfile(company, generalSettings);

  const routeDates = routes.map((route) =>
    getLatestDate(
      route.updatedAt,
      route.lastUpdatedAt,
      route.startedAt,
      route.completedAt,
      route.finishedAt,
      route.createdAt,
      route.timestamp
    )
  );

  const createdAt = getLatestDate(generalSettings.createdAt, generalSettings.trialStartedAt, generalSettings.subscribedAt);
  const lastActivityAt = getLatestDate(
    generalSettings.lastActivityAt,
    generalSettings.updatedAt,
    generalSettings.createdAt,
    routeDates
  );

  return {
    id: company.id,
    name: generalSettings.companyName || company.name || company.id,
    plan: planState.label,
    planKey: planState.key,
    planDescription: planState.description,
    generalSettings,
    modules,
    modulesSettings,
    hasGeneralDocument: Boolean(state.hasGeneralDocument),
    hasModulesDocument: Boolean(state.hasModulesDocument),
    activeRoutesCount,
    lastActivityAt,
    createdAt,
    subscribedAt: getDateFromUnknown(generalSettings.subscribedAt),
    nextBillingAt: getDateFromUnknown(generalSettings.nextBillingAt),
    billingCycle,
    responsibleName: generalSettings.responsibleName || '',
    contactEmail: generalSettings.contactEmail || '',
    contactPhone: generalSettings.contactPhone || '',
    fullAddress: generalSettings.fullAddress || '',
    locality: generalSettings.locality || '',
    province: generalSettings.province || '',
    country: generalSettings.country || '',
    postalCode: generalSettings.postalCode || '',
    internalProfile,
    isProtectedInternalCompany: Boolean(internalProfile?.protected),
    trialState,
    hasActivityToday: isToday(lastActivityAt),
    hasError: Boolean(state.hasError),
    accessSlug:
      generalSettings.accessSlug ||
      generalSettings.companySlug ||
      company.accessSlug ||
      company.companySlug ||
      company.id,
    accessPath:
      generalSettings.accessPath ||
      company.accessPath ||
      buildCompanyAccessPath(
        generalSettings.accessSlug ||
          generalSettings.companySlug ||
          company.accessSlug ||
          company.companySlug ||
          company.id
      ),
  };
};

const applyPlanStateToSummary = (summary, nextModulesSettings, nextGeneralSettings = summary.generalSettings) => {
  const normalizedModulesSettings = normalizeModulesSettings(nextModulesSettings);
  const planState = normalizePlan(nextGeneralSettings, normalizedModulesSettings);

  return {
    ...summary,
    plan: planState.label,
    planKey: planState.key,
    planDescription: planState.description,
    generalSettings: {
      ...nextGeneralSettings,
      ...buildPlanFields(planState, nextGeneralSettings),
    },
    modulesSettings: normalizedModulesSettings,
    modules: getEnabledModules(normalizedModulesSettings),
    billingCycle: resolveBillingCycle(nextGeneralSettings),
    subscribedAt: getDateFromUnknown(nextGeneralSettings.subscribedAt),
    nextBillingAt: getDateFromUnknown(nextGeneralSettings.nextBillingAt),
    trialState: getTrialState(nextGeneralSettings),
  };
};

const applyModuleSettingToSummary = (summary, moduleKey, enabled) => {
  const nextModulesSettings = {
    ...summary.modulesSettings,
    [moduleKey]: {
      enabled,
    },
  };

  return applyPlanStateToSummary(summary, nextModulesSettings, summary.generalSettings);
};

const applyPresetToSummary = (summary, preset, billingCycle = 'monthly') => {
  const nextModulesSettings =
    preset.key === 'trial'
      ? buildModulesSettingsFromKeys(['logistics', 'systemGuide'])
      : buildModulesSettingsFromKeys(preset.modules);

  if (preset.key === 'trial') {
    const trialStartedAt = getDateFromUnknown(summary.generalSettings.trialStartedAt || summary.generalSettings.createdAt) || new Date();
    const trialEndsAt =
      getDateFromUnknown(summary.generalSettings.trialEndsAt) ||
      addDays(trialStartedAt, DEFAULT_TRIAL_DAYS);
    const trialStatus = getStartOfDay(trialEndsAt).getTime() < getStartOfDay(new Date()).getTime() ? 'expired' : 'active';

    const nextGeneralSettings = {
      ...summary.generalSettings,
      ...buildPlanFields(preset, { trialStatus }),
      trialStartedAt,
      trialEndsAt,
      subscribedAt: null,
      nextBillingAt: null,
      billingCycle: null,
      planBillingCycle: null,
      subscriptionCycle: null,
    };

    return applyPlanStateToSummary(summary, nextModulesSettings, nextGeneralSettings);
  }

  const subscribedAt = new Date();
  const nextBillingAt = addDays(subscribedAt, billingCycle === 'annual' ? 365 : 30);
  const existingTrialStartedAt = getDateFromUnknown(summary.generalSettings.trialStartedAt);
  const existingTrialEndsAt = getDateFromUnknown(summary.generalSettings.trialEndsAt);

  const nextGeneralSettings = {
    ...summary.generalSettings,
    ...buildPlanFields(preset, {
      billingCycle,
      trialStatus: existingTrialStartedAt || existingTrialEndsAt ? 'converted' : null,
    }),
    trialStartedAt: existingTrialStartedAt || null,
    trialEndsAt: existingTrialEndsAt || null,
    subscribedAt,
    nextBillingAt,
    billingCycle,
    planBillingCycle: billingCycle,
    subscriptionCycle: billingCycle,
  };

  return applyPlanStateToSummary(summary, nextModulesSettings, nextGeneralSettings);
};

export const CompanyStatusBadge = ({ status }) => {
  const tones = {
    success: 'bg-emerald-600 text-white border-emerald-500 shadow-sm',
    info: 'bg-sky-600 text-white border-sky-500 shadow-sm',
    danger: 'bg-rose-600 text-white border-rose-500 shadow-sm',
    muted: 'bg-slate-700 text-white border-slate-600 shadow-sm',
    neutral: 'bg-violet-600 text-white border-violet-500 shadow-sm',
  };

  return (
    <span className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold ${tones[status.tone] || tones.neutral}`}>
      {status.label}
    </span>
  );
};

export const ModulesChips = ({ modules }) => {
  if (!modules?.length) {
    return <span className="text-sm font-medium text-slate-600">Sin módulos detectados</span>;
  }

  return (
    <div className="flex flex-wrap gap-2">
      {modules.map((module) => (
        <span
          key={module.key}
          className="rounded-full border border-slate-700 bg-slate-800 px-2.5 py-1 text-xs font-semibold text-white shadow-sm"
        >
          {module.label}
        </span>
      ))}
    </div>
  );
};

const LiveSignal = ({ company }) => {
  if (company.activeRoutesCount > 0) {
    return (
      <span className="inline-flex items-center gap-2 rounded-full border border-emerald-500 bg-emerald-600 px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-white shadow-sm">
        <span className="h-2 w-2 animate-pulse rounded-full bg-white" />
        En vivo
      </span>
    );
  }

  if (company.hasActivityToday) {
    return (
      <span className="inline-flex items-center gap-2 rounded-full border border-sky-500 bg-sky-600 px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-white shadow-sm">
        <span className="h-2 w-2 rounded-full bg-white" />
        Movimiento hoy
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-2 rounded-full border border-slate-500 bg-slate-700 px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-white shadow-sm">
      <span className="h-2 w-2 rounded-full bg-slate-200" />
      En espera
    </span>
  );
};

const ModuleToggleButton = ({ module, enabled, busy, locked = false, onToggle }) => {
  const disabled = busy || locked;

  return (
    <button
      type="button"
      onClick={onToggle}
      disabled={disabled}
      className={`group rounded-2xl border p-4 text-left transition-all ${
        enabled
          ? 'border-emerald-400 bg-emerald-100 hover:border-emerald-500 hover:bg-emerald-200'
          : 'border-slate-400 bg-slate-200 hover:border-slate-500 hover:bg-slate-300'
      } ${disabled ? 'cursor-not-allowed opacity-80' : ''}`}
      title={
        locked
          ? `${module.label} es base y siempre queda activo`
          : enabled
            ? `Desactivar ${module.label}`
            : `Activar ${module.label}`
      }
    >
      <div className="flex items-start justify-between gap-3">
        <div className={`rounded-2xl p-2.5 ${enabled ? 'bg-emerald-600 text-white' : 'bg-slate-700 text-white'}`}>
          <Icon path={module.icon} className="h-5 w-5" />
        </div>
        <span
          className={`rounded-full px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide ${
            locked
              ? 'bg-cyan-700 text-white'
              : enabled
                ? 'bg-emerald-700 text-white'
                : 'bg-slate-800 text-white'
          }`}
        >
          {locked ? 'Base' : busy ? 'Guardando' : enabled ? 'Activo' : 'Pausado'}
        </span>
      </div>

      <div className="mt-4">
        <p className="text-sm font-bold text-slate-900">{module.label}</p>
        <p className="mt-1 text-xs text-slate-600">
          {locked ? 'Módulo base siempre visible en la plataforma.' : module.shortDescription}
        </p>
      </div>
    </button>
  );
};

const PlanPresetButton = ({ preset, isCurrent, busy, billingCycle, priceLabel, onApply }) => {
  const cycleLabel = BILLING_OPTIONS.find((option) => option.key === billingCycle)?.label || 'Mensual';

  return (
    <button
      type="button"
      onClick={onApply}
      disabled={busy || isCurrent}
      className={`rounded-2xl border p-4 text-left transition-all ${
        isCurrent
          ? 'border-cyan-500 bg-cyan-100 shadow-md'
          : 'border-slate-400 bg-slate-100 hover:border-slate-500 hover:bg-slate-200'
      } ${busy ? 'cursor-wait opacity-75' : ''} ${isCurrent ? 'cursor-default' : ''}`}
      title={isCurrent ? `Plan ${preset.label} ya aplicado` : `Aplicar plan ${preset.label}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-bold text-slate-900">{preset.label}</p>
          <p className="mt-1 text-xs text-slate-600">{preset.description}</p>
        </div>
        <span className={`rounded-full px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide ${
          isCurrent ? 'bg-cyan-700 text-white' : 'bg-slate-800 text-white'
        }`}>
          {busy ? 'Aplicando' : isCurrent ? 'Actual' : cycleLabel}
        </span>
      </div>

      <div className="mt-3 flex items-end justify-between gap-3">
        <div className="flex flex-wrap gap-2">
          {(preset.modules || []).map((moduleKey) => (
            <span
              key={`${preset.key}-${moduleKey}`}
              className="rounded-full border border-slate-500 bg-white px-2.5 py-1 text-[11px] font-bold text-slate-800 shadow-sm"
            >
              {MODULE_LABELS[moduleKey] || moduleKey}
            </span>
          ))}
        </div>
        <div className="text-right">
          <p className="text-xs font-bold uppercase tracking-[0.14em] text-slate-500">Precio</p>
          <p className="mt-1 text-sm font-black text-slate-900">{priceLabel}</p>
        </div>
      </div>
    </button>
  );
};

const BillingCyclePicker = ({ value = 'monthly', onChange }) => {
  return (
    <div className="inline-flex rounded-2xl border border-slate-300 bg-slate-100 p-1 shadow-sm">
      {BILLING_OPTIONS.map((option) => {
        const active = value === option.key;
        return (
          <button
            key={option.key}
            type="button"
            onClick={() => onChange(option.key)}
            className={`rounded-xl px-3 py-2 text-xs font-bold uppercase tracking-[0.14em] transition ${
              active ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
};

const CommercialPlanEditorCard = ({ plan, currency, onChange }) => {
  const featuresText = Array.isArray(plan.features) ? plan.features.join('\n') : '';

  return (
    <article className="rounded-3xl border border-slate-300 bg-white p-4 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-cyan-700">{plan.eyebrow}</p>
          <h3 className="mt-2 text-lg font-black text-slate-900">{plan.title}</h3>
        </div>

        <label className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-slate-50 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.14em] text-slate-700">
          <input
            type="checkbox"
            checked={Boolean(plan.featured)}
            onChange={(event) => onChange(plan.key, 'featured', event.target.checked)}
            className="h-4 w-4 rounded border-slate-300 text-cyan-600 focus:ring-cyan-500"
          />
          Destacado
        </label>
      </div>

      <div className="mt-4 grid gap-3 md:grid-cols-2">
        <label className="block">
          <span className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-500">Título</span>
          <input
            type="text"
            value={plan.title}
            onChange={(event) => onChange(plan.key, 'title', event.target.value)}
            className="mt-2 w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-800 outline-none transition-all focus:border-cyan-500 focus:ring-2 focus:ring-cyan-200"
          />
        </label>

        <label className="block">
          <span className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-500">Subtítulo</span>
          <input
            type="text"
            value={plan.eyebrow}
            onChange={(event) => onChange(plan.key, 'eyebrow', event.target.value)}
            className="mt-2 w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-800 outline-none transition-all focus:border-cyan-500 focus:ring-2 focus:ring-cyan-200"
          />
        </label>
      </div>

      <label className="mt-3 block">
        <span className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-500">Resumen comercial</span>
        <textarea
          rows={3}
          value={plan.summary}
          onChange={(event) => onChange(plan.key, 'summary', event.target.value)}
          className="mt-2 w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 outline-none transition-all focus:border-cyan-500 focus:ring-2 focus:ring-cyan-200"
        />
      </label>

      <div className="mt-3 grid gap-3 md:grid-cols-2">
        <label className="block">
          <span className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-500">Precio mensual ({currency})</span>
          <input
            type="number"
            min="0"
            disabled={plan.key === 'trial'}
            value={plan.key === 'trial' ? 0 : plan.monthlyPrice}
            onChange={(event) => onChange(plan.key, 'monthlyPrice', Number(event.target.value || 0))}
            className="mt-2 w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-800 outline-none transition-all focus:border-cyan-500 focus:ring-2 focus:ring-cyan-200 disabled:bg-slate-100"
          />
        </label>

        <label className="block">
          <span className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-500">Precio anual ({currency})</span>
          <input
            type="number"
            min="0"
            disabled={plan.key === 'trial'}
            value={plan.key === 'trial' ? 0 : plan.annualPrice}
            onChange={(event) => onChange(plan.key, 'annualPrice', Number(event.target.value || 0))}
            className="mt-2 w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-800 outline-none transition-all focus:border-cyan-500 focus:ring-2 focus:ring-cyan-200 disabled:bg-slate-100"
          />
        </label>
      </div>

      {plan.key !== 'trial' ? (
        <label className="mt-3 block">
          <span className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-500">Badge anual</span>
          <input
            type="text"
            value={plan.annualSavingsLabel}
            onChange={(event) => onChange(plan.key, 'annualSavingsLabel', event.target.value)}
            className="mt-2 w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-800 outline-none transition-all focus:border-cyan-500 focus:ring-2 focus:ring-cyan-200"
          />
        </label>
      ) : null}

      <div className="mt-3 grid gap-3 md:grid-cols-2">
        <label className="block">
          <span className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-500">CTA público</span>
          <input
            type="text"
            value={plan.publicCta}
            onChange={(event) => onChange(plan.key, 'publicCta', event.target.value)}
            className="mt-2 w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-800 outline-none transition-all focus:border-cyan-500 focus:ring-2 focus:ring-cyan-200"
          />
        </label>

        <label className="block">
          <span className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-500">CTA empresa</span>
          <input
            type="text"
            value={plan.companyCta}
            onChange={(event) => onChange(plan.key, 'companyCta', event.target.value)}
            className="mt-2 w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-800 outline-none transition-all focus:border-cyan-500 focus:ring-2 focus:ring-cyan-200"
          />
        </label>
      </div>

      <label className="mt-3 block">
        <span className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-500">Incluye</span>
        <textarea
          rows={5}
          value={featuresText}
          onChange={(event) =>
            onChange(
              plan.key,
              'features',
              event.target.value
                .split('\n')
                .map((item) => item.trim())
                .filter(Boolean)
            )
          }
          className="mt-2 w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 outline-none transition-all focus:border-cyan-500 focus:ring-2 focus:ring-cyan-200"
        />
      </label>
    </article>
  );
};

const CommercialCatalogPanel = ({ draftCatalog, catalogSaving, onChange, onSave }) => {
  return (
    <section className="rounded-[2rem] border border-slate-200 bg-white p-4 shadow-sm md:p-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-cyan-700">Catálogo comercial</p>
          <h2 className="mt-2 text-2xl font-black text-slate-900">Planes, precios y copys sincronizados</h2>
          <p className="mt-2 max-w-3xl text-sm text-slate-600">
            Administrá el catálogo comercial que ve la página pública y también la experiencia de empresa.
          </p>
        </div>

        <button
          type="button"
          onClick={onSave}
          disabled={catalogSaving}
          className={`rounded-2xl px-5 py-3 text-sm font-semibold transition ${
            catalogSaving
              ? 'cursor-wait border border-slate-200 bg-slate-100 text-slate-400'
              : 'bg-cyan-600 text-white hover:bg-cyan-700'
          }`}
        >
          {catalogSaving ? 'Guardando catálogo...' : 'Guardar catálogo'}
        </button>
      </div>

      <div className="mt-5 grid gap-4 xl:grid-cols-2">
        {draftCatalog.plans.map((plan) => (
          <CommercialPlanEditorCard
            key={`commercial-${plan.key}`}
            plan={plan}
            currency={draftCatalog.currency}
            onChange={onChange}
          />
        ))}
      </div>
    </section>
  );
};


const CompanyDeleteModal = ({
  company,
  confirmValue,
  onChangeConfirmValue,
  onClose,
  onConfirm,
  busy = false,
}) => {
  if (!company) return null;

  const expectedTokens = [company.name, company.id]
    .map((value) => normalizeComparableText(value))
    .filter(Boolean);

  const canConfirm = expectedTokens.includes(normalizeComparableText(confirmValue));

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-slate-950/70 px-4 py-6 backdrop-blur-sm">
      <div className="w-full max-w-xl rounded-[2rem] border border-rose-300 bg-white p-6 shadow-2xl">
        <div className="flex items-start gap-4">
          <div className="flex h-14 w-14 flex-none items-center justify-center rounded-2xl bg-rose-100 text-rose-700">
            <Icon path="M12 9v3.75m9.303 3.376c.866 1.5-.217 3.374-1.948 3.374H4.645c-1.73 0-2.813-1.874-1.948-3.374L10.051 3.378c.866-1.5 3.032-1.5 3.898 0l7.354 12.748zM12 15.75h.007v.008H12v-.008z" className="h-7 w-7" />
          </div>

          <div className="min-w-0">
            <p className="text-[11px] font-black uppercase tracking-[0.18em] text-rose-600">Eliminar empresa</p>
            <h3 className="mt-2 text-2xl font-black text-slate-950">{company.name}</h3>
            <p className="mt-3 text-sm leading-6 text-slate-700">
              Esta acción borra la empresa del radar, su configuración principal y las rutas asociadas.
              También limpia referencias visibles en usuarios vinculados. No elimina cuentas de Firebase Authentication.
            </p>
          </div>
        </div>

        <div className="mt-5 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
          Para confirmar, escribí <span className="font-black">{company.name}</span> o <span className="font-black">{company.id}</span>.
        </div>

        <label className="mt-5 block">
          <span className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-500">Confirmación</span>
          <input
            type="text"
            value={confirmValue}
            onChange={(event) => onChangeConfirmValue(event.target.value)}
            placeholder={company.name}
            className="mt-2 w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-800 outline-none transition-all focus:border-rose-500 focus:ring-2 focus:ring-rose-200"
          />
        </label>

        <div className="mt-6 flex flex-wrap items-center justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
          >
            Cancelar
          </button>

          <button
            type="button"
            onClick={onConfirm}
            disabled={busy || !canConfirm}
            className={`rounded-xl px-4 py-2 text-sm font-semibold text-white transition ${
              busy || !canConfirm
                ? 'cursor-not-allowed bg-rose-300'
                : 'bg-rose-600 hover:bg-rose-700'
            }`}
          >
            {busy ? 'Eliminando...' : 'Eliminar empresa'}
          </button>
        </div>
      </div>
    </div>
  );
};


export const CompanyCard = ({
  company,
  commercialCatalog,
  billingDraftMap,
  pendingBillingRequest = null,
  onChangeBillingCycle,
  isActiveContext,
  onToggleModule,
  onApplyPlan,
  onRequestDeleteCompany,
  toggleBusyMap,
  planBusyMap,
  deleteBusyMap,
}) => {
  const status = getCompanyStatus(company);
  const enabledCount = company.modules.length;
  const trialState = company.trialState || getTrialState(company.generalSettings);
  const createdAtLabel = company.createdAt ? formatCompactDate(company.createdAt) : 'Sin fecha';
  const locationLabel = [company.locality, company.province, company.country].filter(Boolean).join(', ') || 'Sin dato';
  const selectedBillingCycle = billingDraftMap?.[company.id] || company.billingCycle || 'monthly';
  const planCycleLabel = selectedBillingCycle === 'annual' ? 'Anual' : 'Mensual';
  const subscriptionState = trialState.isTrial
    ? null
    : getSubscriptionState({
        ...company.generalSettings,
        billingCycle: selectedBillingCycle,
        nextBillingAt: company.nextBillingAt,
        subscribedAt: company.subscribedAt,
      });
  const trialToneClasses = trialState.isExpired
    ? 'border-rose-200 bg-rose-50 text-rose-700'
    : trialState.expiresSoon
      ? 'border-amber-200 bg-amber-50 text-amber-700'
      : 'border-cyan-200 bg-cyan-50 text-cyan-700';
  const subscriptionToneClasses = subscriptionState?.isExpired
    ? 'border-rose-200 bg-rose-50 text-rose-700'
    : subscriptionState?.renewsSoon
      ? 'border-amber-200 bg-amber-50 text-amber-700'
      : 'border-slate-300 bg-slate-100 text-slate-700';
  const commercialToneClasses = trialState.isTrial ? trialToneClasses : subscriptionToneClasses;
  const commercialHeadline = trialState.isTrial ? `Trial · ${trialState.label}` : `${company.plan} · ${planCycleLabel}`;
  const commercialHelper = trialState.isTrial
    ? trialState.helperText
    : subscriptionState?.helperText || company.planDescription;
  const commercialMetaPill = trialState.isTrial
    ? trialState.endsAt
      ? `Vence ${formatCompactDate(trialState.endsAt)}`
      : 'Trial activo'
    : subscriptionState?.hasRenewalDate
      ? `Renueva ${formatCompactDate(subscriptionState.nextBillingAt)}`
      : 'Sin fecha comercial';
  const hasPendingBillingRequest = Boolean(pendingBillingRequest);
  const pendingBillingLabel = pendingBillingRequest
    ? `${pendingBillingRequest.requestedPlanTitle || pendingBillingRequest.requestedPlanKey || 'Plan solicitado'} · ${
        pendingBillingRequest.billingCycle === 'annual' ? 'Anual' : 'Mensual'
      }`
    : '';
  const internalProfile = company.internalProfile || null;
  const isProtectedInternalCompany = Boolean(company.isProtectedInternalCompany);
  const visibleAccessSlug = String(company.accessSlug || '').trim();
  const visibleAccessPath = visibleAccessSlug ? buildCompanyAccessPath(visibleAccessSlug) : company.accessPath;

  const [expanded, setExpanded] = useState(false);

  const customerFields = [
    {
      label: 'Responsable',
      value: company.responsibleName || 'Sin dato',
      helper: 'Contacto principal de la empresa.',
    },
    {
      label: 'Email',
      value: company.contactEmail || 'Sin dato',
      helper: 'Correo declarado en el alta.',
    },
    {
      label: 'Teléfono',
      value: company.contactPhone || 'Sin dato',
      helper: 'Canal directo del cliente.',
    },
    {
      label: 'Alta',
      value: createdAtLabel,
      helper: 'Fecha registrada en el sistema.',
    },
    {
      label: 'Dirección operativa',
      value: company.fullAddress || 'Sin dato',
      helper: 'Base operativa declarada.',
    },
    {
      label: 'Ubicación',
      value: locationLabel,
      helper: company.postalCode ? `CP ${company.postalCode}` : 'Localidad, provincia y país.',
    },
    {
      label: trialState.isTrial ? 'Estado trial' : 'Estado comercial',
      value: commercialHeadline,
      helper: commercialHelper,
    },
  ];

  return (
    <article
      className={`overflow-hidden rounded-3xl border bg-white shadow-xl transition-all ${
        hasPendingBillingRequest
          ? 'animate-pulse border-amber-300 shadow-[0_0_0_1px_rgba(245,158,11,0.24),0_18px_40px_rgba(245,158,11,0.16)]'
          : 'border-slate-300'
      }`}
    >
      <button
        type="button"
        onClick={() => setExpanded((prev) => !prev)}
        className={`w-full px-4 py-4 text-left transition-all md:px-5 ${
          hasPendingBillingRequest ? 'bg-amber-50/70 hover:bg-amber-50' : 'bg-white hover:bg-slate-50'
        }`}
      >
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="min-w-0 space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="truncate text-base font-black text-slate-900 md:text-lg">{company.name}</h3>

              {isActiveContext && (
                <span className="rounded-full border border-cyan-500 bg-cyan-100 px-2.5 py-1 text-[11px] font-bold text-cyan-900 shadow-sm">
                  contexto actual
                </span>
              )}

              <LiveSignal company={company} />
              {hasPendingBillingRequest ? (
                <span className="rounded-full border border-amber-300 bg-amber-100 px-2.5 py-1 text-[11px] font-black uppercase tracking-[0.14em] text-amber-900">
                  Solicitud de plan
                </span>
              ) : null}
              {internalProfile ? (
                <span className={`rounded-full border px-2.5 py-1 text-[11px] font-black uppercase tracking-[0.14em] ${internalProfile.badgeTone}`}>
                  {internalProfile.label}
                </span>
              ) : null}
            </div>

            <div className="flex flex-wrap items-center gap-2 text-sm text-slate-500">
              <span className="rounded-full border border-slate-300 bg-slate-100 px-2.5 py-1 font-bold text-slate-900">
                {commercialHeadline}
              </span>
              <CompanyStatusBadge status={status} />
              <span className={`rounded-full border px-2.5 py-1 font-bold ${commercialToneClasses}`}>
                {commercialMetaPill}
              </span>
              <span className="rounded-full border border-slate-300 bg-slate-100 px-2.5 py-1 font-bold text-slate-900">
                {enabledCount}/{MODULE_DEFINITIONS.length} módulos
              </span>
              <span className="rounded-full border border-slate-300 bg-slate-100 px-2.5 py-1 font-bold text-slate-900">
                {company.activeRoutesCount} rutas activas
              </span>
            </div>

            <p className="text-sm text-slate-500">{commercialHelper}</p>
            {hasPendingBillingRequest ? (
              <p className="text-sm font-semibold text-amber-800">
                Pedido pendiente: {pendingBillingLabel}. Revisar pago y activación desde esta empresa.
              </p>
            ) : null}
            {internalProfile?.helper ? (
              <p className="text-sm font-medium text-slate-600">{internalProfile.helper}</p>
            ) : null}
          </div>

          <div className="flex items-center justify-between gap-3 lg:justify-end">
            <div className="text-right">
              <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-500">Última actividad</p>
              <p className="mt-1 text-xs font-semibold text-slate-800 md:text-sm">{formatLastActivity(company.lastActivityAt)}</p>
            </div>

            <div
              className={`flex h-11 w-11 items-center justify-center rounded-2xl border transition-all ${
                expanded
                  ? 'border-cyan-500 bg-cyan-100 text-cyan-800'
                  : 'border-slate-300 bg-slate-100 text-slate-700'
              }`}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                className={`h-5 w-5 transition-transform ${expanded ? 'rotate-180' : ''}`}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>
        </div>
      </button>

      {expanded && (
        <div className="border-t border-slate-200 bg-slate-50 px-4 py-4 md:px-5">
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
              <div className="grid flex-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
                {hasPendingBillingRequest ? (
                  <div className="rounded-2xl border border-amber-300 bg-amber-50 p-4 shadow-sm md:col-span-2 xl:col-span-4">
                    <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                      <div>
                        <p className="text-xs font-bold uppercase tracking-wide text-amber-700">Solicitud pendiente</p>
                        <p className="mt-2 text-sm font-semibold text-slate-900">{pendingBillingLabel}</p>
                        <p className="mt-1 text-xs text-slate-700">
                          {pendingBillingRequest.requestedByEmail || 'Sin correo'} · {formatBillingRequestTimestamp(pendingBillingRequest.createdAt)}
                        </p>
                      </div>
                      <div className="rounded-full border border-amber-300 bg-white px-3 py-1 text-[11px] font-black uppercase tracking-[0.14em] text-amber-800">
                        Esperando activación
                      </div>
                    </div>
                  </div>
                ) : null}

                <div className="rounded-2xl border border-slate-300 bg-white p-4 shadow-sm">
                  <p className="text-xs font-bold uppercase tracking-wide text-slate-600">Última actividad</p>
                  <p className="mt-2 text-sm font-semibold text-slate-900">{formatLastActivity(company.lastActivityAt)}</p>
                  <p className="mt-1 text-xs text-slate-600">{status.hint}</p>
                </div>

                <div className="rounded-2xl border border-slate-300 bg-white p-4 shadow-sm">
                  <p className="text-xs font-bold uppercase tracking-wide text-slate-600">Rutas activas</p>
                  <p className="mt-2 text-2xl font-black text-slate-950">{company.activeRoutesCount}</p>
                  <p className="mt-1 text-xs text-slate-600">Lectura ejecutiva de operación actual.</p>
                </div>

                <div className="rounded-2xl border border-slate-300 bg-white p-4 shadow-sm">
                  <p className="text-xs font-bold uppercase tracking-wide text-slate-600">Módulos activos</p>
                  <div className="mt-3">
                    <ModulesChips modules={company.modules} />
                  </div>
                </div>

                <div className={`rounded-2xl border bg-white p-4 shadow-sm ${commercialToneClasses}`}>
                  <p className="text-xs font-bold uppercase tracking-wide text-slate-600">Estado comercial</p>
                  <p className="mt-2 text-sm font-semibold text-slate-900">{commercialHeadline}</p>
                  <p className="mt-1 text-xs text-slate-600">{commercialHelper}</p>
                  {!trialState.isTrial && subscriptionState?.hasRenewalDate ? (
                    <p className="mt-2 text-[11px] font-bold uppercase tracking-[0.14em] text-slate-700">
                      Próxima fecha: {formatCompactDate(subscriptionState.nextBillingAt)}
                    </p>
                  ) : null}
                </div>
              </div>

              <div className="space-y-3 lg:w-[260px]">
                {company.id !== 'default' ? (
                  <button
                    type="button"
                    onClick={() => onRequestDeleteCompany(company)}
                    disabled={Boolean(deleteBusyMap?.[company.id]) || isProtectedInternalCompany}
                    className={`inline-flex w-full items-center justify-center gap-2 rounded-2xl border px-4 py-3 text-sm font-semibold transition-all ${
                      deleteBusyMap?.[company.id] || isProtectedInternalCompany
                        ? 'cursor-not-allowed border-rose-200 bg-rose-100 text-rose-400'
                        : 'border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100'
                    }`}
                  >
                    <Icon path="M6 7.5h12m-10.5 0V6A1.5 1.5 0 019 4.5h6A1.5 1.5 0 0116.5 6v1.5m-9 0v9.75A2.25 2.25 0 009.75 19.5h4.5A2.25 2.25 0 0016.5 17.25V7.5" className="w-4 h-4" />
                    {isProtectedInternalCompany ? 'Protegida' : deleteBusyMap?.[company.id] ? 'Eliminando...' : 'Eliminar empresa'}
                  </button>
                ) : null}
              </div>
            </div>

            <div className="rounded-[1.6rem] border border-slate-300 bg-white p-4 shadow-sm">
              <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                <div>
                  <h4 className="text-sm font-black uppercase tracking-wide text-slate-900">Ficha del cliente</h4>
                  <p className="mt-1 text-sm text-slate-700">Datos comerciales y operativos tomados del alta real de la empresa.</p>
                </div>

                <div className="text-xs font-bold text-slate-800">
                  Acceso: {visibleAccessSlug || 'Sin slug todavía'}
                </div>
              </div>

              <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                {customerFields.map((field) => (
                  <div
                    key={`${company.id}-${field.label}`}
                    className="rounded-2xl border border-slate-200 bg-slate-50 p-3"
                  >
                    <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-500">{field.label}</p>
                    <p className="mt-2 break-words text-sm font-semibold text-slate-900">{field.value}</p>
                    <p className="mt-1 break-words text-xs text-slate-600">{field.helper}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-[1.6rem] border border-cyan-200 bg-cyan-50/70 p-4">
              <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
                <div>
                  <h4 className="text-sm font-black uppercase tracking-wide text-slate-900">Plan de la empresa</h4>
                  <p className="mt-1 text-sm text-slate-700">
                    Ajustá plan, ciclo y presencia comercial de cada empresa desde una sola vista.
                  </p>
                </div>

                <div className="flex flex-col items-start gap-2 lg:items-end">
                  <BillingCyclePicker
                    value={selectedBillingCycle}
                    onChange={(cycle) => onChangeBillingCycle(company.id, cycle)}
                  />
                  <div className="text-xs font-bold text-slate-800">
                    {trialState.isTrial
                      ? `Trial · ${trialState.label}`
                      : `${company.plan} · ${selectedBillingCycle === 'annual' ? 'Anual' : 'Mensual'}`}
                  </div>
                </div>
              </div>

              <div className="mt-4 grid gap-3 xl:grid-cols-4">
                {[TRIAL_PLAN, ...PLAN_PRESETS].map((preset) => {
                  const catalogPlan = commercialCatalog.plans.find((plan) => plan.key === preset.key);
                  const priceLabel = preset.key === 'trial'
                    ? 'Gratis'
                    : formatCommercialPrice(
                        selectedBillingCycle === 'annual' ? catalogPlan?.annualPrice : catalogPlan?.monthlyPrice,
                        commercialCatalog.currency
                      );

                  return (
                    <PlanPresetButton
                      key={`${company.id}-${preset.key}`}
                      preset={preset}
                      isCurrent={company.planKey === preset.key}
                      busy={Boolean(planBusyMap?.[company.id])}
                      billingCycle={selectedBillingCycle}
                      priceLabel={priceLabel}
                      onApply={() => onApplyPlan(company, preset, selectedBillingCycle)}
                    />
                  );
                })}
              </div>

              <p className="mt-3 text-xs font-semibold text-slate-800">
                {company.planKey === PERSONALIZED_PLAN.key
                  ? 'La empresa está operando con una combinación personalizada de módulos.'
                  : company.nextBillingAt && !trialState.isTrial
                    ? `Próxima renovación: ${formatCompactDate(company.nextBillingAt)}.`
                    : company.planDescription}
              </p>
            </div>

            <div className="rounded-[1.6rem] border border-slate-300 bg-white p-4 shadow-sm">
              <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                <div>
                  <h4 className="text-sm font-black uppercase tracking-wide text-slate-900">Gestión de módulos</h4>
                  <p className="mt-1 text-sm text-slate-700">
                    Activá o pausá herramientas por empresa y mantené cada entorno alineado a su operación.
                  </p>
                </div>

                <div className="text-xs font-bold text-slate-800">
                  Ruta de acceso: {visibleAccessPath || 'Sin ruta todavía'}
                </div>
              </div>

              <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-5">
                {MODULE_DEFINITIONS.map((module) => {
                  const locked = ALWAYS_ON_MODULE_KEYS.has(module.key);
                  const enabled = locked ? true : company.modulesSettings?.[module.key]?.enabled !== false;
                  const busy = Boolean(toggleBusyMap?.[`${company.id}:${module.key}`]);

                  return (
                    <ModuleToggleButton
                      key={`${company.id}-${module.key}`}
                      module={module}
                      enabled={enabled}
                      busy={busy}
                      locked={locked}
                      onToggle={() => {
                        if (locked) return;
                        onToggleModule(company, module.key, !enabled);
                      }}
                    />
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}
    </article>
  );
};

export const TrialOverviewPanel = ({ companies = [] }) => {
  const trialCompanies = companies
    .filter((company) => company.trialState?.isTrial)
    .sort((a, b) => {
      const aTime = getDateFromUnknown(a.trialState?.endsAt)?.getTime?.() || Number.MAX_SAFE_INTEGER;
      const bTime = getDateFromUnknown(b.trialState?.endsAt)?.getTime?.() || Number.MAX_SAFE_INTEGER;
      return aTime - bTime;
    });

  if (!trialCompanies.length) {
    return null;
  }

  const activeTrials = trialCompanies.filter((company) => !company.trialState?.isExpired);
  const expiringTrials = activeTrials.filter((company) => company.trialState?.expiresSoon);
  const expiredTrials = trialCompanies.filter((company) => company.trialState?.isExpired);

  const cards = [
    {
      label: 'Trials activos',
      value: activeTrials.length,
      helper: 'Empresas operando todavía dentro del período gratuito.',
      tone: 'border-cyan-200 bg-cyan-50 text-cyan-800',
    },
    {
      label: 'Por vencer',
      value: expiringTrials.length,
      helper: `Trials que vencen dentro de ${TRIAL_EXPIRING_SOON_DAYS} días.`,
      tone: 'border-amber-200 bg-amber-50 text-amber-800',
    },
    {
      label: 'Vencidos',
      value: expiredTrials.length,
      helper: 'Casos para revisar o convertir a plan activo.',
      tone: 'border-rose-200 bg-rose-50 text-rose-800',
    },
  ];

  return (
    <section className="rounded-[2rem] border border-slate-200 bg-white p-4 shadow-sm md:p-6">
      <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-cyan-700">Panel trial</p>
          <h2 className="mt-2 text-2xl font-black text-slate-900">Lectura rápida de altas temporales</h2>
          <p className="mt-2 max-w-3xl text-sm text-slate-600">
            Seguimiento de trials activos, próximos vencimientos y conversiones a plan comercial.
          </p>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 shadow-sm">
          <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500">Empresas en trial</p>
          <p className="mt-2 text-2xl font-black text-slate-900">{trialCompanies.length}</p>
        </div>
      </div>

      <div className="mt-5 grid gap-4 xl:grid-cols-[0.9fr_1.1fr]">
        <div className="grid gap-4 md:grid-cols-3 xl:grid-cols-1">
          {cards.map((card) => (
            <div key={card.label} className={`rounded-3xl border p-4 shadow-sm ${card.tone}`}>
              <p className="text-xs font-bold uppercase tracking-[0.16em]">{card.label}</p>
              <p className="mt-2 text-3xl font-black">{card.value}</p>
              <p className="mt-2 text-sm">{card.helper}</p>
            </div>
          ))}
        </div>

        <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4 shadow-sm">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h3 className="text-sm font-black uppercase tracking-[0.18em] text-slate-900">Seguimiento por empresa</h3>
              <p className="mt-1 text-sm text-slate-600">Próximos vencimientos y estado de cada alta trial.</p>
            </div>
          </div>

          <div className="mt-4 space-y-3">
            {trialCompanies.slice(0, 8).map((company) => {
              const trialState = company.trialState || getTrialState(company.generalSettings);
              const pillTone = trialState.isExpired
                ? 'border-rose-500/30 bg-rose-500/10 text-rose-200'
                : trialState.expiresSoon
                  ? 'border-amber-500/30 bg-amber-500/10 text-amber-200'
                  : 'border-cyan-500/30 bg-cyan-500/10 text-cyan-200';

              return (
                <div
                  key={`${company.id}-trial-row`}
                  className="flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white p-4 md:flex-row md:items-center md:justify-between"
                >
                  <div className="min-w-0">
                    <p className="truncate text-sm font-bold text-slate-900">{company.name}</p>
                    <p className="mt-1 text-xs text-slate-500">{company.contactEmail || company.responsibleName || company.accessPath}</p>
                  </div>

                  <div className="flex flex-wrap items-center gap-2">
                    <span className={`rounded-full border px-2.5 py-1 text-[11px] font-bold ${pillTone}`}>{trialState.label}</span>
                    <span className="rounded-full border border-slate-200 bg-slate-100 px-2.5 py-1 text-[11px] font-bold text-slate-700">
                      {trialState.endsAt ? `Vence ${formatCompactDate(trialState.endsAt)}` : 'Sin fecha de vencimiento'}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

const ProspectStagePill = ({ statusKey = 'pending_review' }) => {
  const status = getProspectStatusMeta(statusKey);
  return (
    <span className={`rounded-full border px-2.5 py-1 text-[11px] font-bold uppercase tracking-[0.14em] ${status.tone}`}>
      {status.label}
    </span>
  );
};

const ProspectStageActions = ({ prospect, onAdvance, busy = false }) => {
  const statusKey = normalizeProspectStatusKey(prospect?.status);
  const channels = resolveCommercialChannels(prospect);
  const hasManualChannel =
    Boolean(channels.website) ||
    Boolean(channels.whatsapp) ||
    (!channels.email && Boolean(String(prospect?.contactValue || prospect?.sourceUrl || '').trim()));
  const actionMap = {
    pending_review: [
      ...(channels.email && !channels.email.endsWith('.example')
        ? [{ key: 'queued_to_send', label: 'Dejar listo para agente', tone: 'cyan' }]
        : []),
      ...(!channels.email && hasManualChannel
        ? [{ key: 'manual_sent', label: 'Registrar contacto manual', tone: 'indigo' }]
        : []),
      { key: 'archived', label: 'Archivar', tone: 'rose' },
    ],
    queued_to_send: [
      { key: 'sent', label: 'Registrar envío externo', tone: 'indigo' },
      { key: 'archived', label: 'Archivar', tone: 'rose' },
    ],
    sent: [
      { key: 'responded', label: 'Marcar respuesta', tone: 'cyan' },
      { key: 'archived', label: 'Sin respuesta', tone: 'rose' },
    ],
    manual_sent: [
      { key: 'responded', label: 'Marcar respuesta', tone: 'cyan' },
      { key: 'archived', label: 'Sin respuesta', tone: 'rose' },
    ],
    responded: [
      { key: 'demo', label: 'Agendar demo', tone: 'indigo' },
      { key: 'archived', label: 'Archivar', tone: 'rose' },
    ],
    demo: [
      { key: 'trial', label: 'Activar prueba', tone: 'cyan' },
      { key: 'archived', label: 'Archivar', tone: 'rose' },
    ],
    trial: [
      { key: 'converted', label: 'Convertir cliente', tone: 'indigo' },
      { key: 'archived', label: 'Archivar', tone: 'rose' },
    ],
    archived: [
      { key: 'pending_review', label: 'Reactivar', tone: 'slate' },
    ],
  };
  const actions = actionMap[statusKey] || [];

  if (!actions.length) {
    return null;
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      {actions.map((action) => (
        <RadarActionButton
          key={`${prospect?.id || 'prospect'}-${action.key}`}
          onClick={() => onAdvance?.(prospect, action.key)}
          disabled={busy}
          tone={action.tone}
        >
          {action.label}
        </RadarActionButton>
      ))}
    </div>
  );
};

const ProspectContactActions = ({ prospect, onSend, busy = false }) => {
  const channels = resolveCommercialChannels(prospect);
  const statusKey = normalizeProspectStatusKey(prospect?.status);

  if (!['pending_review', 'queued_to_send'].includes(statusKey)) {
    return null;
  }

  return (
    <div className="mt-3 flex flex-wrap items-center gap-2">
      {channels.email && !channels.email.endsWith('.example') ? (
        <RadarActionButton
          onClick={() => onSend?.(prospect, 'email')}
          disabled={busy}
          tone="indigo"
        >
          Enviar mail
        </RadarActionButton>
      ) : null}
      {channels.whatsapp ? (
        <RadarActionButton
          onClick={() => onSend?.(prospect, 'whatsapp')}
          disabled={busy}
          tone="cyan"
        >
          Enviar WhatsApp
        </RadarActionButton>
      ) : null}
    </div>
  );
};

const RadarStagePill = ({ statusKey = 'detected' }) => {
  const meta = getRadarStatusMeta(statusKey);
  return (
    <span className={`inline-flex items-center rounded-full border px-3 py-1 text-[10px] font-black uppercase tracking-[0.14em] ${meta.tone}`}>
      {meta.label}
    </span>
  );
};

const RadarActionButton = ({ onClick, disabled = false, tone = 'slate', children }) => {
  const toneClassMap = {
    slate: disabled
      ? 'cursor-wait border-slate-200 bg-slate-100 text-slate-400'
      : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-100',
    cyan: disabled
      ? 'cursor-wait border-cyan-200 bg-cyan-50 text-cyan-400'
      : 'border-cyan-200 bg-cyan-50 text-cyan-700 hover:bg-cyan-100',
    indigo: disabled
      ? 'cursor-wait border-indigo-200 bg-indigo-50 text-indigo-400'
      : 'border-indigo-200 bg-indigo-50 text-indigo-700 hover:bg-indigo-100',
    fuchsia: disabled
      ? 'cursor-wait border-fuchsia-200 bg-fuchsia-50 text-fuchsia-400'
      : 'border-fuchsia-200 bg-fuchsia-50 text-fuchsia-700 hover:bg-fuchsia-100',
    rose: disabled
      ? 'cursor-wait border-rose-200 bg-rose-50 text-rose-400'
      : 'border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100',
  };

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`rounded-full border px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.14em] transition ${toneClassMap[tone] || toneClassMap.slate}`}
    >
      {children}
    </button>
  );
};

const CommercialArgentinaCoverageMap = ({
  targetCities = [],
  activeCity = '',
  activeQuery = '',
  activeSegment = '',
  discoveryRunning = false,
  lastDiscoverySummary = null,
}) => {
  const activeProvinceKeys = useMemo(() => buildCommercialProvinceSet(activeCity ? [activeCity] : []), [activeCity]);
  const targetProvinceKeys = useMemo(() => buildCommercialProvinceSet(targetCities), [targetCities]);
  const coveredCityValues = useMemo(() => {
    const selected = Array.isArray(lastDiscoverySummary?.selectedCityBreakdown)
      ? lastDiscoverySummary.selectedCityBreakdown.map((item) => item?.city)
      : [];
    const discovered = Array.isArray(lastDiscoverySummary?.discoveredCityBreakdown)
      ? lastDiscoverySummary.discoveredCityBreakdown.map((item) => item?.city)
      : [];
    return [...selected, ...discovered].filter(Boolean);
  }, [lastDiscoverySummary]);
  const coveredProvinceKeys = useMemo(() => buildCommercialProvinceSet(coveredCityValues), [coveredCityValues]);
  const lastSelectedCount = Number(lastDiscoverySummary?.selected || 0);

  return (
    <div className="rounded-[2rem] border border-slate-200 bg-[linear-gradient(180deg,#f8fafc_0%,#eef6ff_100%)] p-5 shadow-[0_18px_48px_rgba(15,23,42,0.08)]">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-cyan-700">Radar territorial</p>
          <h3 className="mt-2 text-lg font-black text-slate-950">Argentina en seguimiento</h3>
          <p className="mt-2 max-w-xl text-sm leading-6 text-slate-600">
            Cada pasada trabaja una tanda territorial distinta para abrir mercado por provincias y localidades sin insistir siempre sobre los mismos clientes.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {[
            ['Buscando ahora', 'border-cyan-200 bg-cyan-50 text-cyan-700'],
            ['Zona cubierta', 'border-emerald-200 bg-emerald-50 text-emerald-700'],
            ['Zona objetivo', 'border-slate-200 bg-white text-slate-600'],
          ].map(([label, tone]) => (
            <span key={label} className={`rounded-full border px-3 py-1 text-[10px] font-bold uppercase tracking-[0.14em] ${tone}`}>
              {label}
            </span>
          ))}
        </div>
      </div>

      <div className="mt-5 grid gap-5 lg:grid-cols-[0.9fr_1.1fr]">
        <div className="rounded-[1.8rem] border border-white/80 bg-white/75 p-4 shadow-inner">
          <div className="relative mx-auto h-[420px] w-full max-w-[310px] overflow-hidden rounded-[1.6rem] border border-slate-200 bg-[radial-gradient(circle_at_50%_18%,rgba(34,211,238,0.10),transparent_28%),linear-gradient(180deg,#f8fafc_0%,#edf3f9_100%)]">
            <img
              src={commercialArgentinaMap}
              alt="Mapa de Argentina por provincias"
              className="absolute inset-0 h-full w-full object-contain px-2 py-2"
            />
            {COMMERCIAL_ARGENTINA_PROVINCE_BLOCKS.map((province) => {
              const isActive = activeProvinceKeys.has(province.key);
              const isCovered = coveredProvinceKeys.has(province.key);
              const isTarget = targetProvinceKeys.has(province.key);
              if (!isActive && !isCovered && !isTarget) return null;

              const markerSize = isActive ? 26 : isCovered ? 20 : 14;
              const toneClass = isActive
                ? 'border-cyan-400 bg-cyan-400/25 shadow-[0_0_0_8px_rgba(34,211,238,0.16)]'
                : isCovered
                  ? 'border-emerald-400 bg-emerald-400/18 shadow-[0_0_0_6px_rgba(16,185,129,0.12)]'
                  : 'border-slate-400 bg-white/70 shadow-[0_0_0_4px_rgba(148,163,184,0.10)]';
              return (
                <div
                  key={province.key}
                  title={province.label}
                  className={`absolute rounded-full border-2 transition ${toneClass} ${isActive ? 'animate-pulse' : ''}`}
                  style={{
                    left: `${province.x + province.width / 2}%`,
                    top: `${province.y + province.height / 2}%`,
                    width: `${markerSize}px`,
                    height: `${markerSize}px`,
                    transform: 'translate(-50%, -50%)',
                  }}
                />
              );
            })}
            <div className="absolute inset-x-4 bottom-3 rounded-2xl border border-white/90 bg-white/88 px-3 py-2 shadow-sm backdrop-blur">
              <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500">Lectura territorial</p>
              <p className="mt-1 text-xs font-semibold text-slate-800">
                {discoveryRunning
                  ? `Buscando en ${activeCity || 'una nueva zona'}`
                  : `${coveredProvinceKeys.size || 0} provincias tocadas en la última lectura útil`}
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-3">
          <div className="rounded-[1.6rem] border border-slate-200 bg-white px-4 py-4 shadow-sm">
            <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-slate-500">Foco actual</p>
            <p className="mt-2 text-base font-black text-slate-900">
              {discoveryRunning ? (activeCity || 'Buscando nueva plaza') : 'Radar en espera'}
            </p>
            <p className="mt-1 text-sm text-slate-600">
              {discoveryRunning
                ? [activeSegment, activeQuery].filter(Boolean).join(' · ')
                : 'La próxima corrida automática volverá a abrir una nueva tanda territorial.'}
            </p>
          </div>

          <div className="rounded-[1.6rem] border border-slate-200 bg-white px-4 py-4 shadow-sm">
            <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-slate-500">Cobertura reciente</p>
            <p className="mt-2 text-sm leading-6 text-slate-600">
              {coveredProvinceKeys.size
                ? `${coveredProvinceKeys.size} provincias tocaron la última búsqueda útil.`
                : 'Todavía no hay provincias cubiertas en la última pasada registrada.'}
            </p>
            {coveredCityValues.length ? (
              <div className="mt-3 flex flex-wrap gap-2">
                {coveredCityValues.slice(0, 10).map((city) => (
                  <span
                    key={`covered-city-${city}`}
                    className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.12em] text-slate-600"
                  >
                    {city}
                  </span>
                ))}
              </div>
            ) : null}
          </div>

          {lastDiscoverySummary ? (
            <div className={`rounded-[1.6rem] border px-4 py-4 shadow-sm ${lastSelectedCount === 0 ? 'border-amber-200 bg-amber-50' : 'border-slate-200 bg-white'}`}>
              <p className={`text-[11px] font-bold uppercase tracking-[0.16em] ${lastSelectedCount === 0 ? 'text-amber-700' : 'text-slate-500'}`}>
                Lectura de la última pasada
              </p>
              <p className={`mt-2 text-sm leading-6 ${lastSelectedCount === 0 ? 'text-amber-800' : 'text-slate-600'}`}>
                {lastSelectedCount === 0
                  ? 'Una corrida en cero no siempre indica falla: muchas veces significa que esa tanda territorial ya estaba trabajada, duplicada, descartada o sin contacto útil.'
                  : 'La última corrida sí encontró plazas nuevas y las dejó listas para seguir el circuito comercial.'}
              </p>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

const CommercialAgentsPanel = ({
  agentControl = DEFAULT_COMMERCIAL_AGENT_CONTROL,
  queues = {},
  onToggleAgent,
  onApplyTargetPreset,
  onSaveResponseNotify,
  onRunDiscoveryPass,
  onRunLivePass,
  onRunContactPass,
  onRunFollowupPass,
  onRunPilotPass,
  discoveryRunBusy = false,
  liveRunBusy = false,
  contactRunBusy = false,
  followupRunBusy = false,
  pilotRunBusy = false,
  busyMap = {},
}) => {
  const [responseNotifyDraft, setResponseNotifyDraft] = useState(String(agentControl?.followup?.responseNotifyTo || '').trim());
  const lastBackendRunAt = getDateFromUnknown(agentControl?.lastBackendRadarRunAt);
  const lastBackendSummary = agentControl?.lastBackendRadarRunSummary || null;
  const targetProfile = resolveCommercialTargetProfile(agentControl?.targetProfile || {});
  const currentDiscovery = agentControl?.radar?.currentDiscovery || null;
  const lastDiscoverySummary = agentControl?.radar?.lastDiscoverySummary || null;
  const currentProcessing = agentControl?.radar?.currentProcessing || null;
  const discoveryRunning = currentDiscovery?.status === 'running';
  const processingRunning = currentProcessing?.status === 'running';
  const discoveryContextLabel = [currentDiscovery?.currentSegment, currentDiscovery?.currentCity].filter(Boolean).join(' · ');
  const discoveryProgressLabel = discoveryRunning
    ? `${Number(currentDiscovery?.queriesCompleted || 0) + 1}/${Number(currentDiscovery?.queriesTotal || 0)}`
    : '';
  const processingProgressLabel = processingRunning
    ? `${Number(currentProcessing?.processed || 0)}/${Number(currentProcessing?.queued || 0)}`
    : '';
  const lastDiscoveryCities = Array.isArray(lastDiscoverySummary?.selectedCityBreakdown)
    ? lastDiscoverySummary.selectedCityBreakdown
    : [];

  useEffect(() => {
    setResponseNotifyDraft(String(agentControl?.followup?.responseNotifyTo || '').trim());
  }, [agentControl?.followup?.responseNotifyTo]);

  return (
    <section className="rounded-[2rem] border border-slate-200 bg-white p-4 shadow-sm md:p-6">
      <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
        <div className="flex-1 space-y-4">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-cyan-700">Centro de agentes</p>
          <h2 className="mt-2 text-2xl font-black text-slate-900">Operación comercial asistida</h2>
          <p className="mt-2 max-w-3xl text-sm text-slate-600">
            Encendé o pausá cada agente, mirá qué cola tiene pendiente y seguí el circuito completo desde la búsqueda hasta el primer contacto.
          </p>
          <CommercialArgentinaCoverageMap
            targetCities={targetProfile?.cities || []}
            activeCity={currentDiscovery?.currentCity || ''}
            activeQuery={currentDiscovery?.currentQuery || ''}
            activeSegment={currentDiscovery?.currentSegment || ''}
            discoveryRunning={discoveryRunning}
            lastDiscoverySummary={lastDiscoverySummary}
          />
        </div>

        <div className="w-full xl:max-w-[380px] rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 shadow-sm">
          <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500">Lectura rápida</p>
          <p className="mt-2 text-sm font-semibold text-slate-900">
            {COMMERCIAL_AGENT_DEFINITIONS.filter((agent) => agentControl?.[agent.key]?.active !== false).length} agentes activos
          </p>
          <p className="mt-1 text-[11px] text-cyan-700">El circuito busca, valida, carga y envía. Acá solo necesitás disparar la búsqueda y revisar respuestas.</p>
          <button
            type="button"
            onClick={onRunDiscoveryPass}
            disabled={discoveryRunBusy || discoveryRunning}
            className={`mt-4 w-full rounded-2xl px-4 py-3 text-sm font-black uppercase tracking-[0.14em] transition ${
              discoveryRunBusy || discoveryRunning ? 'cursor-wait bg-cyan-100 text-cyan-500' : 'bg-cyan-600 text-white hover:bg-cyan-700'
            }`}
          >
            {discoveryRunBusy || discoveryRunning
              ? `Buscando ${discoveryContextLabel || 'empresas'} ${discoveryProgressLabel}`.trim()
              : 'Buscar y correr circuito'}
          </button>
          <button
            type="button"
            onClick={onRunLivePass}
            disabled={liveRunBusy || processingRunning}
            className={`mt-4 w-full rounded-2xl px-4 py-3 text-sm font-black uppercase tracking-[0.14em] transition ${
              liveRunBusy || processingRunning ? 'cursor-wait bg-slate-200 text-slate-500' : 'bg-slate-900 text-white hover:bg-slate-800'
            }`}
          >
            {liveRunBusy || processingRunning ? `Procesando radar ${processingProgressLabel}` : 'Procesar radar ahora'}
          </button>
          <button
            type="button"
            onClick={onRunFollowupPass}
            disabled={followupRunBusy}
            className={`mt-4 w-full rounded-2xl px-4 py-3 text-sm font-black uppercase tracking-[0.14em] transition ${
              followupRunBusy ? 'cursor-wait bg-fuchsia-100 text-fuchsia-500' : 'bg-fuchsia-600 text-white hover:bg-fuchsia-700'
            }`}
          >
            {followupRunBusy ? 'Revisando respuestas...' : 'Revisar respuestas'}
          </button>
          <details className="mt-4 rounded-2xl border border-slate-200 bg-white px-3 py-2">
            <summary className="cursor-pointer list-none text-[11px] font-bold uppercase tracking-[0.16em] text-slate-500">
              Controles avanzados
            </summary>
            <div className="mt-3 space-y-3">
              <button
                type="button"
                onClick={onRunContactPass}
                disabled={contactRunBusy}
                className={`w-full rounded-2xl px-4 py-3 text-sm font-black uppercase tracking-[0.14em] transition ${
                  contactRunBusy ? 'cursor-wait bg-indigo-100 text-indigo-500' : 'bg-indigo-600 text-white hover:bg-indigo-700'
                }`}
              >
                {contactRunBusy ? 'Procesando contacto...' : 'Procesar cola de contacto'}
              </button>
              <button
                type="button"
                onClick={onRunPilotPass}
                disabled={pilotRunBusy}
                className={`w-full rounded-2xl px-4 py-3 text-sm font-black uppercase tracking-[0.14em] transition ${
                  pilotRunBusy ? 'cursor-wait bg-slate-700 text-slate-300' : 'bg-slate-700 text-white hover:bg-slate-800'
                }`}
              >
                {pilotRunBusy ? 'Encolando piloto...' : 'Encolar piloto AR'}
              </button>
              <p className="text-[11px] text-slate-500">Estos controles quedan solo para pruebas o para destrabar el circuito manualmente.</p>
            </div>
          </details>
          {discoveryRunning ? (
            <div className="mt-3 rounded-2xl border border-cyan-200 bg-cyan-50 px-3 py-2">
              <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-cyan-700">Radar buscando</p>
              <p className="mt-1 text-sm font-semibold text-slate-900">{currentDiscovery?.currentQuery || 'Buscando empresas reales...'}</p>
              <p className="mt-1 text-[11px] text-cyan-700">
                {discoveryProgressLabel} consultas · {Number(currentDiscovery?.discovered || 0)} hallazgos útiles
              </p>
              {discoveryContextLabel ? (
                <p className="mt-1 text-[11px] text-slate-500">{discoveryContextLabel}</p>
              ) : null}
            </div>
          ) : null}
          {lastDiscoverySummary ? (
            <div className="mt-3 rounded-2xl border border-slate-200 bg-white px-3 py-2">
              <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-slate-500">Última cobertura del radar</p>
              <p className="mt-1 text-sm font-semibold text-slate-900">
                {lastDiscoverySummary.coveredCities || 0} ciudades cubiertas · {lastDiscoverySummary.selected || 0} hallazgos finales
              </p>
              <p className="mt-1 text-[11px] text-slate-500">
                {lastDiscoverySummary.queries || 0} consultas ejecutadas
              </p>
              <p className="mt-1 text-[11px] text-slate-500">
                {Number(lastDiscoverySummary.searchResults || 0)} resultados revisados · {Number(lastDiscoverySummary.skippedKnown || 0)} ya conocidos · {Number(lastDiscoverySummary.enrichmentFailures || 0)} webs sin respuesta
              </p>
              {lastDiscoveryCities.length ? (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {lastDiscoveryCities.slice(0, 8).map((item) => (
                    <span
                      key={`last-discovery-city-${item.city}`}
                      className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.12em] text-slate-600"
                    >
                      {item.city} · {item.count}
                    </span>
                  ))}
                </div>
              ) : null}
            </div>
          ) : null}
          {processingRunning ? (
            <div className="mt-3 rounded-2xl border border-slate-200 bg-white px-3 py-2">
              <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-slate-500">Validador trabajando</p>
              <p className="mt-1 text-sm font-semibold text-slate-900">{currentProcessing?.currentCompany || 'Procesando cola actual...'}</p>
              <p className="mt-1 text-[11px] text-slate-500">
                {processingProgressLabel} revisados · {Number(currentProcessing?.validated || 0)} listos
              </p>
            </div>
          ) : null}
          {lastBackendRunAt ? (
            <div className="mt-3 rounded-2xl border border-slate-200 bg-white px-3 py-2">
              <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-slate-500">Última corrida backend</p>
              <p className="mt-1 text-sm font-semibold text-slate-900">{formatLastActivity(lastBackendRunAt)}</p>
              {lastBackendSummary ? (
                <div className="mt-1 space-y-1 text-[11px] text-slate-500">
                  <p>
                    {lastBackendSummary.processed || 0} procesados · {lastBackendSummary.readyValidated || 0} listos · {lastBackendSummary.loaded || 0} derivados
                  </p>
                  <p>
                    {lastBackendSummary.createdRadar || 0} nuevos · {lastBackendSummary.duplicates || 0} repetidos · {lastBackendSummary.invalid || 0} inválidos
                  </p>
                  <p>
                    {lastBackendSummary.sent || 0} enviados · {lastBackendSummary.responded || 0} respuestas · {lastBackendSummary.secondTouches || 0} segundo contacto
                  </p>
                </div>
              ) : null}
            </div>
          ) : null}
          <div className="mt-3 rounded-2xl border border-slate-200 bg-white px-3 py-3">
            <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-slate-500">Aviso de respuestas</p>
            <input
              type="email"
              value={responseNotifyDraft}
              onChange={(event) => setResponseNotifyDraft(event.target.value)}
              placeholder="correo para avisos"
              className="mt-2 w-full rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-900 outline-none focus:border-cyan-300"
            />
            <button
              type="button"
              onClick={() => onSaveResponseNotify?.(responseNotifyDraft)}
              className="mt-3 w-full rounded-2xl border border-cyan-200 bg-cyan-50 px-4 py-2 text-xs font-black uppercase tracking-[0.14em] text-cyan-700 transition hover:bg-cyan-100"
            >
              Guardar correo de aviso
            </button>
          </div>
        </div>
      </div>

      <div className="mt-5 grid gap-4 xl:grid-cols-5">
        {COMMERCIAL_AGENT_DEFINITIONS.map((agent) => {
          const control = agentControl?.[agent.key] || DEFAULT_COMMERCIAL_AGENT_CONTROL[agent.key];
          const isActive = control?.active !== false;
          const queueValue = queues?.[agent.key] || 0;
          const busy = Boolean(busyMap?.[agent.key]);
          const updatedAt = getDateFromUnknown(control?.updatedAt);

          return (
            <div key={agent.key} className="rounded-3xl border border-slate-200 bg-slate-50 p-4 shadow-sm">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-slate-500">{agent.label}</p>
                  <p className="mt-2 text-2xl font-black text-slate-900">{queueValue}</p>
                  <p className="mt-1 text-[11px] uppercase tracking-[0.14em] text-slate-500">casos en cola</p>
                </div>
                <span className={`rounded-full border px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.14em] ${isActive ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-slate-200 bg-white text-slate-500'}`}>
                  {isActive ? 'Activo' : 'Pausado'}
                </span>
              </div>

              <p className="mt-3 text-sm text-slate-600">{agent.helper}</p>
              <p className="mt-3 text-[11px] text-slate-500">
                {updatedAt ? `Último cambio ${formatLastActivity(updatedAt)}` : 'Sin cambios registrados todavía'}
              </p>

              <button
                type="button"
                onClick={() => onToggleAgent?.(agent.key, !isActive)}
                disabled={busy}
                className={`mt-4 w-full rounded-2xl px-4 py-3 text-sm font-black uppercase tracking-[0.14em] transition ${
                  busy
                    ? 'cursor-wait bg-slate-200 text-slate-500'
                    : isActive
                      ? 'bg-slate-900 text-white hover:bg-slate-800'
                      : 'bg-cyan-600 text-white hover:bg-cyan-700'
                }`}
              >
                {busy ? 'Guardando...' : isActive ? 'Pausar agente' : 'Activar agente'}
              </button>
            </div>
          );
        })}
      </div>

      <div className="mt-5 grid gap-4 xl:grid-cols-[1.15fr_0.85fr]">
        <div className="rounded-3xl border border-slate-200 bg-slate-50 p-5 shadow-sm">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-cyan-700">Foco recomendado</p>
              <h3 className="mt-2 text-lg font-black text-slate-900">{targetProfile.label}</h3>
              <p className="mt-2 text-sm text-slate-600">{targetProfile.summary}</p>
              <p className="mt-3 text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">{targetProfile.slogan}</p>
            </div>
            <button
              type="button"
              onClick={() => onApplyTargetPreset?.(DEFAULT_COMMERCIAL_TARGET_PRESET_KEY)}
              className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-xs font-black uppercase tracking-[0.14em] text-slate-700 transition hover:border-cyan-200 hover:text-cyan-700"
            >
              Aplicar foco actual
            </button>
          </div>

          <div className="mt-4 grid gap-4 md:grid-cols-2">
            <div className="rounded-2xl border border-slate-200 bg-white p-4">
              <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-slate-500">Segmentos</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {targetProfile.segments.map((segment) => (
                  <span
                    key={segment}
                    className="rounded-full border border-cyan-200 bg-cyan-50 px-3 py-1 text-[11px] font-semibold text-cyan-700"
                  >
                    {segment}
                  </span>
                ))}
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-4">
              <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-slate-500">Ciudades</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {targetProfile.cities.map((city) => (
                  <span
                    key={city}
                    className="rounded-full border border-slate-200 bg-slate-100 px-3 py-1 text-[11px] font-semibold text-slate-700"
                  >
                    {city}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="rounded-3xl border border-slate-200 bg-slate-50 p-5 shadow-sm">
          <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500">Cadencia sugerida</p>
          <p className="mt-3 text-sm leading-7 text-slate-600">{targetProfile.cadence}</p>
          <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-4">
            <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-slate-500">Qué estamos evitando</p>
            <p className="mt-2 text-sm text-slate-600">
              Casos demasiado chicos, courier ultra informal o estructuras gigantes donde la entrada comercial suele ser más lenta y competitiva.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

const CommercialRadarPanel = ({
  radarItems = [],
  prospects = [],
  onUpdateRadarStatus,
  onPromoteRadarItem,
  onDeleteRadarItem,
  busyMap = {},
}) => {
  const visibleRadarItems = radarItems.filter((item) => !isRadarItemDerivedToProspects(item, prospects));
  const summary = {
    detected: visibleRadarItems.filter((item) => item.status === 'detected').length,
    validated: visibleRadarItems.filter((item) => item.status === 'validated').length,
    loaded: radarItems.filter((item) => isRadarItemDerivedToProspects(item, prospects)).length,
    discarded: visibleRadarItems.filter((item) => item.status === 'discarded').length,
  };

  return (
    <section className="rounded-[2rem] border border-slate-200 bg-white p-4 shadow-sm md:p-6">
      <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-cyan-700">Radar comercial</p>
          <h2 className="mt-2 text-2xl font-black text-slate-900">Empresas para revisar</h2>
          <p className="mt-2 max-w-3xl text-sm text-slate-600">
            Acá quedan solo los casos que piden criterio humano. Lo que viene sólido sigue solo hacia Prospectos y contacto.
          </p>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 shadow-sm">
          <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500">Lectura rápida</p>
          <div className="mt-3 flex flex-wrap gap-2">
            {RADAR_STATUSES.map((status) => (
              <RadarStagePill key={status.key} statusKey={status.key} />
            ))}
          </div>
          <p className="mt-3 text-[11px] text-cyan-700">Si aparece acá, es porque todavía conviene mirarlo antes de dejarlo avanzar.</p>
        </div>
      </div>

      <div className="mt-5 space-y-4">
        <div className="grid gap-3 md:grid-cols-4">
            {[
              { label: 'Radar', value: summary.detected },
              { label: 'Validados', value: summary.validated },
              { label: 'Derivados', value: summary.loaded },
              { label: 'Descartados', value: summary.discarded },
            ].map((item) => (
              <div key={item.label} className="rounded-3xl border border-slate-200 bg-slate-50 p-4 shadow-sm">
                <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-slate-500">{item.label}</p>
                <p className="mt-2 text-2xl font-black text-slate-900">{item.value}</p>
              </div>
            ))}
          </div>

          <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4 shadow-sm">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h3 className="text-sm font-black uppercase tracking-[0.18em] text-slate-900">Casos activos</h3>
                <p className="mt-1 text-sm text-slate-600">Empresas detectadas que todavía necesitan una mirada humana.</p>
              </div>
              <span className="rounded-full border border-slate-200 bg-white px-3 py-1 text-[11px] font-bold uppercase tracking-[0.14em] text-slate-600">
                {prospects.length} en Prospectos
              </span>
            </div>

            <div className="mt-4 space-y-3">
              {!visibleRadarItems.length ? (
                <div className="rounded-2xl border border-dashed border-slate-300 bg-white px-4 py-8 text-center">
                  <p className="text-sm font-bold text-slate-900">No hay empresas pendientes para revisar.</p>
                  <p className="mt-2 text-sm text-slate-500">Los casos sólidos ya avanzaron solos a Prospectos y esta bandeja queda limpia para nuevas detecciones.</p>
                </div>
              ) : (
                visibleRadarItems.map((item) => {
                  const itemBusy = Boolean(busyMap?.[item.id]);
                  const statusMeta = getRadarStatusMeta(item.status);
                  const channels = resolveCommercialChannels(item);
                  return (
                    <div key={item.id} className="rounded-2xl border border-slate-200 bg-white p-4">
                      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                        <div className="min-w-0">
                          <p className="truncate text-sm font-bold text-slate-900">{item.companyName || 'Empresa sin nombre'}</p>
                          <p className="mt-1 text-xs text-slate-500">
                            {(item.segment || 'Sin perfil')}{item.city ? ` · ${item.city}` : ''}{item.priority ? ` · Prioridad ${item.priority}` : ''}
                          </p>
                          <p className="mt-2 text-xs text-slate-600">
                            {item.source || 'Origen no definido'}{item.contactValue ? ` · ${item.contactValue}` : ''}
                          </p>
                          <div className="mt-2 flex flex-wrap items-center gap-2">
                            {channels.website ? (
                              <a
                                href={channels.website}
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex rounded-full border border-cyan-200 bg-cyan-50 px-3 py-1 text-[11px] font-semibold text-cyan-700 hover:bg-cyan-100"
                              >
                                Sitio web
                              </a>
                            ) : null}
                            {channels.email && !channels.email.endsWith('.example') ? (
                              <a
                                href={`mailto:${channels.email}`}
                                className="inline-flex rounded-full border border-indigo-200 bg-indigo-50 px-3 py-1 text-[11px] font-semibold text-indigo-700 hover:bg-indigo-100"
                              >
                                Mail
                              </a>
                            ) : null}
                            {channels.whatsapp ? (
                              <a
                                href={buildWhatsAppUrl(channels.whatsapp)}
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-[11px] font-semibold text-emerald-700 hover:bg-emerald-100"
                              >
                                WhatsApp
                              </a>
                            ) : null}
                            {item.sourceUrl ? (
                              <a
                                href={normalizeWebUrl(item.sourceUrl)}
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex rounded-full border border-slate-200 bg-slate-100 px-3 py-1 text-[11px] font-semibold text-slate-700 hover:bg-slate-200"
                              >
                                Fuente
                              </a>
                            ) : null}
                          </div>
                        </div>

                        <div className="flex flex-wrap items-center gap-2">
                          <RadarStagePill statusKey={item.status} />
                      {statusMeta.key === 'detected' ? (
                            <RadarActionButton onClick={() => onUpdateRadarStatus?.(item, 'validated')} disabled={itemBusy} tone="cyan">
                              Validar ahora
                            </RadarActionButton>
                          ) : null}
                          {statusMeta.key === 'validated' ? (
                            <RadarActionButton onClick={() => onPromoteRadarItem?.(item)} disabled={itemBusy} tone="indigo">
                              Forzar carga ahora
                            </RadarActionButton>
                          ) : null}
                          {statusMeta.key !== 'discarded' && statusMeta.key !== 'loaded' ? (
                            <RadarActionButton onClick={() => onUpdateRadarStatus?.(item, 'discarded')} disabled={itemBusy} tone="rose">
                              Descartar
                            </RadarActionButton>
                          ) : (
                            <RadarActionButton onClick={() => onUpdateRadarStatus?.(item, 'detected')} disabled={itemBusy} tone="slate">
                              Reactivar
                            </RadarActionButton>
                          )}
                          <RadarActionButton onClick={() => onDeleteRadarItem?.(item)} disabled={itemBusy} tone="rose">
                            Eliminar
                          </RadarActionButton>
                        </div>
                      </div>

                      {statusMeta.key === 'loaded' ? (
                        <p className="mt-3 text-sm text-indigo-700">El caso ya fue cargado por el sistema y quedó dentro del circuito comercial.</p>
                      ) : null}
                      {item.recommendedAction ? (
                        <p className="mt-3 text-sm text-cyan-700">Siguiente paso sugerido: {item.recommendedAction}</p>
                      ) : null}
                      {item.notes ? (
                        <p className="mt-2 text-sm text-slate-500">{item.notes}</p>
                      ) : null}
                    </div>
                  );
                })
              )}
            </div>
          </div>
      </div>
    </section>
  );
};

const CommercialProspectsPanel = ({
  prospects = [],
  onAdvanceProspect,
  onCopyTemplate,
  onCopyManualMessage,
  onMarkResponseSeen,
  onSendProspect,
  onDeleteProspect,
  busyMap = {},
}) => {
  const statusSections = [
    {
      key: 'pending_review',
      title: 'Pendientes de revisión',
      helper: 'Casos sin mail directo o con datos incompletos. Requieren criterio humano.',
      tone: 'border-slate-200 bg-slate-50',
    },
    {
      key: 'queued_to_send',
      title: 'En cola de contacto',
      helper: 'Ya están listos para que el agente de contacto envíe el primer mail o para intervenir manualmente.',
      tone: 'border-cyan-500/20 bg-cyan-500/10',
    },
    {
      key: 'sent',
      title: 'Esperando respuesta',
      helper: 'Mensajes enviados por mail o por contacto manual. Quedan ordenados por el movimiento más reciente.',
      tone: 'border-indigo-500/20 bg-indigo-500/10',
    },
    {
      key: 'responded',
      title: 'Respondieron',
      helper: 'Empresas que contestaron y necesitan una acción comercial concreta.',
      tone: 'border-fuchsia-500/20 bg-fuchsia-500/10',
    },
    {
      key: 'demo',
      title: 'Demos agendadas',
      helper: 'Casos con reunión o presentación ya encaminada.',
      tone: 'border-violet-500/20 bg-violet-500/10',
    },
    {
      key: 'trial',
      title: 'Pruebas activas',
      helper: 'Empresas usando la plataforma en etapa de evaluación.',
      tone: 'border-emerald-500/20 bg-emerald-500/10',
    },
    {
      key: 'converted',
      title: 'Clientes activos',
      helper: 'Casos convertidos que ya forman parte de la operación.',
      tone: 'border-amber-500/20 bg-amber-500/10',
    },
    {
      key: 'archived',
      title: 'Archivados',
      helper: 'Casos cerrados sin avance o guardados para retomar más adelante.',
      tone: 'border-slate-200 bg-slate-50',
    },
  ];

  const summary = {
    total: prospects.length,
    pendingReview: prospects.filter((item) => getProspectStatusMeta(item.status).key === 'pending_review').length,
    readyToSend: prospects.filter((item) => getProspectStatusMeta(item.status).key === 'queued_to_send').length,
    sent: prospects.filter((item) => ['sent', 'manual_sent'].includes(getProspectStatusMeta(item.status).key)).length,
    demos: prospects.filter((item) => item.status === 'demo').length,
    converted: prospects.filter((item) => item.status === 'converted').length,
  };
  const groupedProspects = statusSections.map((section) => ({
    ...section,
    items: prospects
      .filter((item) => {
        const itemStatus = getProspectStatusMeta(item.status).key;
        return section.key === 'sent'
          ? ['sent', 'manual_sent'].includes(itemStatus)
          : itemStatus === section.key;
      })
      .sort((a, b) => {
        const aTime = getProspectActivityDate(a)?.getTime?.() || 0;
        const bTime = getProspectActivityDate(b)?.getTime?.() || 0;
        return bTime - aTime;
      }),
  }));

  return (
    <section className="rounded-[2rem] border border-slate-200 bg-white p-4 shadow-sm md:p-6">
      <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-cyan-700">Prospectos</p>
          <h2 className="mt-2 text-2xl font-black text-slate-900">Revisión final y salida comercial</h2>
          <p className="mt-2 max-w-3xl text-sm text-slate-600">
            Los agentes ya hicieron búsqueda, validación y carga. Acá seguís la salida comercial, controlás qué quedó listo para envío automático y resolvés solo los casos que necesitan criterio humano.
          </p>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 shadow-sm">
          <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500">Asesoramiento por WhatsApp</p>
          <p className="mt-2 text-sm font-semibold text-slate-900">{COMMERCIAL_WHATSAPP}</p>
          <p className="mt-2 text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500">Correo comercial</p>
          <p className="mt-2 text-sm font-semibold text-slate-900">{COMMERCIAL_EMAIL}</p>
          <p className="mt-1 text-[11px] text-cyan-700">Consultas, demostraciones y acompañamiento inicial.</p>
        </div>
      </div>

      <div className="mt-5 rounded-3xl border border-cyan-200 bg-cyan-50 p-5 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="max-w-4xl">
            <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-cyan-700">Presentación comercial</p>
            <h3 className="mt-2 text-xl font-black text-slate-900">Mensaje base para mostrar G-CENTRA</h3>
            <p className="mt-3 text-sm leading-7 text-slate-700">{COMMERCIAL_PRESENTATION_MESSAGE}</p>
          </div>

          <div className="flex flex-col gap-2">
            <button
              type="button"
              onClick={() => onCopyTemplate?.('whatsapp')}
              className="rounded-2xl border border-cyan-200 bg-white px-4 py-3 text-sm font-black uppercase tracking-[0.14em] text-cyan-700 transition hover:bg-cyan-100"
            >
              Copiar WhatsApp
            </button>
            <button
              type="button"
              onClick={() => onCopyTemplate?.('email')}
              className="rounded-2xl border border-cyan-200 bg-white px-4 py-3 text-sm font-black uppercase tracking-[0.14em] text-cyan-700 transition hover:bg-cyan-100"
            >
              Copiar mail
            </button>
          </div>
        </div>
      </div>

      <div className="mt-5 grid gap-4 xl:grid-cols-[0.82fr_1.18fr]">
        <div className="space-y-4">
          <div className="rounded-3xl border border-slate-200 bg-slate-50 p-5 shadow-sm">
            <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500">Secuencia comercial</p>
            <div className="mt-4 space-y-3">
              {[
                'Pendiente de revisión: el caso necesita criterio humano porque le falta mail directo o quedó incompleto.',
                'En cola de contacto: el sistema encontró mail directo y lo dejó listo para envío automático o intervención manual.',
                'Enviado: ya salió el mensaje y la empresa queda a la espera de respuesta.',
                'Seguimiento: si responde, avanza a demo, prueba y conversión; si no, se archiva.',
              ].map((step, index) => (
                <div key={step} className="flex items-start gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3">
                  <span className="mt-0.5 inline-flex h-7 w-7 items-center justify-center rounded-full border border-cyan-200 bg-cyan-50 text-xs font-black text-cyan-700">
                    {index + 1}
                  </span>
                  <p className="text-sm text-slate-700">{step}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-3xl border border-slate-200 bg-slate-50 p-5 shadow-sm">
            <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500">Lectura operativa</p>
            <p className="mt-3 text-sm leading-7 text-slate-600">
              Prospectos ya no es un sector de carga manual. Se comporta como la bandeja donde aterrizan los casos derivados por agentes, listos para que salga el primer contacto y para seguir la conversación después.
            </p>
          </div>
        </div>

        <div className="space-y-4">
          <div className="grid gap-3 md:grid-cols-4">
            {[
              { label: 'Revisión', value: summary.pendingReview },
              { label: 'Listos', value: summary.readyToSend },
              { label: 'Enviados', value: summary.sent },
              { label: 'Convertidos', value: summary.converted },
            ].map((item) => (
              <div key={item.label} className="rounded-3xl border border-slate-200 bg-slate-50 p-4 shadow-sm">
                <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-slate-500">{item.label}</p>
                <p className="mt-2 text-2xl font-black text-slate-900">{item.value}</p>
              </div>
            ))}
          </div>

          <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4 shadow-sm">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h3 className="text-sm font-black uppercase tracking-[0.18em] text-slate-900">Bloques comerciales</h3>
                <p className="mt-1 text-sm text-slate-600">Cada bloque reúne un estado distinto y se ordena por el movimiento más reciente.</p>
              </div>
            </div>

            <div className="mt-4 space-y-3">
              {!prospects.length ? (
                <div className="rounded-2xl border border-dashed border-slate-300 bg-white px-4 py-8 text-center">
                  <p className="text-sm font-bold text-slate-900">Todavía no hay prospectos cargados por el flujo automático.</p>
                  <p className="mt-2 text-sm text-slate-500">Cuando Radar y Validación terminen un caso, va a aparecer acá listo para seguimiento comercial.</p>
                </div>
              ) : (
                groupedProspects.map((section) => (
                  <div key={section.key} className={`rounded-3xl border p-4 shadow-inner ${section.tone}`}>
                    <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
                      <div>
                        <h4 className="text-sm font-black uppercase tracking-[0.18em] text-slate-900">{section.title}</h4>
                        <p className="mt-1 text-sm text-slate-600">{section.helper}</p>
                      </div>
                      <span className="rounded-full border border-slate-200 bg-white px-3 py-1 text-[11px] font-bold uppercase tracking-[0.14em] text-slate-600">
                        {section.items.length} caso{section.items.length === 1 ? '' : 's'}
                      </span>
                    </div>

                    <div className="mt-4 space-y-3">
                      {!section.items.length ? (
                        <div className="rounded-2xl border border-dashed border-slate-300 bg-white px-4 py-5 text-sm text-slate-500">
                          Todavía no hay casos en este bloque.
                        </div>
                      ) : (
                        section.items.map((prospect) => {
                          const stageTimestamp = getProspectStageTimestamp(prospect, section.key);
                          const statusKey = getProspectStatusMeta(prospect.status).key;
                          const channels = resolveCommercialChannels(prospect);
                          const hasManualChannel =
                            Boolean(channels.website) ||
                            Boolean(channels.whatsapp) ||
                            (!channels.email && Boolean(String(prospect.contactValue || prospect.sourceUrl || '').trim()));
                          const showManualMessage = statusKey === 'pending_review' && !channels.email && hasManualChannel;
                          const manualMessage = showManualMessage ? buildCommercialProspectManualMessage(prospect) : '';
                          const manualMessageLabel = resolveCommercialProspectManualChannelLabel(channels);
                          return (
                            <div
                              key={prospect.id}
                              className="rounded-2xl border border-slate-200 bg-white p-4"
                            >
                              <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                                <div className="min-w-0">
                                  <p className="truncate text-sm font-bold text-slate-900">{prospect.companyName || 'Prospecto sin nombre'}</p>
                                  <p className="mt-1 text-xs text-slate-500">
                                    {prospect.segment || 'Sin perfil'}{prospect.city ? ` · ${prospect.city}` : ''}
                                  </p>
                                  <p className="mt-2 text-xs text-slate-600">
                                    {prospect.contactName || 'Sin contacto'}{prospect.contactValue ? ` · ${prospect.contactChannel || 'Contacto'}: ${prospect.contactValue}` : ''}
                                  </p>
                                  <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px] text-slate-500">
                                    <span>Movimiento: {formatLastActivity(stageTimestamp)}</span>
                                    {prospect.sourceCountry ? <span>· {prospect.sourceCountry}</span> : null}
                                  </div>
                                  <div className="mt-2 flex flex-wrap items-center gap-2">
                                    {channels.website ? (
                                      <a
                                        href={channels.website}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="inline-flex rounded-full border border-cyan-200 bg-cyan-50 px-3 py-1 text-[11px] font-semibold text-cyan-700 hover:bg-cyan-100"
                                      >
                                        Sitio web
                                      </a>
                                    ) : null}
                                    {channels.email && !channels.email.endsWith('.example') ? (
                                      <a
                                        href={`mailto:${channels.email}`}
                                        className="inline-flex rounded-full border border-indigo-200 bg-indigo-50 px-3 py-1 text-[11px] font-semibold text-indigo-700 hover:bg-indigo-100"
                                      >
                                        Mail
                                      </a>
                                    ) : null}
                                    {channels.whatsapp ? (
                                      <a
                                        href={buildWhatsAppUrl(channels.whatsapp)}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="inline-flex rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-[11px] font-semibold text-emerald-700 hover:bg-emerald-100"
                                      >
                                        WhatsApp
                                      </a>
                                    ) : null}
                                    {prospect.sourceUrl ? (
                                      <a
                                        href={normalizeWebUrl(prospect.sourceUrl)}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="inline-flex rounded-full border border-slate-200 bg-slate-100 px-3 py-1 text-[11px] font-semibold text-slate-700 hover:bg-slate-200"
                                      >
                                        Fuente
                                      </a>
                                    ) : null}
                                  </div>
                                </div>

                                <div className="flex flex-wrap items-center gap-2">
                                  {prospect.unreadResponse ? (
                                    <span className="rounded-full border border-fuchsia-200 bg-fuchsia-50 px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.14em] text-fuchsia-700">
                                      Respuesta nueva
                                    </span>
                                  ) : null}
                                  <ProspectStagePill statusKey={prospect.status} />
                                  <ProspectStageActions
                                    prospect={prospect}
                                    onAdvance={onAdvanceProspect}
                                    busy={Boolean(busyMap?.[prospect.id])}
                                  />
                                </div>
                              </div>

                              <ProspectContactActions
                                prospect={prospect}
                                onSend={onSendProspect}
                                busy={Boolean(busyMap?.[prospect.id])}
                              />
                              <div className="mt-3 flex flex-wrap items-center gap-2">
                                {prospect.unreadResponse ? (
                                  <RadarActionButton onClick={() => onMarkResponseSeen?.(prospect)} disabled={Boolean(busyMap?.[prospect.id])} tone="fuchsia">
                                    Marcar visto
                                  </RadarActionButton>
                                ) : null}
                                <RadarActionButton onClick={() => onDeleteProspect?.(prospect)} disabled={Boolean(busyMap?.[prospect.id])} tone="rose">
                                  Eliminar
                                </RadarActionButton>
                              </div>

                              {prospect.nextStep ? (
                                <p className="mt-3 text-sm text-cyan-700">Próximo paso: {prospect.nextStep}</p>
                              ) : null}
                              {statusKey === 'pending_review' ? (
                                <p className="mt-2 text-sm text-slate-600">Caso derivado a revisión manual porque le falta mail directo o necesita criterio comercial.</p>
                              ) : null}
                              {showManualMessage ? (
                                <div className="mt-3 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-4">
                                  <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                                    <div className="min-w-0">
                                      <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-emerald-700">{manualMessageLabel}</p>
                                      <p className="mt-2 whitespace-pre-line text-sm leading-6 text-slate-700">{manualMessage}</p>
                                      <div className="mt-3 flex flex-wrap gap-2">
                                        {channels.website ? (
                                          <a
                                            href={channels.website}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="inline-flex rounded-full border border-cyan-200 bg-white px-3 py-1 text-[11px] font-semibold text-cyan-700 hover:bg-cyan-50"
                                          >
                                            Abrir sitio
                                          </a>
                                        ) : null}
                                        {channels.whatsapp ? (
                                          <a
                                            href={buildProspectWhatsAppLink(prospect)}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="inline-flex rounded-full border border-emerald-200 bg-white px-3 py-1 text-[11px] font-semibold text-emerald-700 hover:bg-emerald-50"
                                          >
                                            Abrir WhatsApp listo
                                          </a>
                                        ) : null}
                                      </div>
                                    </div>
                                    <div className="flex shrink-0 flex-wrap gap-2">
                                      <RadarActionButton
                                        onClick={() => onCopyManualMessage?.(prospect)}
                                        disabled={Boolean(busyMap?.[prospect.id])}
                                        tone="cyan"
                                      >
                                        Copiar mensaje
                                      </RadarActionButton>
                                      <RadarActionButton
                                        onClick={() => onAdvanceProspect?.(prospect, 'manual_sent')}
                                        disabled={Boolean(busyMap?.[prospect.id])}
                                        tone="indigo"
                                      >
                                        Ya envié mensaje
                                      </RadarActionButton>
                                    </div>
                                  </div>
                                </div>
                              ) : null}
                              {statusKey === 'queued_to_send' ? (
                                <p className="mt-2 text-sm text-slate-600">Caso listo para contacto automático. Si querés, también podés intervenir manualmente desde acá.</p>
                              ) : null}
                              {['sent', 'manual_sent'].includes(statusKey) ? (
                                <p className="mt-2 text-sm text-slate-600">
                                  {statusKey === 'manual_sent'
                                    ? 'Contacto manual registrado. Ahora toca esperar respuesta o archivar si no avanza.'
                                    : 'Mensaje enviado. Ahora toca esperar respuesta o archivar si no avanza.'}
                                </p>
                              ) : null}
                              {prospect.lastOutreach?.at ? (
                                <p className="mt-2 text-sm text-slate-500">
                                  Último contacto: {prospect.lastOutreach?.channel === 'whatsapp' ? 'WhatsApp' : 'Mail'} · {formatLastActivity(prospect.lastOutreach.at)}
                                </p>
                              ) : null}
                              {prospect.lastInbound?.receivedAt || prospect.lastInbound?.detectedAt ? (
                                <div className="mt-3 rounded-2xl border border-fuchsia-200 bg-fuchsia-50 px-3 py-3">
                                  <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-fuchsia-700">Última respuesta detectada</p>
                                  <p className="mt-1 text-sm font-semibold text-slate-900">
                                    {prospect.lastInbound?.fromName || prospect.lastInbound?.fromEmail || 'Contacto sin nombre'}
                                  </p>
                                  <p className="mt-1 text-xs text-slate-600">
                                    {prospect.lastInbound?.subject || 'Sin asunto'} · {formatLastActivity(prospect.lastInbound?.receivedAt || prospect.lastInbound?.detectedAt)}
                                  </p>
                                  {prospect.lastInbound?.summary ? (
                                    <p className="mt-2 text-sm text-slate-600">{prospect.lastInbound.summary}</p>
                                  ) : null}
                                  {prospect.lastInbound?.link ? (
                                    <a
                                      href={prospect.lastInbound.link}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="mt-2 inline-flex rounded-full border border-fuchsia-200 bg-white px-3 py-1 text-[11px] font-semibold text-fuchsia-700 hover:bg-fuchsia-100"
                                    >
                                      Abrir correo
                                    </a>
                                  ) : null}
                                </div>
                              ) : null}
                              {prospect.notes ? (
                                <p className="mt-2 text-sm text-slate-500">{prospect.notes}</p>
                              ) : null}
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const formatBillingRequestTimestamp = (value) => {
  const date = getDateFromUnknown(value);
  if (!date) return 'Sin fecha';
  return date.toLocaleString('es-AR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const ArchitectStats = ({ stats }) => {
  const cards = [
    {
      key: 'totalCompanies',
      label: 'Empresas activas',
      value: stats.totalCompanies,
      icon: 'M3.75 21h16.5M4.5 18.75h15M6.75 18.75V9.75m4.5 9V6.75m4.5 12V12',
    },
    {
      key: 'activityToday',
      label: 'Actividad hoy',
      value: stats.activityToday,
      icon: 'M12 6v6l4 2.25M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
    },
    {
      key: 'activeRoutes',
      label: 'Con rutas activas',
      value: stats.activeRoutes,
      icon: 'M8.25 18.75a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 01-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h1.125',
    },
    {
      key: 'alerts',
      label: 'A revisar',
      value: stats.alerts,
      icon: 'M12 9v3.75m9.303 3.376c.866 1.5-.217 3.374-1.948 3.374H4.645c-1.73 0-2.813-1.874-1.948-3.374L10.051 3.378c.866-1.5 3.032-1.5 3.898 0l7.354 12.748zM12 15.75h.007v.008H12v-.008z',
    },
  ];

  return (
    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
      {cards.map((card) => (
        <div key={card.key} className="rounded-3xl border border-slate-300 bg-slate-950 p-5 shadow-xl">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-sm font-semibold text-slate-300">{card.label}</p>
              <p className="mt-2 text-3xl font-black text-white">{card.value}</p>
            </div>
            <div className="rounded-2xl bg-cyan-600 p-3 text-white shadow-lg">
              <Icon path={card.icon} className="w-5 h-5" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

const formatTrafficNumber = (value = 0) => new Intl.NumberFormat('es-AR').format(Number(value || 0));

const sumTrafficMetric = (daily = [], field) =>
  daily.reduce((acc, item) => acc + Number(item?.[field] || 0), 0);

const sumTrafficNestedMetric = (daily = [], field, key) =>
  daily.reduce((acc, item) => acc + Number(item?.[field]?.[key] || 0), 0);

const buildTrafficTopEntries = (map = {}, labels = {}) =>
  Object.entries(map || {})
    .map(([key, value]) => ({
      key,
      label: labels?.[key] || key,
      value: Number(value || 0),
    }))
    .filter((item) => item.value > 0)
    .sort((a, b) => b.value - a.value);

const PUBLIC_TRAFFIC_PAGE_LABELS = {
  landing_home: 'Inicio',
  landing_plans: 'Planes',
  trial_signup: 'Probar gratis',
  commercial_request: 'Solicitud comercial',
  landing_other: 'Otra pública',
};

const PUBLIC_TRAFFIC_SOURCE_LABELS = {
  direct: 'Directo',
  instagram: 'Instagram',
  facebook: 'Facebook',
  whatsapp: 'WhatsApp',
  google: 'Google',
  bing: 'Bing',
  linkedin: 'LinkedIn',
  email: 'Correo',
  referral: 'Referidos',
};

const TrafficMetricCard = ({ label, value, helper }) => (
  <div className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm">
    <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500">{label}</p>
    <p className="mt-2 text-3xl font-black text-slate-900">{value}</p>
    <p className="mt-1 text-sm text-slate-600">{helper}</p>
  </div>
);

const PublicTrafficPanel = ({ summary, daily = [], loading = false }) => {
  const today = daily[0] || null;
  const visits7d = sumTrafficMetric(daily.slice(0, 7), 'totalViews');
  const sessions7d = sumTrafficMetric(daily.slice(0, 7), 'sessions');
  const trial7d = sumTrafficNestedMetric(daily.slice(0, 7), 'interest', 'trial_signup') + sumTrafficNestedMetric(daily.slice(0, 7), 'interest', 'trial');
  const plans7d = sumTrafficNestedMetric(daily.slice(0, 7), 'interest', 'plans');
  const request7d = sumTrafficNestedMetric(daily.slice(0, 7), 'interest', 'commercial_request');
  const topPages = buildTrafficTopEntries(summary?.pageViews, {
    ...PUBLIC_TRAFFIC_PAGE_LABELS,
    ...(summary?.pageLabels || {}),
  }).slice(0, 5);
  const topSources = buildTrafficTopEntries(summary?.sources, PUBLIC_TRAFFIC_SOURCE_LABELS).slice(0, 5);
  const lastVisitLabel = formatLastActivity(summary?.lastVisitAt);

  return (
    <section className="rounded-[2rem] border border-slate-200 bg-white p-4 shadow-sm md:p-6">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-cyan-700">Visitas y tráfico</p>
          <h2 className="mt-2 text-2xl font-black text-slate-900">Qué está pasando en la web pública</h2>
          <p className="mt-2 max-w-3xl text-sm text-slate-600">
            Esta vista muestra si la gente está llegando a la landing, si revisa planes y si avanza hacia prueba gratis o solicitud comercial.
          </p>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
          <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500">Última señal</p>
          <p className="mt-2 text-sm font-semibold text-slate-900">{lastVisitLabel || 'Sin señales todavía'}</p>
        </div>
      </div>

      <div className="mt-5 grid gap-4 md:grid-cols-2 xl:grid-cols-5">
        <TrafficMetricCard
          label="Visitas hoy"
          value={formatTrafficNumber(today?.totalViews || 0)}
          helper={today?.dateKey ? `Página pública el ${today.dateKey}` : 'Todavía sin visitas registradas'}
        />
        <TrafficMetricCard
          label="Visitas 7 días"
          value={formatTrafficNumber(visits7d)}
          helper={`${formatTrafficNumber(sessions7d)} sesiones públicas recientes`}
        />
        <TrafficMetricCard
          label="Planes"
          value={formatTrafficNumber(plans7d)}
          helper="Visitas recientes a la página de planes"
        />
        <TrafficMetricCard
          label="Prueba gratis"
          value={formatTrafficNumber(trial7d)}
          helper="Entradas recientes al alta trial"
        />
        <TrafficMetricCard
          label="Solicitud comercial"
          value={formatTrafficNumber(request7d)}
          helper="Interés reciente en demo o plan"
        />
      </div>

      <div className="mt-6 grid gap-4 xl:grid-cols-[1.2fr_1fr_1fr]">
        <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500">Resumen diario</p>
              <h3 className="mt-2 text-lg font-black text-slate-900">Últimos días</h3>
            </div>
            <span className="rounded-full border border-slate-200 bg-white px-3 py-1 text-[11px] font-bold uppercase tracking-[0.14em] text-slate-600">
              {daily.length} días
            </span>
          </div>

          <div className="mt-4 space-y-3">
            {loading ? (
              <div className="rounded-2xl border border-slate-200 bg-white px-4 py-5 text-sm text-slate-500">Cargando tráfico...</div>
            ) : daily.length ? (
              daily.slice(0, 10).map((item) => (
                <div key={item.id || item.dateKey} className="rounded-2xl border border-slate-200 bg-white px-4 py-3">
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-sm font-black text-slate-900">{item.dateKey}</p>
                    <p className="text-sm font-semibold text-slate-700">{formatTrafficNumber(item.totalViews || 0)} visitas</p>
                  </div>
                  <p className="mt-1 text-xs text-slate-500">
                    {formatTrafficNumber(item.sessions || 0)} sesiones · {formatTrafficNumber(item.interest?.trial_signup || item.interest?.trial || 0)} trial · {formatTrafficNumber(item.interest?.plans || 0)} planes
                  </p>
                </div>
              ))
            ) : (
              <div className="rounded-2xl border border-dashed border-slate-200 bg-white px-4 py-6 text-sm text-slate-500">
                Todavía no hay visitas públicas registradas para mostrar.
              </div>
            )}
          </div>
        </div>

        <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4">
          <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500">Páginas más vistas</p>
          <div className="mt-4 space-y-3">
            {topPages.length ? (
              topPages.map((item) => (
                <div key={item.key} className="rounded-2xl border border-slate-200 bg-white px-4 py-3">
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-sm font-semibold text-slate-900">{item.label}</p>
                    <p className="text-sm font-black text-slate-900">{formatTrafficNumber(item.value)}</p>
                  </div>
                </div>
              ))
            ) : (
              <div className="rounded-2xl border border-dashed border-slate-200 bg-white px-4 py-6 text-sm text-slate-500">
                Sin páginas registradas todavía.
              </div>
            )}
          </div>
        </div>

        <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4">
          <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500">Origen principal</p>
          <div className="mt-4 space-y-3">
            {topSources.length ? (
              topSources.map((item) => (
                <div key={item.key} className="rounded-2xl border border-slate-200 bg-white px-4 py-3">
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-sm font-semibold text-slate-900">{item.label}</p>
                    <p className="text-sm font-black text-slate-900">{formatTrafficNumber(item.value)}</p>
                  </div>
                </div>
              ))
            ) : (
              <div className="rounded-2xl border border-dashed border-slate-200 bg-white px-4 py-6 text-sm text-slate-500">
                Aún no hay origen de tráfico visible.
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

const COMMERCIAL_LEAD_STATUS_META = {
  new: { label: 'Nueva', tone: 'border-cyan-200 bg-cyan-50 text-cyan-700' },
  reviewing: { label: 'En revisión', tone: 'border-amber-200 bg-amber-50 text-amber-700' },
  contacted: { label: 'Contactada', tone: 'border-emerald-200 bg-emerald-50 text-emerald-700' },
  archived: { label: 'Archivada', tone: 'border-slate-200 bg-slate-100 text-slate-600' },
};

const CommercialRequestsPanel = ({ requests = [], onUpdateStatus, busyMap = {} }) => {
  const activeRequests = requests.filter((request) => String(request.status || 'new').toLowerCase() !== 'archived');
  const archivedRequests = requests.filter((request) => String(request.status || '').toLowerCase() === 'archived');

  const renderRequest = (request) => {
    const statusKey = String(request.status || 'new').toLowerCase();
    const statusMeta = COMMERCIAL_LEAD_STATUS_META[statusKey] || COMMERCIAL_LEAD_STATUS_META.new;
    const busy = Boolean(busyMap[request.id]);

    return (
      <article key={request.id} className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="text-lg font-black text-slate-950">{request.companyName || 'Empresa sin nombre'}</h3>
              <span className={`rounded-full border px-3 py-1 text-[10px] font-black uppercase tracking-[0.14em] ${statusMeta.tone}`}>
                {statusMeta.label}
              </span>
            </div>
            <p className="mt-2 text-sm text-slate-600">
              {request.interest || request.requestedPlanTitle || 'Plan sin especificar'} · {formatBillingRequestTimestamp(request.createdAt)}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {request.email ? (
              <a href={`mailto:${request.email}`} className="rounded-full border border-cyan-200 bg-cyan-50 px-3 py-2 text-xs font-bold text-cyan-700">
                Enviar correo
              </a>
            ) : null}
            {request.phone ? (
              <a href={`tel:${request.phone}`} className="rounded-full border border-slate-200 bg-slate-50 px-3 py-2 text-xs font-bold text-slate-700">
                Llamar
              </a>
            ) : null}
          </div>
        </div>

        <div className="mt-5 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
            <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500">Responsable</p>
            <p className="mt-2 text-sm font-semibold text-slate-900">{request.contactName || 'Sin responsable'}</p>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
            <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500">Correo</p>
            <p className="mt-2 break-all text-sm font-semibold text-slate-900">{request.email || 'Sin correo'}</p>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
            <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500">Teléfono</p>
            <p className="mt-2 text-sm font-semibold text-slate-900">{request.phone || 'Sin teléfono'}</p>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
            <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500">Operación</p>
            <p className="mt-2 text-sm font-semibold text-slate-900">{request.operationSize || 'Sin detalle'}</p>
          </div>
        </div>

        {request.message ? (
          <div className="mt-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm leading-6 text-slate-700">
            {request.message}
          </div>
        ) : null}

        <div className="mt-4 flex flex-wrap gap-2">
          {statusKey !== 'reviewing' ? (
            <button type="button" disabled={busy} onClick={() => onUpdateStatus(request, 'reviewing')} className="rounded-full border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-bold text-amber-700 disabled:opacity-50">
              Poner en revisión
            </button>
          ) : null}
          {statusKey !== 'contacted' ? (
            <button type="button" disabled={busy} onClick={() => onUpdateStatus(request, 'contacted')} className="rounded-full border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs font-bold text-emerald-700 disabled:opacity-50">
              Marcar contactada
            </button>
          ) : null}
          {statusKey !== 'archived' ? (
            <button type="button" disabled={busy} onClick={() => onUpdateStatus(request, 'archived')} className="rounded-full border border-slate-200 bg-white px-3 py-2 text-xs font-bold text-slate-600 disabled:opacity-50">
              Archivar
            </button>
          ) : (
            <button type="button" disabled={busy} onClick={() => onUpdateStatus(request, 'new')} className="rounded-full border border-cyan-200 bg-cyan-50 px-3 py-2 text-xs font-bold text-cyan-700 disabled:opacity-50">
              Reabrir
            </button>
          )}
        </div>
      </article>
    );
  };

  return (
    <section className="rounded-[2rem] border border-slate-200 bg-slate-50 p-4 shadow-sm md:p-6">
      <div>
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-cyan-700">Solicitudes comerciales</p>
        <h2 className="mt-2 text-2xl font-black text-slate-950">Empresas interesadas en un plan</h2>
        <p className="mt-2 text-sm text-slate-600">Acá llegan los formularios públicos enviados desde la página de planes.</p>
      </div>

      <div className="mt-5 space-y-4">
        {activeRequests.length ? activeRequests.map(renderRequest) : (
          <div className="rounded-3xl border border-dashed border-slate-300 bg-white px-5 py-8 text-center text-sm text-slate-500">
            No hay solicitudes comerciales pendientes.
          </div>
        )}
      </div>

      {archivedRequests.length ? (
        <details className="mt-5 rounded-3xl border border-slate-200 bg-white p-4">
          <summary className="cursor-pointer text-sm font-black text-slate-700">Archivadas · {archivedRequests.length}</summary>
          <div className="mt-4 space-y-4">{archivedRequests.map(renderRequest)}</div>
        </details>
      ) : null}
    </section>
  );
};

const FILTER_OPTIONS = [
  { key: 'all', label: 'Todas' },
  { key: 'attention', label: 'A revisar' },
  { key: 'trial', label: 'Trial' },
  { key: 'expiring', label: 'Por vencer' },
  { key: 'expired', label: 'Vencidas' },
  { key: 'activity', label: 'Con actividad' },
  { key: 'idle', label: 'Sin actividad' },
  { key: 'base', label: 'Base' },
  { key: 'intermedio', label: 'Intermedio' },
  { key: 'avanzado', label: 'Avanzado' },
];

const SORT_OPTIONS = [
  { key: 'commercialDueAsc', label: 'Vencimiento más próximo' },
  { key: 'activityDesc', label: 'Actividad más reciente' },
  { key: 'createdDesc', label: 'Altas más recientes' },
  { key: 'nameAsc', label: 'Nombre A-Z' },
];

const getCommercialDueDate = (company) => {
  const trialState = company?.trialState || getTrialState(company?.generalSettings || {});
  if (trialState.isTrial) return getDateFromUnknown(trialState.endsAt);

  const billingCycle = company?.billingCycle || resolveBillingCycle(company?.generalSettings || {});
  const subscriptionState = getSubscriptionState({
    ...(company?.generalSettings || {}),
    billingCycle,
    nextBillingAt: company?.nextBillingAt,
    subscribedAt: company?.subscribedAt,
  });

  return subscriptionState.hasRenewalDate ? getDateFromUnknown(subscriptionState.nextBillingAt) : null;
};

const hasCommercialAttention = (company) => {
  const trialState = company?.trialState || getTrialState(company?.generalSettings || {});
  if (trialState.isTrial) {
    return trialState.isExpired || trialState.expiresSoon;
  }

  const billingCycle = company?.billingCycle || resolveBillingCycle(company?.generalSettings || {});
  const subscriptionState = getSubscriptionState({
    ...(company?.generalSettings || {}),
    billingCycle,
    nextBillingAt: company?.nextBillingAt,
    subscribedAt: company?.subscribedAt,
  });

  return subscriptionState.isExpired || subscriptionState.renewsSoon;
};

const matchesCompanyFilter = (company, filterKey) => {
  if (!filterKey || filterKey === 'all') return true;

  const trialState = company?.trialState || getTrialState(company?.generalSettings || {});
  const billingCycle = company?.billingCycle || resolveBillingCycle(company?.generalSettings || {});
  const subscriptionState = getSubscriptionState({
    ...(company?.generalSettings || {}),
    billingCycle,
    nextBillingAt: company?.nextBillingAt,
    subscribedAt: company?.subscribedAt,
  });

  switch (filterKey) {
    case 'attention':
      return hasCommercialAttention(company) || company.hasError;
    case 'trial':
      return trialState.isTrial;
    case 'expiring':
      return trialState.isTrial ? trialState.expiresSoon : subscriptionState.renewsSoon;
    case 'expired':
      return trialState.isTrial ? trialState.isExpired : subscriptionState.isExpired;
    case 'activity':
      return company.activeRoutesCount > 0 || company.hasActivityToday;
    case 'idle':
      return company.activeRoutesCount === 0 && !company.hasActivityToday;
    case 'base':
    case 'intermedio':
    case 'avanzado':
      return company.planKey === filterKey;
    default:
      return true;
  }
};

const sortCompaniesForRadar = (companies = [], sortKey = 'commercialDueAsc', activeCompanyId = null) => {
  const list = [...companies];

  const safeDateValue = (value, fallback) => {
    const date = getDateFromUnknown(value);
    return date ? date.getTime() : fallback;
  };

  list.sort((a, b) => {
    let comparison = 0;

    if (sortKey === 'activityDesc') {
      comparison = safeDateValue(b.lastActivityAt, 0) - safeDateValue(a.lastActivityAt, 0);
    } else if (sortKey === 'createdDesc') {
      comparison = safeDateValue(b.createdAt, 0) - safeDateValue(a.createdAt, 0);
    } else if (sortKey === 'nameAsc') {
      comparison = String(a.name || a.id).localeCompare(String(b.name || b.id));
    } else {
      comparison = safeDateValue(getCommercialDueDate(a), Number.MAX_SAFE_INTEGER) - safeDateValue(getCommercialDueDate(b), Number.MAX_SAFE_INTEGER);
    }

    if (comparison !== 0) return comparison;
    if (a.id === activeCompanyId) return -1;
    if (b.id === activeCompanyId) return 1;
    return String(a.name || a.id).localeCompare(String(b.name || b.id));
  });

  return list;
};

export default function ArchitectPanel({
  currentUserData,
  companyOptions = [],
  activeCompanyId,
  onCompanyDeleted,
  showMessage,
}) {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [toggleBusyMap, setToggleBusyMap] = useState({});
  const [planBusyMap, setPlanBusyMap] = useState({});
  const [billingDraftMap, setBillingDraftMap] = useState({});
  const [refreshNonce, setRefreshNonce] = useState(0);
  const [lastLiveUpdateAt, setLastLiveUpdateAt] = useState(null);
  const [commercialCatalog, setCommercialCatalog] = useState(DEFAULT_COMMERCIAL_CATALOG);
  const [commercialCatalogDraft, setCommercialCatalogDraft] = useState(DEFAULT_COMMERCIAL_CATALOG);
  const [catalogSaving, setCatalogSaving] = useState(false);
  const [billingRequests, setBillingRequests] = useState([]);
  const [commercialLeads, setCommercialLeads] = useState([]);
  const [commercialLeadBusyMap, setCommercialLeadBusyMap] = useState({});
  const [commercialAgentControl, setCommercialAgentControl] = useState(DEFAULT_COMMERCIAL_AGENT_CONTROL);
  const [commercialAgentBusyMap, setCommercialAgentBusyMap] = useState({});
  const [pilotAgentRunBusy, setPilotAgentRunBusy] = useState(false);
  const [liveCommercialRunBusy, setLiveCommercialRunBusy] = useState(false);
  const [discoveryCommercialRunBusy, setDiscoveryCommercialRunBusy] = useState(false);
  const [contactCommercialRunBusy, setContactCommercialRunBusy] = useState(false);
  const [followupCommercialRunBusy, setFollowupCommercialRunBusy] = useState(false);
  const [radarItems, setRadarItems] = useState([]);
  const [radarBusyMap, setRadarBusyMap] = useState({});
  const [prospects, setProspects] = useState([]);
  const [prospectBusyMap, setProspectBusyMap] = useState({});
  const [publicTrafficSummary, setPublicTrafficSummary] = useState(null);
  const [publicTrafficDaily, setPublicTrafficDaily] = useState([]);
  const [publicTrafficLoading, setPublicTrafficLoading] = useState(true);
  const [architectSection, setArchitectSection] = useState('companies');
  const [companyFilter, setCompanyFilter] = useState('all');
  const [companySort, setCompanySort] = useState('commercialDueAsc');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteConfirmValue, setDeleteConfirmValue] = useState('');
  const [deleteBusyMap, setDeleteBusyMap] = useState({});
  const [creatingLaboratory, setCreatingLaboratory] = useState(false);

  const reconnectRadar = useCallback(() => {
    setRefreshNonce((prev) => prev + 1);
  }, []);

  useEffect(() => {
    const ref = doc(db, ...COMMERCIAL_PLANS_DOC_PATH);

    const unsubscribe = onSnapshot(
      ref,
      (snapshot) => {
        const resolved = snapshot.exists()
          ? resolveCommercialCatalog(snapshot.data() || {})
          : DEFAULT_COMMERCIAL_CATALOG;

        setCommercialCatalog(resolved);
        setCommercialCatalogDraft(resolved);
      },
      (catalogError) => {
        console.error('Error reading commercial catalog:', catalogError);
      }
    );

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const summaryRef = doc(db, resolvePublicTrafficSummaryDocPath());
    const dailyQuery = query(
      collection(db, resolvePublicTrafficDailyCollectionPath()),
      orderBy('dateKey', 'desc'),
      limit(21)
    );

    const unsubscribeSummary = onSnapshot(
      summaryRef,
      (snapshot) => {
        setPublicTrafficSummary(snapshot.exists() ? { id: snapshot.id, ...snapshot.data() } : null);
      },
      (trafficError) => {
        console.error('Error reading public traffic summary:', trafficError);
      }
    );

    const unsubscribeDaily = onSnapshot(
      dailyQuery,
      (snapshot) => {
        setPublicTrafficDaily(snapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
        setPublicTrafficLoading(false);
      },
      (trafficError) => {
        console.error('Error reading public traffic daily stats:', trafficError);
        setPublicTrafficLoading(false);
      }
    );

    return () => {
      unsubscribeSummary();
      unsubscribeDaily();
    };
  }, []);

  useEffect(() => {
    const requestsRef = collection(db, `artifacts/${appId}/billingRequests`);

    const unsubscribe = onSnapshot(
      requestsRef,
      (snapshot) => {
        const nextRequests = snapshot.docs
          .map((requestDoc) => ({ id: requestDoc.id, ...requestDoc.data() }))
          .sort((a, b) => {
            const aTime = getDateFromUnknown(a.updatedAt || a.createdAt)?.getTime?.() || 0;
            const bTime = getDateFromUnknown(b.updatedAt || b.createdAt)?.getTime?.() || 0;
            return bTime - aTime;
          });

        setBillingRequests(nextRequests);
      },
      (requestsError) => {
        console.error('Error reading billing requests:', requestsError);
      }
    );

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const leadsRef = collection(db, `artifacts/${appId}/commercialLeads`);

    const unsubscribe = onSnapshot(
      leadsRef,
      (snapshot) => {
        setCommercialLeads(
          snapshot.docs
            .map((leadDoc) => ({ id: leadDoc.id, ...leadDoc.data() }))
            .sort((a, b) => {
              const aTime = getDateFromUnknown(a.updatedAt || a.createdAt)?.getTime?.() || 0;
              const bTime = getDateFromUnknown(b.updatedAt || b.createdAt)?.getTime?.() || 0;
              return bTime - aTime;
            })
        );
      },
      (leadsError) => console.error('Error reading commercial leads:', leadsError)
    );

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const controlRef = doc(db, resolveCommercialAgentsDocPath());

    const unsubscribe = onSnapshot(
      controlRef,
      (snapshot) => {
        const nextValue = snapshot.exists()
          ? resolveCommercialAgentControl(snapshot.data() || {})
          : DEFAULT_COMMERCIAL_AGENT_CONTROL;

        setCommercialAgentControl(nextValue);
      },
      (controlError) => {
        console.error('Error reading commercial agent control:', controlError);
      }
    );

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const radarRef = collection(db, resolveCommercialRadarCollectionPath());

    const unsubscribe = onSnapshot(
      radarRef,
      (snapshot) => {
        const nextRadarItems = snapshot.docs
          .map((radarDoc) => ({ id: radarDoc.id, ...radarDoc.data() }))
          .sort((a, b) => {
            const aTime = getDateFromUnknown(a.updatedAt || a.createdAt)?.getTime?.() || 0;
            const bTime = getDateFromUnknown(b.updatedAt || b.createdAt)?.getTime?.() || 0;
            return bTime - aTime;
          });

        setRadarItems(nextRadarItems);
      },
      (radarError) => {
        console.error('Error reading commercial radar:', radarError);
      }
    );

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const prospectsRef = collection(db, resolveCommercialProspectsCollectionPath());

    const unsubscribe = onSnapshot(
      prospectsRef,
      (snapshot) => {
        const nextProspects = snapshot.docs
          .map((prospectDoc) => ({ id: prospectDoc.id, ...prospectDoc.data() }))
          .sort((a, b) => {
            const aTime = getDateFromUnknown(a.updatedAt || a.createdAt)?.getTime?.() || 0;
            const bTime = getDateFromUnknown(b.updatedAt || b.createdAt)?.getTime?.() || 0;
            return bTime - aTime;
          });

        setProspects(nextProspects);
      },
      (prospectsError) => {
        console.error('Error reading commercial prospects:', prospectsError);
      }
    );

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!companyOptions.length) {
      setCompanies([]);
      setLoading(false);
      setError('');
      return undefined;
    }

    let cancelled = false;
    const companyStateMap = new Map();
    const readyMap = new Map();

    setLoading(true);
    setError('');

    const publishCompanies = () => {
      if (cancelled) return;

      const summaries = companyOptions
        .map((company) => companyStateMap.get(company.id))
        .filter(Boolean)
        .map((entry) => buildCompanySummaryFromState(entry.company, entry));

      setCompanies(sortAndFilterSummaries(summaries, activeCompanyId));
      setBillingDraftMap((prev) => {
        const next = { ...prev };
        summaries.forEach((summary) => {
          if (!next[summary.id]) {
            next[summary.id] = summary.billingCycle || 'monthly';
          }
        });
        return next;
      });

      const allReady = companyOptions.every((company) => {
        const ready = readyMap.get(company.id);
        return ready?.general && ready?.modules && ready?.routes;
      });

      if (allReady) {
        setLoading(false);
      }

      if (summaries.length > 0) {
        setLastLiveUpdateAt(new Date());
      }
    };

    const setCompanyErrorState = (company, source, snapshotError) => {
      console.error(`Error reading ${source} for company ${company.id}:`, snapshotError);

      const existing = companyStateMap.get(company.id) || {
        company,
        generalSettings: {},
        modulesSettings: {},
        routes: [],
        hasGeneralDocument: false,
        hasModulesDocument: false,
        hasError: false,
      };

      companyStateMap.set(company.id, {
        ...existing,
        hasError: true,
      });

      const ready = readyMap.get(company.id) || { general: false, modules: false, routes: false };
      ready[source] = true;
      readyMap.set(company.id, ready);
      publishCompanies();
    };

    const unsubscribes = companyOptions.flatMap((company) => {
      companyStateMap.set(company.id, {
        company,
        generalSettings: {},
        modulesSettings: {},
        routes: [],
        hasGeneralDocument: false,
        hasModulesDocument: false,
        hasError: false,
      });

      readyMap.set(company.id, { general: false, modules: false, routes: false });

      const generalRef = doc(db, resolveCompanyGeneralSettingsPath(company.id));
      const modulesRef = doc(db, resolveCompanyModulesSettingsPath(company.id));
      const routesRef = collection(db, resolveRoutesCollectionPath(company.id));

      const generalUnsub = onSnapshot(
        generalRef,
        (snapshot) => {
          const previous = companyStateMap.get(company.id);
          if (!previous) return;

          companyStateMap.set(company.id, {
            ...previous,
            generalSettings: snapshot.data() || {},
            hasGeneralDocument: snapshot.exists(),
            hasError: previous.hasError,
          });

          const ready = readyMap.get(company.id) || { general: false, modules: false, routes: false };
          ready.general = true;
          readyMap.set(company.id, ready);
          publishCompanies();
        },
        (snapshotError) => setCompanyErrorState(company, 'general', snapshotError)
      );

      const modulesUnsub = onSnapshot(
        modulesRef,
        (snapshot) => {
          const previous = companyStateMap.get(company.id);
          if (!previous) return;

          companyStateMap.set(company.id, {
            ...previous,
            modulesSettings: snapshot.data() || {},
            hasModulesDocument: snapshot.exists(),
            hasError: previous.hasError,
          });

          const ready = readyMap.get(company.id) || { general: false, modules: false, routes: false };
          ready.modules = true;
          readyMap.set(company.id, ready);
          publishCompanies();
        },
        (snapshotError) => setCompanyErrorState(company, 'modules', snapshotError)
      );

      const routesUnsub = onSnapshot(
        routesRef,
        (snapshot) => {
          const previous = companyStateMap.get(company.id);
          if (!previous) return;

          companyStateMap.set(company.id, {
            ...previous,
            routes: snapshot.docs.map((routeDoc) => ({ id: routeDoc.id, ...routeDoc.data() })),
            hasError: previous.hasError,
          });

          const ready = readyMap.get(company.id) || { general: false, modules: false, routes: false };
          ready.routes = true;
          readyMap.set(company.id, ready);
          publishCompanies();
        },
        (snapshotError) => setCompanyErrorState(company, 'routes', snapshotError)
      );

      return [generalUnsub, modulesUnsub, routesUnsub];
    });

    return () => {
      cancelled = true;
      unsubscribes.forEach((unsubscribe) => {
        if (typeof unsubscribe === 'function') {
          unsubscribe();
        }
      });
    };
  }, [activeCompanyId, companyOptions, refreshNonce]);

  const handleCommercialFieldChange = useCallback((planKey, field, value) => {
    setCommercialCatalogDraft((prev) => ({
      ...prev,
      plans: prev.plans.map((plan) =>
        plan.key === planKey
          ? {
              ...plan,
              [field]: value,
            }
          : plan
      ),
    }));
  }, []);

  const handleUpdateCommercialLeadStatus = useCallback(async (lead, nextStatus) => {
    if (!lead?.id || !nextStatus) return;

    setCommercialLeadBusyMap((prev) => ({ ...prev, [lead.id]: true }));
    try {
      await setDoc(
        doc(db, `artifacts/${appId}/commercialLeads`, lead.id),
        {
          status: nextStatus,
          statusUpdatedAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
      showMessage?.(`Solicitud de ${lead.companyName || 'la empresa'} actualizada.`, 'success');
    } catch (leadError) {
      console.error('Error updating commercial lead:', leadError);
      showMessage?.('No pudimos actualizar la solicitud comercial.', 'error');
    } finally {
      setCommercialLeadBusyMap((prev) => ({ ...prev, [lead.id]: false }));
    }
  }, [showMessage]);

  const handleSaveCommercialCatalog = useCallback(async () => {
    setCatalogSaving(true);
    try {
      await setDoc(
        doc(db, ...COMMERCIAL_PLANS_DOC_PATH),
        {
          ...commercialCatalogDraft,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      if (showMessage) {
        showMessage('Catálogo comercial actualizado.', 'success');
      }
    } catch (catalogError) {
      console.error('Error saving commercial catalog:', catalogError);
      if (showMessage) {
        showMessage('No se pudo guardar el catálogo comercial.', 'error');
      }
    } finally {
      setCatalogSaving(false);
    }
  }, [commercialCatalogDraft, showMessage]);

  const handleChangeBillingCycle = useCallback((companyId, cycle) => {
    setBillingDraftMap((prev) => ({
      ...prev,
      [companyId]: cycle,
    }));
  }, []);

  const handleUpdateRadarStatus = useCallback(async (item, nextStatus) => {
    if (!item?.id || !nextStatus) return;

    setRadarBusyMap((prev) => ({ ...prev, [item.id]: true }));
    try {
      if (nextStatus === 'discarded') {
        const companyKey = String(item.companyKey || '').trim();
        if (companyKey) {
          await setDoc(
            doc(db, resolveCommercialRadarExcludedCollectionPath(), companyKey),
            {
              companyKey,
              companyName: String(item.companyName || '').trim(),
              website: String(item.website || '').trim(),
              sourceUrl: String(item.sourceUrl || '').trim(),
              discardedAt: serverTimestamp(),
              updatedAt: serverTimestamp(),
            },
            { merge: true }
          );
        }

        await deleteDoc(doc(db, resolveCommercialRadarCollectionPath(), item.id));
        showMessage?.(`${item.companyName || 'La empresa'} fue descartada y salió del Radar.`, 'success');
        return;
      }

      await setDoc(
        doc(db, resolveCommercialRadarCollectionPath(), item.id),
        {
          status: nextStatus,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      const statusLabel = getRadarStatusMeta(nextStatus).label;
      showMessage?.(`${item.companyName || 'La empresa'} pasó a ${statusLabel}.`, 'success');
    } catch (updateRadarError) {
      console.error('Error updating radar status:', updateRadarError);
      showMessage?.('No pudimos actualizar el estado de Radar.', 'error');
    } finally {
      setRadarBusyMap((prev) => ({ ...prev, [item.id]: false }));
    }
  }, [showMessage]);

  const handleDeleteRadarItem = useCallback(async (item) => {
    if (!item?.id) return;

    setRadarBusyMap((prev) => ({ ...prev, [item.id]: true }));
    try {
      await deleteDoc(doc(db, resolveCommercialRadarCollectionPath(), item.id));
      showMessage?.(`${item.companyName || 'El hallazgo'} se eliminó del Radar.`, 'success');
    } catch (deleteRadarError) {
      console.error('Error deleting radar item:', deleteRadarError);
      showMessage?.('No pudimos eliminar este hallazgo del Radar.', 'error');
    } finally {
      setRadarBusyMap((prev) => ({ ...prev, [item.id]: false }));
    }
  }, [showMessage]);

  const handleDeleteProspect = useCallback(async (prospect) => {
    if (!prospect?.id) return;

    setProspectBusyMap((prev) => ({ ...prev, [prospect.id]: true }));
    try {
      await deleteDoc(doc(db, resolveCommercialProspectsCollectionPath(), prospect.id));
      showMessage?.(`${prospect.companyName || 'El prospecto'} se eliminó de Prospectos.`, 'success');
    } catch (deleteProspectError) {
      console.error('Error deleting prospect:', deleteProspectError);
      showMessage?.('No pudimos eliminar este prospecto.', 'error');
    } finally {
      setProspectBusyMap((prev) => ({ ...prev, [prospect.id]: false }));
    }
  }, [showMessage]);

  const handleMarkProspectResponseSeen = useCallback(async (prospect) => {
    if (!prospect?.id) return;

    setProspectBusyMap((prev) => ({ ...prev, [prospect.id]: true }));
    try {
      await setDoc(
        doc(db, resolveCommercialProspectsCollectionPath(), prospect.id),
        {
          unreadResponse: false,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
      showMessage?.(`Respuesta marcada como vista para ${prospect.companyName || 'el prospecto'}.`, 'success');
    } catch (seenError) {
      console.error('Error marking prospect response as seen:', seenError);
      showMessage?.('No pudimos marcar esta respuesta como vista.', 'error');
    } finally {
      setProspectBusyMap((prev) => ({ ...prev, [prospect.id]: false }));
    }
  }, [showMessage]);

  const handleSaveCommercialResponseNotify = useCallback(async (email) => {
    const normalizedEmail = String(email || '').trim();
    if (normalizedEmail && !/\S+@\S+\.\S+/.test(normalizedEmail)) {
      showMessage?.('Ingresá un correo válido para los avisos de respuesta.', 'error');
      return;
    }

    setCommercialAgentBusyMap((prev) => ({ ...prev, followup_notify: true }));
    try {
      await setDoc(
        doc(db, resolveCommercialAgentsDocPath()),
        {
          followup: {
            ...(commercialAgentControl?.followup || {}),
            responseNotifyTo: normalizedEmail,
            updatedAt: serverTimestamp(),
          },
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
      showMessage?.(
        normalizedEmail
          ? `Avisos de respuesta configurados para ${normalizedEmail}.`
          : 'Se limpió el correo de aviso de respuestas.',
        'success'
      );
    } catch (notifyError) {
      console.error('Error saving response notify email:', notifyError);
      showMessage?.('No pudimos guardar el correo de aviso.', 'error');
    } finally {
      setCommercialAgentBusyMap((prev) => ({ ...prev, followup_notify: false }));
    }
  }, [commercialAgentControl?.followup, showMessage]);

  const handleToggleCommercialAgent = useCallback(async (agentKey, active) => {
    if (!agentKey) return;

    setCommercialAgentBusyMap((prev) => ({ ...prev, [agentKey]: true }));
    try {
      await setDoc(
        doc(db, resolveCommercialAgentsDocPath()),
        {
          [agentKey]: {
            active,
            updatedAt: serverTimestamp(),
          },
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      const agentLabel = COMMERCIAL_AGENT_DEFINITIONS.find((agent) => agent.key === agentKey)?.label || 'Agente';
      showMessage?.(`${agentLabel} ${active ? 'activado' : 'pausado'}.`, 'success');
    } catch (toggleError) {
      console.error('Error updating commercial agent control:', toggleError);
      showMessage?.('No pudimos actualizar el estado del agente.', 'error');
    } finally {
      setCommercialAgentBusyMap((prev) => ({ ...prev, [agentKey]: false }));
    }
  }, [showMessage]);

  const handleRunPilotCommercialPass = useCallback(async () => {
    if (!functions) {
      showMessage?.('Las funciones de Firebase no están disponibles en este entorno.', 'error');
      return;
    }

    setPilotAgentRunBusy(true);
    try {
      const enqueuePilot = httpsCallable(functions, 'enqueueCommercialRadarSourceBatch');
      const response = await enqueuePilot({
        sources: COMMERCIAL_AGENT_PILOT_BATCH_AR.map((seed) => ({
          ...seed,
          country: 'Argentina',
          source: seed.source || 'Radar piloto Argentina',
        })),
      });
      const queued = Number(response?.data?.queued || 0);
      showMessage?.(
        queued
          ? `Piloto encolado en backend. ${queued} caso${queued === 1 ? '' : 's'} quedó${queued === 1 ? '' : 'ron'} listo${queued === 1 ? '' : 's'} para el Radar vivo.`
          : 'No se encolaron casos piloto.',
        queued ? 'success' : 'info'
      );
    } catch (pilotError) {
      console.error('Error running pilot commercial pass:', pilotError);
      showMessage?.('No pudimos encolar la pasada piloto de agentes.', 'error');
    } finally {
      setPilotAgentRunBusy(false);
    }
  }, [showMessage]);

  const handleRunLiveCommercialPass = useCallback(async () => {
    if (!functions) {
      showMessage?.('Las funciones de Firebase no están disponibles en este entorno.', 'error');
      return;
    }

    setLiveCommercialRunBusy(true);
    try {
      const runCommercialRadar = httpsCallable(functions, 'runCommercialRadarAgentNow');
      const response = await runCommercialRadar({});
      const result = response?.data || {};
      const processing = result?.processing || {};
      const loading = result?.loading || {};
      const contact = result?.contact || {};
      const processed = Number(processing.processed || 0);
      const validated = Number(processing.validated || 0);
      const readyValidated = Number(processing.readyValidated || 0);
      const duplicates = Number(processing.duplicates || 0);
      const invalid = Number(processing.invalid || 0);
      const loaded = Number(loading.loaded || 0);
      const sent = Number(contact.sent || 0);
      const reroutedManual = Number(contact.reroutedManual || 0);

      showMessage?.(
        processed || loaded || invalid || duplicates || sent || reroutedManual
          ? `Radar procesado. ${processed} revisados, ${readyValidated} listos, ${validated} nuevos validados, ${loaded} cargados a Prospectos, ${sent} enviados por mail, ${reroutedManual} derivados a revisión manual, ${duplicates} duplicados y ${invalid} inválidos.`
          : 'Radar backend ejecutado. No había fuentes pendientes para procesar.',
        'success'
      );
    } catch (runError) {
      console.error('Error running live commercial radar:', runError);
      showMessage?.('No pudimos ejecutar el Radar backend.', 'error');
    } finally {
      setLiveCommercialRunBusy(false);
    }
  }, [showMessage]);

  const handleRunDiscoveryCommercialPass = useCallback(async () => {
    if (!functions) {
      showMessage?.('Las funciones de Firebase no están disponibles en este entorno.', 'error');
      return;
    }

    setDiscoveryCommercialRunBusy(true);
    try {
      const discoverCommercialRadar = httpsCallable(functions, 'discoverCommercialRadarSourcesNow');
      const response = await discoverCommercialRadar({});
      const discovery = response?.data?.discovery || {};
      const processing = response?.data?.processing || {};
      const loading = response?.data?.loading || {};
      const contact = response?.data?.contact || {};
      const queued = Number(discovery.queued || 0);
      const selected = Number(discovery.selected || 0);
      const processed = Number(processing.processed || 0);
      const validated = Number(processing.validated || 0);
      const readyValidated = Number(processing.readyValidated || 0);
      const loaded = Number(loading.loaded || 0);
      const sent = Number(contact.sent || 0);
      const reroutedManual = Number(contact.reroutedManual || 0);

      showMessage?.(
        queued || processed || loaded || sent || reroutedManual
          ? `Radar real ejecutado. ${selected || queued} hallazgos útiles, ${processed} procesados, ${readyValidated} listos, ${validated} nuevos validados, ${loaded} cargados a Prospectos, ${sent} enviados por mail y ${reroutedManual} derivados a revisión manual.`
          : 'Radar real ejecutado. No encontramos empresas nuevas en esta pasada.',
        'success'
      );
    } catch (discoveryError) {
      console.error('Error running discovery commercial pass:', discoveryError);
      showMessage?.('No pudimos buscar empresas reales en esta pasada.', 'error');
    } finally {
      setDiscoveryCommercialRunBusy(false);
    }
  }, [showMessage]);

  const handleRunContactCommercialPass = useCallback(async () => {
    if (!functions) {
      showMessage?.('Las funciones de Firebase no están disponibles en este entorno.', 'error');
      return;
    }

    setContactCommercialRunBusy(true);
    try {
      const runCommercialContact = httpsCallable(functions, 'runCommercialContactAgentNow');
      const response = await runCommercialContact({});
      const result = response?.data || {};
      const sent = Number(result.sent || 0);
      const queued = Number(result.queued || 0);
      const skippedNoEmail = Number(result.skippedNoEmail || 0);
      const reroutedManual = Number(result.reroutedManual || 0);
      const archivedGeneric = Number(result.archivedGeneric || 0);
      const blocked = Number(result.blocked || 0);
      const errors = Number(result.errors || 0);

      showMessage?.(
        sent || queued || skippedNoEmail || blocked || errors || reroutedManual || archivedGeneric
          ? `Cola de contacto procesada. ${sent} enviado${sent === 1 ? '' : 's'}, ${reroutedManual} derivados a revisión manual, ${archivedGeneric} archivados por genéricos, ${errors} con error y ${blocked} bloqueados.`
          : 'No había prospectos pendientes en la cola de contacto.',
        'success'
      );
    } catch (contactError) {
      console.error('Error running commercial contact pass:', contactError);
      showMessage?.('No pudimos ejecutar la cola de contacto.', 'error');
    } finally {
      setContactCommercialRunBusy(false);
    }
  }, [showMessage]);

  const handleRunFollowupCommercialPass = useCallback(async () => {
    if (!functions) {
      showMessage?.('Las funciones de Firebase no están disponibles en este entorno.', 'error');
      return;
    }

    setFollowupCommercialRunBusy(true);
    try {
      const runCommercialFollowup = httpsCallable(functions, 'runCommercialFollowupAgentNow');
      const response = await runCommercialFollowup({});
      const result = response?.data || {};
      const inboxUnread = Number(result.inboxUnread || 0);
      const scanned = Number(result.scanned || 0);
      const matched = Number(result.matched || 0);
      const responded = Number(result.responded || 0);
      const bounced = Number(result.bounced || 0);
      const updated = Number(result.updated || 0);
      const ignored = Number(result.ignored || 0);
      const secondTouches = Number(result.secondTouches || 0);
      const blocked = Number(result.blocked || 0);

      showMessage?.(
        scanned || matched || responded || bounced || updated || ignored || blocked || inboxUnread || secondTouches
          ? `Seguimiento revisado. Inbox con ${inboxUnread} sin leer, ${scanned} mensajes nuevos revisados, ${matched} vinculados, ${responded} respuestas nuevas, ${bounced} correo${bounced === 1 ? '' : 's'} inválido${bounced === 1 ? '' : 's'} detectado${bounced === 1 ? '' : 's'} y ${secondTouches} segundo${secondTouches === 1 ? '' : 's'} contacto${secondTouches === 1 ? '' : 's'} automático${secondTouches === 1 ? '' : 's'}.`
          : 'No aparecieron respuestas nuevas para procesar en esta pasada.',
        'success'
      );
    } catch (followupError) {
      console.error('Error running commercial followup pass:', followupError);
      showMessage?.('No pudimos revisar respuestas comerciales.', 'error');
    } finally {
      setFollowupCommercialRunBusy(false);
    }
  }, [showMessage]);

  const handleApplyCommercialTargetPreset = useCallback(async (presetKey) => {
    const preset = resolveCommercialTargetPreset(presetKey);
    if (!preset?.key) return;

    try {
      await setDoc(
        doc(db, resolveCommercialAgentsDocPath()),
        {
          targetProfile: {
            presetKey: preset.key,
            label: preset.label,
            slogan: preset.slogan,
            summary: preset.summary,
            segments: preset.segments,
            cities: preset.cities,
            cadence: preset.cadence,
            updatedAt: serverTimestamp(),
          },
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      showMessage?.(`Foco comercial actualizado a ${preset.label}.`, 'success');
    } catch (presetError) {
      console.error('Error applying commercial target preset:', presetError);
      showMessage?.('No pudimos actualizar el foco comercial.', 'error');
    }
  }, [showMessage]);

  const handleAdvanceProspect = useCallback(async (prospect, nextStatus) => {
    if (!prospect?.id || !nextStatus) return;
    setProspectBusyMap((prev) => ({ ...prev, [prospect.id]: true }));
    try {
      const normalizedStatus = normalizeProspectStatusKey(nextStatus);
      const nextStepMap = {
        pending_review: 'Revisar datos del caso y definir el próximo contacto.',
        queued_to_send: 'Esperar el envío automático o intervenir manualmente si hace falta.',
        sent: 'Esperar respuesta al correo comercial.',
        manual_sent: 'Esperar respuesta al contacto manual por web o WhatsApp.',
        responded: 'Responder el contacto comercial y coordinar la siguiente acción.',
        demo: 'Coordinar la demo y registrar el resultado comercial.',
        trial: 'Acompañar la prueba y seguir la activación operativa.',
        converted: 'Cliente activo. Seguir continuidad comercial y operativa.',
        archived: 'Caso archivado para revisión o descarte.',
      };
      await setDoc(
        doc(db, resolveCommercialProspectsCollectionPath(), prospect.id),
        {
          status: normalizedStatus,
          statusUpdatedAt: serverTimestamp(),
          unreadResponse: normalizedStatus === 'responded' ? prospect.unreadResponse === true : false,
          stageTimestamps: {
            ...(prospect.stageTimestamps || {}),
            [normalizedStatus]: serverTimestamp(),
          },
          nextStep: nextStepMap[normalizedStatus] || prospect.nextStep || 'Seguimiento comercial en curso.',
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      let contactSummary = '';
      if (normalizedStatus === 'queued_to_send' && functions) {
        try {
          const runCommercialContact = httpsCallable(functions, 'runCommercialContactAgentNow');
          const contactResponse = await runCommercialContact({});
          const contactResult = contactResponse?.data || {};

          if (Number(contactResult.sent || 0) > 0) {
            contactSummary = ' El agente de contacto intentó el envío automático en esta misma pasada.';
          } else if (String(contactResult.reason || '').trim() === 'missing_smtp_config') {
            contactSummary = ' Quedó listo, pero falta configurar la casilla de envío automático.';
          } else if (Number(contactResult.reroutedManual || 0) > 0) {
            contactSummary = ' Quedó otra vez en revisión manual porque sigue faltando un mail directo.';
          }
        } catch (contactRunError) {
          console.error('Error running contact agent after prospect advance:', contactRunError);
          contactSummary = ' El estado cambió, pero la pasada automática de contacto no respondió en este intento.';
        }
      }

      const nextStatusLabel = getProspectStatusMeta(normalizedStatus).label;
      showMessage?.(`${prospect.companyName || 'El prospecto'} pasó a ${nextStatusLabel}.${contactSummary}`, 'success');
    } catch (advanceError) {
      console.error('Error advancing prospect:', advanceError);
      showMessage?.('No pudimos actualizar la etapa comercial.', 'error');
    } finally {
      setProspectBusyMap((prev) => ({ ...prev, [prospect.id]: false }));
    }
  }, [showMessage]);

  const handleSendProspect = useCallback(async (prospect, channel = 'email') => {
    if (!prospect?.id) return;

    const normalizedChannel = channel === 'whatsapp' ? 'whatsapp' : 'email';
    const targetUrl = normalizedChannel === 'whatsapp'
      ? buildProspectWhatsAppLink(prospect)
      : buildMailtoLink(prospect);

    if (!targetUrl) {
      showMessage?.(
        normalizedChannel === 'whatsapp'
          ? 'Este prospecto no tiene WhatsApp disponible para enviar.'
          : 'Este prospecto no tiene correo disponible para enviar.',
        'error'
      );
      return;
    }

    setProspectBusyMap((prev) => ({ ...prev, [prospect.id]: true }));
    try {
      if (typeof window !== 'undefined') {
        window.open(targetUrl, '_blank', 'noopener,noreferrer');
      }

      const nextStatus = normalizedChannel === 'whatsapp' ? 'manual_sent' : 'sent';

      await setDoc(
        doc(db, resolveCommercialProspectsCollectionPath(), prospect.id),
        {
          status: nextStatus,
          statusUpdatedAt: serverTimestamp(),
          unreadResponse: false,
          stageTimestamps: {
            ...(prospect.stageTimestamps || {}),
            [nextStatus]: serverTimestamp(),
          },
          lastOutreach: {
            channel: normalizedChannel,
            at: serverTimestamp(),
            template: 'commercial_presentation',
          },
          nextStep: normalizedChannel === 'whatsapp'
            ? 'Esperar respuesta al primer contacto por WhatsApp.'
            : 'Esperar respuesta al correo comercial.',
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      showMessage?.(
        normalizedChannel === 'whatsapp'
          ? `${prospect.companyName || 'El prospecto'} quedó registrado como contacto manual por WhatsApp.`
          : `${prospect.companyName || 'El prospecto'} quedó registrado como contacto por mail.`,
        'success'
      );
    } catch (sendError) {
      console.error('Error sending commercial prospect:', sendError);
      showMessage?.('No pudimos abrir el canal de contacto de este prospecto.', 'error');
    } finally {
      setProspectBusyMap((prev) => ({ ...prev, [prospect.id]: false }));
    }
  }, [showMessage]);

  const handlePromoteRadarItem = useCallback(async (item) => {
    if (!item?.id) return;

    setRadarBusyMap((prev) => ({ ...prev, [item.id]: true }));
    try {
      const normalizedCompany = normalizeComparableText(item.companyName || '');
      const existingProspect = prospects.find(
        (prospect) => normalizeComparableText(prospect.companyName || '') === normalizedCompany
      );

      const targetProspectId = existingProspect?.id || doc(collection(db, resolveCommercialProspectsCollectionPath())).id;
      const suggestedStatus = String(item.contactEmail || existingProspect?.contactEmail || '').trim() ? 'queued_to_send' : 'pending_review';
      const nextStatus = resolveProspectStatusAfterLoad(existingProspect?.status, suggestedStatus);
      const nextStep = nextStatus === 'queued_to_send'
        ? 'Contacto automático listo para enviar por mail.'
        : nextStatus === 'pending_review'
          ? 'No tiene mail directo. Revisar contacto manual por web o WhatsApp.'
          : String(existingProspect?.nextStep || 'Seguimiento comercial en curso.').trim();

      await setDoc(
        doc(db, resolveCommercialProspectsCollectionPath(), targetProspectId),
        {
          companyName: String(item.companyName || existingProspect?.companyName || '').trim(),
          segment: String(item.segment || existingProspect?.segment || PROSPECT_SEGMENTS[0]).trim(),
          city: String(item.city || existingProspect?.city || '').trim(),
          contactName: existingProspect?.contactName || '',
          contactChannel: item.contactValue?.includes('@')
            ? 'Email'
            : (existingProspect?.contactChannel || 'WhatsApp'),
          contactValue: String(item.contactValue || existingProspect?.contactValue || '').trim(),
          website: String(item.website || existingProspect?.website || '').trim(),
          contactEmail: String(item.contactEmail || existingProspect?.contactEmail || '').trim(),
          contactWhatsApp: String(item.contactWhatsApp || existingProspect?.contactWhatsApp || '').trim(),
          sourceUrl: String(item.sourceUrl || existingProspect?.sourceUrl || '').trim(),
          nextStep,
          notes: String(item.notes || existingProspect?.notes || '').trim(),
          status: nextStatus,
          statusUpdatedAt: serverTimestamp(),
          stageTimestamps: {
            ...(existingProspect?.stageTimestamps || {}),
            ...(normalizeProspectStatusKey(existingProspect?.status) !== nextStatus
              ? { [nextStatus]: serverTimestamp() }
              : {}),
          },
          outreachDraft: {
            subject: buildCommercialProspectSubject({
              companyName: item.companyName || existingProspect?.companyName || '',
              segment: item.segment || existingProspect?.segment || '',
            }),
            emailBody: buildCommercialProspectEmailBody({
              companyName: item.companyName || existingProspect?.companyName || '',
              segment: item.segment || existingProspect?.segment || '',
            }),
            whatsappBody: buildCommercialProspectWhatsAppMessage({
              companyName: item.companyName || existingProspect?.companyName || '',
              segment: item.segment || existingProspect?.segment || '',
            }),
          },
          sourceStage: 'agent_loaded',
          updatedAt: serverTimestamp(),
          ...(existingProspect
            ? {}
            : {
                createdAt: serverTimestamp(),
              }),
        },
        { merge: true }
      );

      await setDoc(
        doc(db, resolveCommercialRadarCollectionPath(), item.id),
        {
          status: 'loaded',
          linkedProspectId: targetProspectId,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      let contactSummaryMessage = '';
      if (functions) {
        try {
          const runCommercialContact = httpsCallable(functions, 'runCommercialContactAgentNow');
          const contactResponse = await runCommercialContact({});
          const contactResult = contactResponse?.data || {};

          if (Number(contactResult.sent || 0) > 0) {
            contactSummaryMessage = ' El agente de contacto envió el correo automáticamente.';
          } else if (String(contactResult.reason || '').trim() === 'missing_smtp_config') {
            contactSummaryMessage = ' Quedó en cola de contacto, pero falta configurar la casilla de envío automático.';
          } else if (Number(contactResult.skippedNoEmail || 0) > 0) {
            contactSummaryMessage = ' El sistema lo dejó en revisión manual porque no tiene mail directo para envío automático.';
          }
        } catch (contactRunError) {
          console.error('Error running contact agent after radar approval:', contactRunError);
          contactSummaryMessage = ' Quedó en cola de contacto, pero el envío automático no respondió en esta pasada.';
        }
      }

      showMessage?.(
        `${existingProspect
          ? `${item.companyName || 'La empresa'} ya estaba en Prospectos y se volvió a sincronizar con el circuito comercial.`
          : `${item.companyName || 'La empresa'} pasó de Radar al circuito comercial.`}${contactSummaryMessage}`,
        'success'
      );
    } catch (promoteError) {
      console.error('Error promoting radar item:', promoteError);
      showMessage?.('No pudimos pasar esta empresa a Prospectos.', 'error');
    } finally {
      setRadarBusyMap((prev) => ({ ...prev, [item.id]: false }));
    }
  }, [prospects, showMessage]);

  const handleCopyCommercialTemplate = useCallback(async (templateKey) => {
    const text = templateKey === 'email' ? COMMERCIAL_EMAIL_TEMPLATE : COMMERCIAL_WHATSAPP_TEMPLATE;

    try {
      if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        throw new Error('Clipboard API unavailable');
      }

      showMessage?.(
        templateKey === 'email' ? 'Plantilla de mail copiada.' : 'Plantilla de WhatsApp copiada.',
        'success'
      );
    } catch (copyError) {
      console.error('Error copying commercial template:', copyError);
      showMessage?.('No pudimos copiar la plantilla automáticamente.', 'error');
    }
  }, [showMessage]);

  const handleCopyManualProspectMessage = useCallback(async (prospect) => {
    const text = buildCommercialProspectManualMessage(prospect);

    try {
      if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        throw new Error('Clipboard API unavailable');
      }

      showMessage?.(`Mensaje listo para ${prospect?.companyName || 'el prospecto'} copiado.`, 'success');
    } catch (copyError) {
      console.error('Error copying manual commercial message:', copyError);
      showMessage?.('No pudimos copiar el mensaje sugerido.', 'error');
    }
  }, [showMessage]);

  const handleToggleModule = useCallback(
    async (company, moduleKey, nextEnabled) => {
      const busyKey = `${company.id}:${moduleKey}`;
      const currentLabel = company.name || company.id;
      const optimisticCompany = applyModuleSettingToSummary(company, moduleKey, nextEnabled);
      const nextPlanState = {
        key: optimisticCompany.planKey,
        label: optimisticCompany.plan,
        description: optimisticCompany.planDescription,
      };

      setToggleBusyMap((prev) => ({
        ...prev,
        [busyKey]: true,
      }));

      setCompanies((prev) => prev.map((item) => (item.id === company.id ? optimisticCompany : item)));

      try {
        await Promise.all([
          setDoc(
            doc(db, resolveCompanyModulesSettingsPath(company.id)),
            {
              [moduleKey]: {
                enabled: nextEnabled,
              },
              updatedAt: serverTimestamp(),
            },
            { merge: true }
          ),
          setDoc(
            doc(db, resolveCompanyGeneralSettingsPath(company.id)),
            buildPlanSettingsPayload(nextPlanState, {
              billingCycle: company.billingCycle,
              subscribedAt: company.subscribedAt || null,
              nextBillingAt: company.nextBillingAt || null,
              trialStartedAt: company.generalSettings?.trialStartedAt || null,
              trialEndsAt: company.generalSettings?.trialEndsAt || null,
              trialStatus: company.generalSettings?.trialStatus || null,
            }),
            { merge: true }
          ),
          setDoc(
            doc(db, resolveCompanyPlanSettingsPath(company.id)),
            buildPlanSettingsPayload(nextPlanState, {
              billingCycle: company.billingCycle,
              subscribedAt: company.subscribedAt || null,
              nextBillingAt: company.nextBillingAt || null,
              trialStartedAt: company.generalSettings?.trialStartedAt || null,
              trialEndsAt: company.generalSettings?.trialEndsAt || null,
              trialStatus: company.generalSettings?.trialStatus || null,
            }),
            { merge: true }
          ),
          setDoc(
            doc(db, resolveCompanyDocPath(company.id)),
            buildPlanSettingsPayload(nextPlanState, {
              billingCycle: company.billingCycle,
              subscribedAt: company.subscribedAt || null,
              nextBillingAt: company.nextBillingAt || null,
              trialStartedAt: company.generalSettings?.trialStartedAt || null,
              trialEndsAt: company.generalSettings?.trialEndsAt || null,
              trialStatus: company.generalSettings?.trialStatus || null,
            }),
            { merge: true }
          ),
        ]);

        if (showMessage) {
          showMessage(
            `${MODULE_LABELS[moduleKey] || moduleKey} ${nextEnabled ? 'activado' : 'desactivado'} para ${currentLabel}. Plan actual: ${optimisticCompany.plan}.`,
            'success'
          );
        }
      } catch (toggleError) {
        console.error('Error updating company module:', toggleError);
        if (showMessage) {
          showMessage(`No se pudo actualizar ${MODULE_LABELS[moduleKey] || moduleKey} en ${currentLabel}.`, 'error');
        }
        reconnectRadar();
      } finally {
        setToggleBusyMap((prev) => {
          const next = { ...prev };
          delete next[busyKey];
          return next;
        });
      }
    },
    [reconnectRadar, showMessage]
  );

  const handleApplyPlan = useCallback(
    async (company, preset, billingCycle) => {
      const busyKey = company.id;
      const currentLabel = company.name || company.id;
      const selectedBillingCycle = preset.key === 'trial' ? null : billingCycle || 'monthly';
      const optimisticCompany = applyPresetToSummary(company, preset, selectedBillingCycle || 'monthly');

      setPlanBusyMap((prev) => ({
        ...prev,
        [busyKey]: true,
      }));

      setCompanies((prev) => prev.map((item) => (item.id === company.id ? optimisticCompany : item)));

      try {
        let payloadExtra = {};

        if (preset.key === 'trial') {
          const trialStartedAt = getDateFromUnknown(company.generalSettings?.trialStartedAt || company.generalSettings?.createdAt) || new Date();
          const trialEndsAt =
            getDateFromUnknown(company.generalSettings?.trialEndsAt) ||
            addDays(trialStartedAt, DEFAULT_TRIAL_DAYS);
          const trialStatus = getStartOfDay(trialEndsAt).getTime() < getStartOfDay(new Date()).getTime() ? 'expired' : 'active';

          payloadExtra = {
            trialStartedAt,
            trialEndsAt,
            trialStatus,
            subscribedAt: null,
            nextBillingAt: null,
            billingCycle: null,
          };
        } else {
          const subscribedAt = new Date();
          const nextBillingAt = addDays(subscribedAt, selectedBillingCycle === 'annual' ? 365 : 30);
          payloadExtra = {
            billingCycle: selectedBillingCycle,
            subscribedAt,
            nextBillingAt,
            trialStartedAt: company.generalSettings?.trialStartedAt || null,
            trialEndsAt: company.generalSettings?.trialEndsAt || null,
            trialStatus:
              company.generalSettings?.trialStartedAt || company.generalSettings?.trialEndsAt
                ? 'converted'
                : company.generalSettings?.trialStatus || null,
          };
        }

        const planPayload = buildPlanSettingsPayload(preset, payloadExtra);

        await Promise.all([
          setDoc(
            doc(db, resolveCompanyModulesSettingsPath(company.id)),
            buildModulesSettingsPayload(preset.key === 'trial' ? ['logistics', 'systemGuide'] : preset.modules),
            { merge: true }
          ),
          setDoc(doc(db, resolveCompanyGeneralSettingsPath(company.id)), planPayload, { merge: true }),
          setDoc(doc(db, resolveCompanyPlanSettingsPath(company.id)), planPayload, { merge: true }),
          setDoc(doc(db, resolveCompanyDocPath(company.id)), planPayload, { merge: true }),
        ]);

        if (showMessage) {
          showMessage(`Plan ${preset.label} aplicado a ${currentLabel}.`, 'success');
        }
      } catch (planError) {
        console.error('Error applying company plan:', planError);
        if (showMessage) {
          showMessage(`No se pudo aplicar el plan ${preset.label} en ${currentLabel}.`, 'error');
        }
        reconnectRadar();
      } finally {
        setPlanBusyMap((prev) => {
          const next = { ...prev };
          delete next[busyKey];
          return next;
        });
      }
    },
    [reconnectRadar, showMessage]
  );

  const handleRequestDeleteCompany = useCallback((company) => {
    if (!company || company.id === 'default') return;
    setDeleteTarget(company);
    setDeleteConfirmValue('');
  }, []);

  const handleEnsureLaboratoryCompany = useCallback(async () => {
    const laboratoryExists = companies.some((company) => normalizeComparableText(company.id) === 'laboratorio');
    if (laboratoryExists) {
      showMessage?.('Laboratorio ya existe dentro del radar.', 'success');
      return;
    }

    setCreatingLaboratory(true);
    try {
      await ensureCompanyBootstrap({
        companyId: 'laboratorio',
        companyName: 'Laboratorio',
        planId: 'avanzado',
        planName: 'Plan Avanzado',
        status: 'active',
        isSandbox: true,
        main: {
          environmentTag: 'laboratory',
          internalProfile: 'laboratory',
        },
      });

      await Promise.all([
        setDoc(
          doc(db, resolveCompanyGeneralSettingsPath('laboratorio')),
          {
            companyName: 'Laboratorio',
            plan: 'Plan Avanzado',
            planId: 'avanzado',
            planName: 'Plan Avanzado',
            status: 'active',
            internalProfile: 'laboratory',
            environmentTag: 'laboratory',
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        ),
        setDoc(
          doc(db, resolveCompanyDocPath('laboratorio')),
          {
            companyId: 'laboratorio',
            companyName: 'Laboratorio',
            plan: 'Plan Avanzado',
            planId: 'avanzado',
            planName: 'Plan Avanzado',
            status: 'active',
            internalProfile: 'laboratory',
            environmentTag: 'laboratory',
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        ),
      ]);

      reconnectRadar();
      showMessage?.('Laboratorio quedó creado como entorno de pruebas.', 'success');
    } catch (error) {
      console.error('Error creating laboratory company:', error);
      showMessage?.('No se pudo crear Laboratorio en este momento.', 'error');
    } finally {
      setCreatingLaboratory(false);
    }
  }, [companies, reconnectRadar, showMessage]);

  const handleCloseDeleteCompany = useCallback(() => {
    setDeleteTarget(null);
    setDeleteConfirmValue('');
  }, []);

const handleDeleteCompany = useCallback(async () => {
  if (!deleteTarget?.id) return;

  const company = deleteTarget;
  if (company.isProtectedInternalCompany) {
    if (showMessage) {
      showMessage('Esta empresa interna está protegida y no se puede eliminar desde Arquitectura.', 'error');
    }
    return;
  }
  const busyKey = company.id;
  const normalizedConfirmValue = normalizeComparableText(deleteConfirmValue);
  const validConfirmation = [company.name, company.id]
    .map((value) => normalizeComparableText(value))
    .includes(normalizedConfirmValue);

  if (!validConfirmation) {
    if (showMessage) {
      showMessage('Escribí el nombre o ID exacto de la empresa para confirmar.', 'error');
    }
    return;
  }

  setDeleteBusyMap((prev) => ({
    ...prev,
    [busyKey]: true,
  }));

  try {
    const [routesSnapshot, usersSnapshot] = await Promise.all([
      getDocs(collection(db, resolveRoutesCollectionPath(company.id))),
      getDocs(collection(db, 'users')),
    ]);

    const userCleanupPromises = usersSnapshot.docs.map(async (userDoc) => {
      const userData = userDoc.data() || {};
      const updatePayload = {};

      const cleanedCompanies = Array.isArray(userData.companies)
        ? userData.companies.filter((entry) => {
            if (!entry) return false;
            if (typeof entry === 'string') return entry !== company.id;
            return entry.id !== company.id && entry.companyId !== company.id;
          })
        : userData.companies;

      const cleanedAllowed = Array.isArray(userData.allowedCompanies)
        ? userData.allowedCompanies.filter((entry) => {
            if (!entry) return false;
            if (typeof entry === 'string') return entry !== company.id;
            return entry.id !== company.id && entry.companyId !== company.id;
          })
        : userData.allowedCompanies;

      const cleanedAvailable = Array.isArray(userData.availableCompanies)
        ? userData.availableCompanies.filter((entry) => {
            if (!entry) return false;
            if (typeof entry === 'string') return entry !== company.id;
            return entry.id !== company.id && entry.companyId !== company.id;
          })
        : userData.availableCompanies;

      if (Array.isArray(userData.companies) && cleanedCompanies.length !== userData.companies.length) {
        updatePayload.companies = cleanedCompanies;
      }

      if (Array.isArray(userData.allowedCompanies) && cleanedAllowed.length !== userData.allowedCompanies.length) {
        updatePayload.allowedCompanies = cleanedAllowed;
      }

      if (Array.isArray(userData.availableCompanies) && cleanedAvailable.length !== userData.availableCompanies.length) {
        updatePayload.availableCompanies = cleanedAvailable;
      }

      if (userData.companyId === company.id) {
        updatePayload.companyId = DEFAULT_COMPANY_ID;
        updatePayload.companyName = DEFAULT_COMPANY_NAME;
        updatePayload.accessSlug = null;
        updatePayload.companySlug = null;
      }

      if (!Object.keys(updatePayload).length) return null;

      updatePayload.updatedAt = serverTimestamp();
      return setDoc(doc(db, 'users', userDoc.id), updatePayload, { merge: true });
    });

    const routeDeletePromises = routesSnapshot.docs.map((routeDoc) => deleteDoc(routeDoc.ref));

    await Promise.all([
      ...routeDeletePromises,
      ...userCleanupPromises.filter(Boolean),
      deleteDoc(doc(db, resolveCompanyGeneralSettingsPath(company.id))),
      deleteDoc(doc(db, resolveCompanyModulesSettingsPath(company.id))),
      deleteDoc(doc(db, resolveCompanyPlanSettingsPath(company.id))),
      deleteDoc(doc(db, resolveCompanyDocPath(company.id))),
      deleteDoc(doc(db, resolveCompaniesCollectionPath(), company.id)),
    ]);

    setCompanies((prev) => prev.filter((entry) => entry.id !== company.id));
    setBillingDraftMap((prev) => {
      const next = { ...prev };
      delete next[company.id];
      return next;
    });

    handleCloseDeleteCompany();

    if (typeof onCompanyDeleted === 'function') {
      onCompanyDeleted(company.id);
    }

    if (showMessage) {
      showMessage(`Empresa eliminada: ${company.name || company.id}.`, 'success');
    }

    reconnectRadar();
  } catch (deleteError) {
    console.error('Error eliminando empresa:', deleteError);
    if (showMessage) {
      showMessage(`No se pudo eliminar ${company.name || company.id}.`, 'error');
    }
    reconnectRadar();
  } finally {
    setDeleteBusyMap((prev) => {
      const next = { ...prev };
      delete next[busyKey];
      return next;
    });
  }
}, [deleteConfirmValue, deleteTarget, handleCloseDeleteCompany, onCompanyDeleted, reconnectRadar, showMessage]);


  const stats = useMemo(() => {
    return {
      totalCompanies: companies.length,
      activityToday: companies.filter((company) => company.hasActivityToday).length,
      activeRoutes: companies.filter((company) => company.activeRoutesCount > 0).length,
      alerts: companies.filter((company) => {
        const status = getCompanyStatus(company);
        return company.hasError || status.tone === 'danger' || status.tone === 'muted' || company.trialState?.isExpired;
      }).length,
    };
  }, [companies]);

  const liveTrackedCompanies = useMemo(() => {
    return companies.filter((company) => company.activeRoutesCount > 0 || company.hasActivityToday).length;
  }, [companies]);

  const commercialBreakdown = useMemo(() => {
    const planOrder = ['trial', 'base', 'intermedio', 'avanzado', 'personalizado'];
    const labels = {
      trial: 'Trial',
      base: 'Base',
      intermedio: 'Intermedio',
      avanzado: 'Avanzado',
      personalizado: 'Personalizado',
    };

    const counts = companies.reduce((acc, company) => {
      const key = company.planKey || 'sin-plan';
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});

    return planOrder
      .filter((key) => counts[key])
      .map((key) => ({ key, label: labels[key] || key, count: counts[key] }));
  }, [companies]);

  const filterOptionsWithCounts = useMemo(() => {
    return FILTER_OPTIONS.map((option) => ({
      ...option,
      count: companies.filter((company) => matchesCompanyFilter(company, option.key)).length,
    }));
  }, [companies]);

  const latestPendingBillingRequestsByCompany = useMemo(() => {
    const nextMap = new Map();

    billingRequests.forEach((request) => {
      const status = normalizeComparableText(request.status || 'pending');
      if (status && status !== 'pending') return;

      const requestKeys = [request.companyId, request.companyName]
        .map(normalizeBillingRequestCompanyKey)
        .filter(Boolean);

      if (!requestKeys.length) return;

      const requestTime = getDateFromUnknown(request.updatedAt || request.createdAt)?.getTime?.() || 0;

      requestKeys.forEach((key) => {
        const current = nextMap.get(key);
        const currentTime = getDateFromUnknown(current?.updatedAt || current?.createdAt)?.getTime?.() || 0;
        if (!current || requestTime >= currentTime) {
          nextMap.set(key, request);
        }
      });
    });

    return nextMap;
  }, [billingRequests]);

  const commercialAgentQueues = useMemo(() => {
    return {
      radar: radarItems.filter((item) => !isRadarItemDerivedToProspects(item, prospects) && getRadarStatusMeta(item.status).key === 'detected').length,
      validation: radarItems.filter((item) => !isRadarItemDerivedToProspects(item, prospects) && getRadarStatusMeta(item.status).key === 'detected').length,
      loading: radarItems.filter((item) => !isRadarItemDerivedToProspects(item, prospects) && getRadarStatusMeta(item.status).key === 'validated').length,
      contact: prospects.filter((item) => getProspectStatusMeta(item.status).key === 'queued_to_send').length,
      followup: prospects.filter((item) => {
        const statusKey = getProspectStatusMeta(item.status).key;
        return ['sent', 'responded', 'demo', 'trial'].includes(statusKey);
      }).length,
    };
  }, [prospects, radarItems]);

  const filteredSortedCompanies = useMemo(() => {
    const filtered = companies.filter((company) => matchesCompanyFilter(company, companyFilter));
    return sortCompaniesForRadar(filtered, companySort, activeCompanyId);
  }, [activeCompanyId, companies, companyFilter, companySort]);

  const architectSectionItems = useMemo(() => {
    const expiringTrials = companies.filter((company) => company.trialState?.isTrial && company.trialState?.expiresSoon).length;

    return ARCHITECT_SECTIONS.map((section) => {
      if (section.key === 'companies') {
        return {
          ...section,
          meta: `${companies.length} empresas`,
          helper: expiringTrials ? `${expiringTrials} trial por vencer` : 'Radar operativo y comercial',
        };
      }

      if (section.key === 'catalog') {
        return {
          ...section,
          meta: `${commercialCatalog.plans.length} planes`,
          helper: 'Planes, precios y propuesta comercial',
        };
      }

      if (section.key === 'requests') {
        const pendingRequests = commercialLeads.filter((lead) => !['contacted', 'archived'].includes(String(lead.status || 'new').toLowerCase())).length;
        return {
          ...section,
          meta: `${commercialLeads.length} solicitudes`,
          helper: pendingRequests ? `${pendingRequests} pendientes de gestión` : 'Formularios públicos al día',
        };
      }

      if (section.key === 'traffic') {
        const visits7d = sumTrafficMetric(publicTrafficDaily.slice(0, 7), 'totalViews');
        const trial7d =
          sumTrafficNestedMetric(publicTrafficDaily.slice(0, 7), 'interest', 'trial_signup') +
          sumTrafficNestedMetric(publicTrafficDaily.slice(0, 7), 'interest', 'trial');

        return {
          ...section,
          meta: `${formatTrafficNumber(visits7d)} visitas`,
          helper: trial7d ? `${formatTrafficNumber(trial7d)} entradas recientes a prueba gratis` : 'Landing, planes y prueba gratis',
        };
      }

      if (section.key === 'radar') {
        const activeRadarItems = radarItems.filter((item) => !isRadarItemDerivedToProspects(item, prospects));
        const validatedRadar = activeRadarItems.filter((item) => getRadarStatusMeta(item.status).key === 'validated').length;
        return {
          ...section,
          meta: `${activeRadarItems.length} hallazgos`,
          helper: validatedRadar ? `${validatedRadar} validados para carga automática` : 'Detección automática y revisión humana',
        };
      }

      const reviewQueue = prospects.filter((item) => getProspectStatusMeta(item.status).key === 'pending_review').length;
      return {
        ...section,
        meta: `${prospects.length} prospectos`,
        helper: reviewQueue ? `${reviewQueue} con revisión manual pendiente` : 'Salida, seguimiento y conversión comercial',
      };
    });
  }, [commercialCatalog.plans.length, commercialLeads, companies, prospects, publicTrafficDaily, radarItems]);

  return (
    <section className="min-h-screen bg-slate-100 p-4 text-slate-900 md:p-6 xl:p-8">
      <div className="mx-auto flex max-w-7xl flex-col gap-6">
        <header className="rounded-[2rem] border border-slate-200 bg-gradient-to-br from-white via-slate-50 to-cyan-50 p-6 shadow-sm">
          <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
            <div className="space-y-3">
              <div className="inline-flex items-center gap-2 rounded-full border border-cyan-200 bg-cyan-50 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.25em] text-cyan-700">
                Arquitectura G-CENTRA
              </div>

              <div className="space-y-2">
                <h1 className="text-3xl font-black tracking-tight text-slate-900 md:text-4xl">Radar multiempresa en tiempo real</h1>
                <p className="max-w-3xl text-sm text-slate-600 md:text-base">
                  Supervisá planes, actividad y módulos de cada empresa desde una sola vista ejecutiva.
                </p>
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
                <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-slate-500">Perfil activo</p>
                <p className="mt-2 text-sm font-semibold text-slate-900">
                  {currentUserData?.companyName || currentUserData?.name || currentUserData?.email || 'Arquitectura'}
                </p>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
                <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-slate-500">Sincronización</p>
                <p className="mt-2 text-sm font-semibold text-slate-900">
                  {lastLiveUpdateAt ? formatLastActivity(lastLiveUpdateAt) : 'Esperando datos'}
                </p>
                <p className="mt-1 text-[11px] text-cyan-700">
                  {liveTrackedCompanies} empresa{liveTrackedCompanies === 1 ? '' : 's'} con señales operativas
                </p>
              </div>
            </div>
          </div>
        </header>

        <ArchitectStats stats={stats} />

        <CommercialAgentsPanel
          agentControl={commercialAgentControl}
          queues={commercialAgentQueues}
          onToggleAgent={handleToggleCommercialAgent}
          onApplyTargetPreset={handleApplyCommercialTargetPreset}
          onSaveResponseNotify={handleSaveCommercialResponseNotify}
          onRunDiscoveryPass={handleRunDiscoveryCommercialPass}
          onRunLivePass={handleRunLiveCommercialPass}
          onRunContactPass={handleRunContactCommercialPass}
          onRunFollowupPass={handleRunFollowupCommercialPass}
          onRunPilotPass={handleRunPilotCommercialPass}
          discoveryRunBusy={discoveryCommercialRunBusy}
          liveRunBusy={liveCommercialRunBusy}
          contactRunBusy={contactCommercialRunBusy}
          followupRunBusy={followupCommercialRunBusy}
          pilotRunBusy={pilotAgentRunBusy}
          busyMap={commercialAgentBusyMap}
        />

        <section className="rounded-[2rem] border border-slate-200 bg-white p-4 shadow-sm md:p-5">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 rounded-full border border-cyan-200 bg-cyan-50 px-3 py-1 text-[11px] font-black uppercase tracking-[0.22em] text-cyan-700">
                Navegación ejecutiva
              </div>
              <h2 className="mt-4 text-2xl font-black tracking-tight text-slate-950 md:text-3xl">Centro de control comercial</h2>
              <p className="mt-2 text-sm leading-6 text-slate-600">
                Entrá a cada vista por separado: catálogo, tráfico, solicitudes, prospectos y radar. Menos ruido en pantalla, más lectura operativa.
              </p>
            </div>

            <div className="rounded-3xl border border-slate-200 bg-slate-50 px-4 py-3 shadow-inner">
              <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500">Vista actual</p>
              <p className="mt-2 text-sm font-black text-slate-900">
                {architectSectionItems.find((item) => item.key === architectSection)?.label || 'Panel'}
              </p>
              <p className="mt-1 text-xs text-cyan-700">
                {architectSectionItems.find((item) => item.key === architectSection)?.meta || 'Seguimiento ejecutivo'}
              </p>
            </div>
          </div>

          <div className="mt-5 grid gap-3 lg:grid-cols-2 xl:grid-cols-4">
            {architectSectionItems.map((section, index) => {
              const active = architectSection === section.key;
              return (
                <button
                  key={section.key}
                  type="button"
                  onClick={() => setArchitectSection(section.key)}
                  className={`group relative overflow-hidden rounded-3xl border p-4 text-left transition ${
                    active
                      ? 'border-cyan-300 bg-gradient-to-br from-cyan-50 via-white to-white shadow-[0_16px_36px_rgba(8,145,178,0.14)]'
                      : 'border-slate-200 bg-white hover:-translate-y-0.5 hover:border-cyan-200 hover:shadow-md'
                  }`}
                >
                  <span className={`absolute inset-x-0 top-0 h-1 ${active ? 'bg-cyan-500' : 'bg-slate-100 group-hover:bg-cyan-200'}`} />
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className={`inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[11px] font-black ${
                          active ? 'bg-cyan-600 text-white' : 'bg-slate-100 text-slate-500 group-hover:bg-cyan-50 group-hover:text-cyan-700'
                        }`}>
                          {index + 1}
                        </span>
                        <p className={`truncate text-[11px] font-black uppercase tracking-[0.18em] ${active ? 'text-cyan-700' : 'text-slate-500'}`}>
                          {section.label}
                        </p>
                      </div>
                      <p className="mt-3 text-lg font-black leading-tight text-slate-950">{section.meta}</p>
                    </div>
                    {active ? (
                      <span className="shrink-0 rounded-full border border-cyan-200 bg-white px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.14em] text-cyan-700">
                        Activa
                      </span>
                    ) : null}
                  </div>
                  <p className={`mt-3 text-sm leading-6 ${active ? 'text-cyan-800' : 'text-slate-600'}`}>{section.helper}</p>
                </button>
              );
            })}
          </div>
        </section>

        {architectSection === 'catalog' ? (
          <CommercialCatalogPanel
            draftCatalog={commercialCatalogDraft}
            catalogSaving={catalogSaving}
            onChange={handleCommercialFieldChange}
            onSave={handleSaveCommercialCatalog}
          />
        ) : null}

        {architectSection === 'traffic' ? (
          <PublicTrafficPanel summary={publicTrafficSummary} daily={publicTrafficDaily} loading={publicTrafficLoading} />
        ) : null}

        {architectSection === 'requests' ? (
          <CommercialRequestsPanel
            requests={commercialLeads}
            onUpdateStatus={handleUpdateCommercialLeadStatus}
            busyMap={commercialLeadBusyMap}
          />
        ) : null}

        {architectSection === 'prospects' ? (
          <CommercialProspectsPanel
            prospects={prospects}
            onAdvanceProspect={handleAdvanceProspect}
            onCopyTemplate={handleCopyCommercialTemplate}
            onCopyManualMessage={handleCopyManualProspectMessage}
            onMarkResponseSeen={handleMarkProspectResponseSeen}
            onSendProspect={handleSendProspect}
            onDeleteProspect={handleDeleteProspect}
            busyMap={prospectBusyMap}
          />
        ) : null}

        {architectSection === 'radar' ? (
          <CommercialRadarPanel
            radarItems={radarItems}
            prospects={prospects}
            onUpdateRadarStatus={handleUpdateRadarStatus}
            onPromoteRadarItem={handlePromoteRadarItem}
            onDeleteRadarItem={handleDeleteRadarItem}
            busyMap={radarBusyMap}
          />
        ) : null}

        {architectSection === 'companies' ? <TrialOverviewPanel companies={companies} /> : null}

        <CompanyDeleteModal
          company={deleteTarget}
          confirmValue={deleteConfirmValue}
          onChangeConfirmValue={setDeleteConfirmValue}
          onClose={handleCloseDeleteCompany}
          onConfirm={handleDeleteCompany}
          busy={Boolean(deleteTarget && deleteBusyMap?.[deleteTarget.id])}
        />

        {error ? (
          <div className="rounded-3xl border border-rose-500/40 bg-rose-500/10 p-4 text-sm text-rose-100 shadow-lg">
            {error}
          </div>
        ) : null}

        {architectSection === 'companies' ? (
          <div className="rounded-[2rem] border border-slate-800 bg-white/5 p-4 shadow-2xl backdrop-blur md:p-6">
            <div className="mb-5 flex flex-col gap-4">
              <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                <div>
                  <h2 className="text-xl font-black text-white md:text-2xl">Empresas cargadas</h2>
                  <p className="mt-1 text-sm text-slate-300">Control comercial y operativo por empresa con lectura viva del sistema.</p>
                  {commercialBreakdown.length ? (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {commercialBreakdown.map((item) => (
                        <button
                          key={`breakdown-${item.key}`}
                          type="button"
                          onClick={() => setCompanyFilter(item.key)}
                          className={`rounded-full border px-3 py-1 text-[11px] font-bold uppercase tracking-[0.14em] transition ${companyFilter === item.key ? 'border-cyan-400 bg-cyan-500/20 text-cyan-100' : 'border-slate-700 bg-slate-900 text-slate-200 hover:border-slate-500'}`}
                        >
                          {item.label} · {item.count}
                        </button>
                      ))}
                    </div>
                  ) : null}
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={handleEnsureLaboratoryCompany}
                    disabled={creatingLaboratory}
                    className={`rounded-full border px-3 py-1 text-xs font-semibold transition ${
                      creatingLaboratory
                        ? 'cursor-wait border-violet-300/30 bg-violet-500/10 text-violet-200'
                        : 'border-violet-300/30 bg-violet-500/10 text-violet-100 hover:bg-violet-500/20'
                    }`}
                  >
                    {creatingLaboratory ? 'Creando Laboratorio...' : 'Crear Laboratorio'}
                  </button>

                  <div className="inline-flex items-center gap-2 rounded-full border border-emerald-400/30 bg-emerald-500/10 px-3 py-1 text-xs font-semibold text-emerald-200">
                    <span className="h-2 w-2 animate-pulse rounded-full bg-emerald-300" />
                    Radar activo
                  </div>
                </div>
              </div>

              <div className="rounded-3xl border border-slate-800 bg-slate-950/70 p-4 shadow-inner">
                <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
                  <div>
                    <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-400">Vista ejecutiva</p>
                    <p className="mt-2 text-sm text-slate-300">Filtrá y ordená el radar para detectar rápido trials, renovaciones y actividad.</p>
                  </div>

                  <div className="flex flex-col gap-3 xl:items-end">
                    <div className="flex flex-wrap gap-2">
                      {filterOptionsWithCounts.map((option) => (
                        <button
                          key={`filter-${option.key}`}
                          type="button"
                          onClick={() => setCompanyFilter(option.key)}
                          className={`rounded-full border px-3 py-1 text-[11px] font-bold uppercase tracking-[0.14em] transition ${companyFilter === option.key ? 'border-cyan-400 bg-cyan-500/20 text-cyan-100' : 'border-slate-700 bg-slate-900 text-slate-200 hover:border-slate-500'}`}
                        >
                          {option.label} · {option.count}
                        </button>
                      ))}
                    </div>

                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                      <div className="rounded-2xl border border-slate-700 bg-slate-900 px-3 py-2 text-xs font-semibold text-slate-200">
                        Mostrando {filteredSortedCompanies.length} de {companies.length}
                      </div>
                      <label className="flex items-center gap-2 rounded-2xl border border-slate-700 bg-slate-900 px-3 py-2">
                        <span className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-400">Orden</span>
                        <select
                          value={companySort}
                          onChange={(event) => setCompanySort(event.target.value)}
                          className="bg-transparent text-sm font-semibold text-white outline-none"
                        >
                          {SORT_OPTIONS.map((option) => (
                            <option key={option.key} value={option.key} className="text-slate-900">
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {loading ? (
              <div className="grid gap-4 xl:grid-cols-2">
                {[0, 1].map((item) => (
                  <div key={item} className="h-[320px] animate-pulse rounded-3xl border border-slate-700 bg-slate-900/60" />
                ))}
              </div>
            ) : companies.length === 0 ? (
              <div className="rounded-3xl border border-dashed border-slate-700 bg-slate-900/50 px-6 py-10 text-center shadow-inner">
                <p className="text-lg font-bold text-white">Todavía no hay empresas listas para mostrar.</p>
                <p className="mt-2 text-sm text-slate-400">Cuando existan compañías registradas, el radar las va a resumir acá automáticamente.</p>
              </div>
            ) : filteredSortedCompanies.length === 0 ? (
              <div className="rounded-3xl border border-dashed border-slate-700 bg-slate-900/50 px-6 py-10 text-center shadow-inner">
                <p className="text-lg font-bold text-white">No hay empresas para ese filtro.</p>
                <p className="mt-2 text-sm text-slate-400">Probá otra vista del radar para seguir leyendo la cartera.</p>
              </div>
            ) : (
              <div className="flex flex-col gap-4">
                {filteredSortedCompanies.map((company) => (
                  <CompanyCard
                    key={company.id}
                    company={company}
                    commercialCatalog={commercialCatalog}
                    billingDraftMap={billingDraftMap}
                    pendingBillingRequest={
                      latestPendingBillingRequestsByCompany.get(normalizeBillingRequestCompanyKey(company.id)) ||
                      latestPendingBillingRequestsByCompany.get(normalizeBillingRequestCompanyKey(company.name)) ||
                      null
                    }
                    onChangeBillingCycle={handleChangeBillingCycle}
                    isActiveContext={company.id === activeCompanyId}
                    onToggleModule={handleToggleModule}
                    onApplyPlan={handleApplyPlan}
                    onRequestDeleteCompany={handleRequestDeleteCompany}
                    toggleBusyMap={toggleBusyMap}
                    planBusyMap={planBusyMap}
                    deleteBusyMap={deleteBusyMap}
                  />
                ))}
              </div>
            )}
          </div>
        ) : null}
      </div>
    </section>
  );
}
